// src/components/Designer/Designer.tsx
import {
  useCallback as useCallback11,
  useEffect as useEffect16,
  useMemo as useMemo8,
  useRef as useRef15,
  useState as useState15
} from "react";

// src/host/types.ts
var DESIGNER_HOST_COMMAND_IDS = [
  "new",
  "open",
  "save",
  "saveAs",
  "templateBrowser",
  "versionHistory",
  "restoreVersion",
  "importTemplate",
  "exportTemplate",
  "preview",
  "print",
  "exportDocument",
  "keyboardShortcuts",
  "documentation",
  "about"
];
function resolveDesignerHostCommand(host, command, locallyPending = false) {
  const declared = Boolean(
    host?.commands && Object.prototype.hasOwnProperty.call(host.commands, command)
  );
  const state = host?.commands?.[command];
  const pending = locallyPending || Boolean(state?.pending);
  if (!declared) return { enabled: false, pending, reason: "\u529F\u80FD\u5F85\u63A5\u5165" };
  if (!host?.onCommand) return { enabled: false, pending, reason: "\u5BBF\u4E3B\u672A\u63D0\u4F9B\u547D\u4EE4\u5904\u7406\u5668" };
  if (state?.enabled === false) {
    return { enabled: false, pending, reason: state.reason ?? "\u5F53\u524D\u4E0D\u53EF\u7528" };
  }
  if (pending) return { enabled: true, pending: true, reason: state?.reason ?? "\u6B63\u5728\u6267\u884C" };
  return { enabled: true, pending: false, reason: state?.reason };
}

// src/host/useDesignerHostCommands.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
function useDesignerHostCommands(host, getTemplate) {
  const pendingRef = useRef(/* @__PURE__ */ new Set());
  const mountedRef = useRef(true);
  const [pendingCommands, setPendingCommands] = useState([]);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );
  const publishPending = useCallback(() => {
    if (mountedRef.current) setPendingCommands([...pendingRef.current]);
  }, []);
  const getState = useCallback(
    (command) => resolveDesignerHostCommand(host, command, pendingCommands.includes(command)),
    [host, pendingCommands]
  );
  const execute = useCallback(
    async (command) => {
      const state = resolveDesignerHostCommand(host, command, pendingRef.current.has(command));
      if (!state.enabled || state.pending || !host?.onCommand) return false;
      pendingRef.current.add(command);
      publishPending();
      try {
        await host.onCommand(command, {
          template: getTemplate(),
          document: host.document
        });
      } catch (error) {
        host.onCommandError?.(command, error);
      } finally {
        pendingRef.current.delete(command);
        publishPending();
      }
      return true;
    },
    [getTemplate, host, publishPending]
  );
  return useMemo(
    () => ({ configured: Boolean(host), document: host?.document, getState, execute }),
    [execute, getState, host]
  );
}

// src/state/editor.ts
import { computed, signal } from "@preact/signals-react";
import {
  getPageDimensions,
  getComponentBindingTargets,
  getTableCellAt,
  getTableCellBounds,
  normalizeTemplateData,
  normalizeSimpleTableProps,
  pageConfigError,
  updateTableCellText,
  validateRuntimeRecords
} from "@ptd/core";

// src/utils/index.ts
function generateId(length = 8) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (const byte of array) {
    result += chars[byte % chars.length];
  }
  return result;
}
function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}
function angleToRadian(angle) {
  return angle * Math.PI / 180;
}
function calculateRotatedPointCoordinate(point, center, rotate) {
  const rad = angleToRadian(rotate);
  return {
    x: (point.x - center.x) * Math.cos(rad) - (point.y - center.y) * Math.sin(rad) + center.x,
    y: (point.x - center.x) * Math.sin(rad) + (point.y - center.y) * Math.cos(rad) + center.y
  };
}
function getCenterPoint(p1, p2) {
  return { x: p1.x + (p2.x - p1.x) / 2, y: p1.y + (p2.y - p1.y) / 2 };
}
function mod360(deg) {
  return (deg % 360 + 360) % 360;
}
function getComponentRotatedStyle(style) {
  const { width, height, rotate = 0 } = style;
  const left = style.left ?? 0;
  const top = style.top ?? 0;
  if (rotate !== 0) {
    const center = { x: left + width / 2, y: top + height / 2 };
    const corners = [
      { x: left, y: top },
      { x: left + width, y: top },
      { x: left + width, y: top + height },
      { x: left, y: top + height }
    ];
    const rotated = corners.map((p) => calculateRotatedPointCoordinate(p, center, rotate));
    const xs = rotated.map((p) => p.x);
    const ys = rotated.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    return {
      left: minX,
      top: minY,
      right: maxX,
      bottom: maxY,
      width: maxX - minX,
      height: maxY - minY
    };
  }
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height
  };
}

// src/utils/groupGeometry.ts
var BASE_WIDTH = "baseWidth";
var BASE_HEIGHT = "baseHeight";
function finite(value, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function groupChildren(group) {
  return group.component === "RoyGroup" && Array.isArray(group.propValue) ? group.propValue : [];
}
function createGroupMetrics(width, height) {
  return { [BASE_WIDTH]: width, [BASE_HEIGHT]: height };
}
function getScaledGroupChildren(group) {
  const width = finite(group.style.width);
  const height = finite(group.style.height);
  const baseWidth = finite(group.groupStyle[BASE_WIDTH], width) || width || 1;
  const baseHeight = finite(group.groupStyle[BASE_HEIGHT], height) || height || 1;
  const scaleX = width / baseWidth;
  const scaleY = height / baseHeight;
  return groupChildren(group).map((child) => ({
    ...child,
    style: {
      ...child.style,
      left: finite(child.style.left) * scaleX,
      top: finite(child.style.top) * scaleY,
      width: finite(child.style.width) * scaleX,
      height: finite(child.style.height) * scaleY
    }
  }));
}
function getAbsoluteGroupChildren(group) {
  const groupLeft = finite(group.style.left);
  const groupTop = finite(group.style.top);
  const groupWidth = finite(group.style.width);
  const groupHeight = finite(group.style.height);
  const groupRotate = finite(group.style.rotate);
  const groupCenter = { x: groupWidth / 2, y: groupHeight / 2 };
  return getScaledGroupChildren(group).map((child) => {
    const childWidth = finite(child.style.width);
    const childHeight = finite(child.style.height);
    const childCenter = {
      x: finite(child.style.left) + childWidth / 2,
      y: finite(child.style.top) + childHeight / 2
    };
    const rotatedCenter = calculateRotatedPointCoordinate(childCenter, groupCenter, groupRotate);
    return {
      ...child,
      style: {
        ...child.style,
        left: groupLeft + rotatedCenter.x - childWidth / 2,
        top: groupTop + rotatedCenter.y - childHeight / 2,
        rotate: finite(child.style.rotate) + groupRotate
      }
    };
  });
}

// src/catalog/componentCatalog.ts
import { defaultRegistry } from "@ptd/core";
import {
  RiBarcodeLine,
  RiBracesLine,
  RiCalendar2Line,
  RiCheckboxBlankCircleLine,
  RiImage2Line,
  RiLayoutGridLine,
  RiListCheck3,
  RiPagesLine,
  RiQrCodeLine,
  RiRectangleLine,
  RiShapesLine,
  RiStarLine,
  RiSubtractLine,
  RiTBoxLine,
  RiTable2,
  RiTableLine,
  RiTableView,
  RiText
} from "@remixicon/react";
var catalogGroups = [
  { id: "text", name: "\u6587\u672C", introduction: "\u6807\u9898\u3001\u6807\u7B7E\u4E0E\u591A\u6BB5\u843D\u6392\u7248\u5185\u5BB9" },
  { id: "table", name: "\u8868\u683C", introduction: "\u56FA\u5B9A\u7F51\u683C\u3001\u5206\u533A\u7ED3\u6784\u4E0E\u91CD\u590D\u6570\u636E" },
  { id: "image", name: "\u56FE\u50CF", introduction: "Logo\u3001\u5370\u7AE0\u3001\u7167\u7247\u4E0E\u77E2\u91CF\u7D20\u6750" },
  { id: "code", name: "\u7F16\u7801", introduction: "\u5C06\u7F51\u5740\u3001\u5355\u53F7\u4E0E\u4E1A\u52A1\u6807\u8BC6\u7F16\u7801\u5230\u7EB8\u9762" },
  { id: "shape", name: "\u56FE\u5F62", introduction: "\u5206\u9694\u3001\u8FB9\u6846\u3001\u5E95\u5F62\u4E0E\u89C6\u89C9\u6807\u8BB0" }
];
var ICONS = {
  RoySimpleText: RiText,
  RoyText: RiTBoxLine,
  RoySimpleTable: RiTable2,
  RoyComplexTable: RiTableLine,
  RoyImage: RiImage2Line,
  RoyQRCode: RiQrCodeLine,
  RoyBarCode: RiBarcodeLine,
  RoyLine: RiSubtractLine,
  RoyRect: RiRectangleLine,
  RoyCircle: RiCheckboxBlankCircleLine,
  RoyStar: RiStarLine
};
var plannedCatalogItems = [
  {
    kind: "planned",
    id: "data-expression-text",
    name: "\u6570\u636E\u5B57\u6BB5 / \u8868\u8FBE\u5F0F\u6587\u672C",
    group: "text",
    icon: RiBracesLine,
    description: "\u63D2\u5165\u4E1A\u52A1\u5B57\u6BB5\uFF0C\u5E76\u901A\u8FC7\u8868\u8FBE\u5F0F\u7EC4\u5408\u52A8\u6001\u6587\u672C",
    keywords: ["\u6570\u636E\u5B57\u6BB5", "\u8868\u8FBE\u5F0F", "\u53D8\u91CF", "\u7ED1\u5B9A", "\u52A8\u6001\u6587\u672C"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "page-number",
    name: "\u9875\u7801 / \u603B\u9875\u6570",
    group: "text",
    icon: RiPagesLine,
    description: "\u5728\u6253\u5370\u65F6\u663E\u793A\u5F53\u524D\u9875\u7801\u4E0E\u6587\u6863\u603B\u9875\u6570",
    keywords: ["\u9875\u7801", "\u603B\u9875\u6570", "\u5206\u9875", "\u6253\u5370\u4E0A\u4E0B\u6587"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "date-time",
    name: "\u65E5\u671F\u65F6\u95F4",
    group: "text",
    icon: RiCalendar2Line,
    description: "\u6309\u6307\u5B9A\u683C\u5F0F\u8F93\u51FA\u6253\u5370\u65E5\u671F\u4E0E\u65F6\u95F4",
    keywords: ["\u65E5\u671F", "\u65F6\u95F4", "\u683C\u5F0F\u5316", "\u6253\u5370\u65F6\u95F4"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "structured-table",
    name: "\u7ED3\u6784\u8868\u683C",
    group: "table",
    icon: RiTableLine,
    description: "\u6309\u8868\u5934\u3001\u8868\u4F53\u4E0E\u8868\u5C3E\u7EC4\u7EC7\u6570\u636E\uFF0C\u5E76\u652F\u6301\u62A5\u8868\u5206\u9875\u5408\u540C",
    keywords: ["\u8868\u5934", "\u8868\u4F53", "\u8868\u5C3E", "\u5206\u533A", "\u62A5\u8868", "\u53EA\u8BFB\u517C\u5BB9"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "repeating-list",
    name: "\u91CD\u590D\u660E\u7EC6 / \u5217\u8868",
    group: "table",
    icon: RiListCheck3,
    description: "\u4F7F\u7528\u884C\u6A21\u677F\u91CD\u590D\u5448\u73B0\u96C6\u5408\u4E0E\u4E1A\u52A1\u660E\u7EC6",
    keywords: ["\u91CD\u590D\u660E\u7EC6", "\u5217\u8868", "\u96C6\u5408", "\u884C\u6A21\u677F", "\u5FAA\u73AF"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "flowing-data-table",
    name: "\u6570\u636E\u9A71\u52A8\u8868\u683C / \u81EA\u52A8\u5206\u9875\u8868\u683C",
    group: "table",
    icon: RiTableView,
    description: "\u6839\u636E\u6570\u636E\u751F\u6210\u8868\u683C\uFF0C\u5E76\u5728\u6EA2\u51FA\u65F6\u81EA\u52A8\u5206\u9875",
    keywords: ["\u6570\u636E\u8868\u683C", "\u81EA\u52A8\u5206\u9875", "\u8868\u5934\u91CD\u590D", "\u884C\u62C6\u5206", "\u6EA2\u51FA"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "vector-image",
    name: "SVG / \u56FE\u6807",
    group: "image",
    icon: RiShapesLine,
    description: "\u653E\u7F6E\u53EF\u65E0\u635F\u7F29\u653E\u7684\u77E2\u91CF\u56FE\u5F62\u4E0E\u56FE\u6807",
    keywords: ["SVG", "\u77E2\u91CF", "\u56FE\u6807", "\u65E0\u635F\u7F29\u653E"],
    maturity: "planned"
  },
  {
    kind: "planned",
    id: "frame",
    name: "\u5BB9\u5668 / Frame",
    group: "shape",
    icon: RiLayoutGridLine,
    description: "\u5BB9\u7EB3\u3001\u88C1\u5207\u5E76\u7EC4\u7EC7\u4E00\u7EC4\u7248\u9762\u5185\u5BB9",
    keywords: ["\u5BB9\u5668", "Frame", "\u88C1\u5207", "\u5B50\u5143\u7D20", "\u5E03\u5C40"],
    maturity: "planned"
  }
];
var availableCatalogItems = defaultRegistry.getCatalogDefinitions().map((definition) => ({
  kind: "available",
  id: definition.catalog.id,
  type: definition.type,
  name: definition.name,
  category: definition.category,
  group: definition.catalog.group,
  icon: ICONS[definition.type],
  description: definition.catalog.description,
  keywords: definition.catalog.keywords,
  maturity: definition.catalog.maturity,
  creationMode: definition.catalog.creationMode
}));
var componentCatalog = catalogGroups.flatMap((group) => [
  ...availableCatalogItems.filter((item) => item.group === group.id),
  ...plannedCatalogItems.filter((item) => item.group === group.id)
]);
var frequentComponentTypes = [
  "RoySimpleText",
  "RoyImage",
  "RoySimpleTable",
  "RoyQRCode"
];
var frequentCatalogItems = frequentComponentTypes.map(
  (type) => {
    const item = findAvailableCatalogItem(type);
    if (!item) throw new Error(`Missing frequent catalog item: ${type}`);
    return item;
  }
);
function rememberRecentComponentType(current, type, limit = 4) {
  if (limit <= 0) return [];
  return [type, ...current.filter((currentType) => currentType !== type)].slice(0, limit);
}
function isAvailableCatalogItem(item) {
  return item.kind === "available";
}
function isDrawingComponentType(type) {
  return type === "RoyLine" || type === "RoyRect" || type === "RoyCircle" || type === "RoyStar";
}
function isDrawnComponentType(type) {
  return findAvailableCatalogItem(type)?.creationMode === "draw";
}
function findAvailableCatalogItem(type) {
  return componentCatalog.find(
    (item) => item.kind === "available" && item.type === type
  );
}
function searchComponentCatalog(query) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [...componentCatalog];
  return componentCatalog.filter((item) => {
    const technicalType = item.kind === "available" ? item.type : "";
    const searchable = [item.name, item.description, technicalType, ...item.keywords].join("\n").toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
}
function searchAvailableComponentCatalog(query) {
  return searchComponentCatalog(query).filter(isAvailableCatalogItem);
}
function createComponentSchema(type, center, bounds) {
  const definition = defaultRegistry.get(type);
  if (!definition || definition.internal) throw new Error(`Unknown component type: ${type}`);
  const width = finiteSize(definition.defaultStyle.width, 100);
  const height = finiteSize(definition.defaultStyle.height, 40);
  const left = clamp(Math.round(center.x - width / 2), 0, Math.max(0, bounds.width - width));
  const top = clamp(Math.round(center.y - height / 2), 0, Math.max(0, bounds.height - height));
  return {
    id: generateId(),
    component: definition.type,
    icon: definition.icon,
    code: definition.type,
    name: definition.name,
    group: definition.category,
    propValue: deepCopy(definition.defaultProps),
    style: {
      width,
      height,
      rotate: 0,
      opacity: 1,
      ...deepCopy(definition.defaultStyle),
      left,
      top
    },
    groupStyle: {},
    position: { x: left, y: top }
  };
}
function finiteSize(value, fallback) {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}
function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

// src/catalog/shapeDrawing.ts
var DRAWING_THRESHOLD_CSS_PX = 4;
function isDrawingGestureLongEnough(start, end, threshold = DRAWING_THRESHOLD_CSS_PX) {
  return Math.hypot(end.x - start.x, end.y - start.y) >= threshold;
}
function shapeDrawGeometry(type, start, end, bounds, constrain = false) {
  const from = clampPoint(start, bounds);
  let to = clampPoint(end, bounds);
  if (type !== "RoyLine" && constrain) {
    const size = Math.min(Math.abs(to.x - from.x), Math.abs(to.y - from.y));
    to = {
      x: from.x + signedSize(to.x - from.x, size),
      y: from.y + signedSize(to.y - from.y, size)
    };
  }
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (type === "RoyLine") {
    const length = Math.hypot(dx, dy);
    if (length === 0) return null;
    const height2 = 2;
    const midpoint = { x: from.x + dx / 2, y: from.y + dy / 2 };
    return {
      left: midpoint.x - length / 2,
      top: midpoint.y - height2 / 2,
      width: length,
      height: height2,
      rotate: Math.atan2(dy, dx) * 180 / Math.PI
    };
  }
  const width = Math.abs(dx);
  const height = Math.abs(dy);
  if (width === 0 || height === 0) return null;
  return {
    left: Math.min(from.x, to.x),
    top: Math.min(from.y, to.y),
    width,
    height,
    rotate: 0
  };
}
function textFrameDrawGeometry(start, end, bounds) {
  const from = clampPoint(start, bounds);
  const to = clampPoint(end, bounds);
  const width = Math.abs(to.x - from.x);
  const height = Math.abs(to.y - from.y);
  if (width === 0 || height === 0) return null;
  return {
    left: Math.min(from.x, to.x),
    top: Math.min(from.y, to.y),
    width,
    height,
    rotate: 0
  };
}
function drawnComponentGeometry(type, start, end, bounds, constrain = false) {
  if (type === "RoyQRCode") {
    return shapeDrawGeometry("RoyRect", start, end, bounds, true);
  }
  return isDrawingComponentType(type) ? shapeDrawGeometry(type, start, end, bounds, constrain) : textFrameDrawGeometry(start, end, bounds);
}
function createDrawnComponentSchema(type, start, end, bounds, constrain = false) {
  const geometry = drawnComponentGeometry(type, start, end, bounds, constrain);
  if (!geometry) return null;
  const schema = createComponentSchema(
    type,
    { x: geometry.left + geometry.width / 2, y: geometry.top + geometry.height / 2 },
    bounds
  );
  return {
    ...schema,
    style: { ...schema.style, ...geometry },
    position: { x: geometry.left, y: geometry.top }
  };
}
function clampPoint(point, bounds) {
  return {
    x: clamp2(point.x, 0, Math.max(0, bounds.width)),
    y: clamp2(point.y, 0, Math.max(0, bounds.height))
  };
}
function signedSize(delta, size) {
  return delta < 0 ? -size : size;
}
function clamp2(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

// src/state/editor.ts
var HISTORY_LIMIT = 20;
var PASTE_OFFSET = 12;
var RECENT_COLOR_LIMIT = 8;
var GESTURE_ECHO_LIMIT = 32;
function clone(value) {
  return JSON.parse(JSON.stringify(value));
}
function randomId() {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(36)).join("").slice(0, 10);
}
function number(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
function isShapeDrawingTool(tool) {
  return isDrawingComponentType(tool);
}
function clamp3(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function withPosition(component, left, top) {
  return { ...component, style: { ...component.style, left, top } };
}
function regenerateIds(component, idFactory) {
  const next = { ...clone(component), id: idFactory() };
  if (next.component === "RoyGroup" && Array.isArray(next.propValue)) {
    next.propValue = next.propValue.map(
      (child) => regenerateIds(child, idFactory)
    );
  }
  return next;
}
function duplicatePage(page, idFactory) {
  return {
    ...clone(page),
    id: idFactory(),
    componentData: page.componentData.map((component) => regenerateIds(component, idFactory))
  };
}
var EditorStore = class {
  template = signal(null);
  currentPageIndex = signal(0);
  selectedIds = signal([]);
  scale = signal(1);
  measurementUnit = signal("mm");
  recentColors = signal([]);
  showRuler = signal(true);
  guides = signal([]);
  guidesVisible = signal(true);
  guidesLocked = signal(false);
  activeGuideColor = signal("cobalt");
  selectedGuideId = signal(null);
  areaSelection = signal({
    style: { top: 0, left: 0, width: 0, height: 0 },
    componentIds: []
  });
  isSelectingArea = signal(false);
  clipboard = signal(null);
  componentToReveal = signal(null);
  activeTool = signal("select");
  temporaryHand = signal(false);
  lastDrawingTool = signal("RoyRect");
  history = signal([]);
  historyIndex = signal(0);
  editingComponentId = signal(null);
  tableCellSelection = signal(null);
  editingTableCell = signal(null);
  proofMode = signal(false);
  proofRecordIndex = signal(0);
  proofRecordSelectionTouched = signal(false);
  hostRenderContext = signal(void 0);
  proofNow = signal((/* @__PURE__ */ new Date()).toISOString());
  proofLocale = signal("zh-CN");
  proofTimeZone = signal("Asia/Shanghai");
  /** Advances only when the controlled Host supplies a genuinely external template object. */
  externalTemplateRevision = signal(0);
  currentPage = computed(() => this.template.value.pages[this.currentPageIndex.value]);
  components = computed(() => this.currentPage.value?.componentData ?? []);
  pageConfig = computed(() => this.template.value.pageConfig);
  selectedComponents = computed(() => {
    const ids = new Set(this.selectedIds.value);
    return this.components.value.filter((component) => ids.has(component.id));
  });
  primaryComponent = computed(() => {
    const id = this.selectedIds.value.at(-1);
    return id ? this.components.value.find((component) => component.id === id) ?? null : null;
  });
  outOfBoundsComponents = computed(() => {
    const page = getPageDimensions(this.pageConfig.value);
    return this.components.value.filter((component) => {
      const bounds = getComponentRotatedStyle(component.style);
      return bounds.left < 0 || bounds.top < 0 || bounds.right > page.width || bounds.bottom > page.height;
    });
  });
  effectiveTool = computed(
    () => this.temporaryHand.value ? "hand" : this.activeTool.value
  );
  canUndo = computed(() => this.historyIndex.value > 0);
  canRedo = computed(() => this.historyIndex.value < this.history.value.length - 1);
  normalizedTemplateData = computed(() => normalizeTemplateData(this.template.value));
  sampleRecords = computed(
    () => this.normalizedTemplateData.value.data.sampleRecords ?? []
  );
  proofRecords = computed(() => {
    const hostContext = this.hostRenderContext.value;
    if (!hostContext) return this.sampleRecords.value;
    const validated = validateRuntimeRecords(hostContext.data);
    if (validated.ok) return validated.records;
    return hostContext.record ? [hostContext.record] : [];
  });
  proofRenderContext = computed(() => {
    if (!this.proofMode.value) return void 0;
    const hostContext = this.hostRenderContext.value;
    const records = this.proofRecords.value;
    const recordIndex = clampRecordIndex(this.proofRecordIndex.value, records.length);
    const record = hostContext?.record && !this.proofRecordSelectionTouched.value ? hostContext.record : records[recordIndex] ?? hostContext?.record;
    return {
      data: hostContext?.data ?? (records.length === 1 ? records[0] : [...records]),
      ...record ? { record } : {},
      recordIndex,
      locale: hostContext?.locale ?? this.proofLocale.value,
      timeZone: hostContext?.timeZone ?? this.proofTimeZone.value,
      now: hostContext?.now ?? this.proofNow.value,
      mode: "proof"
    };
  });
  onChange;
  idFactory;
  lastEmitted = null;
  pendingGestureEchoes = [];
  gestureStart = null;
  constructor(initialTemplate, options = {}) {
    this.template.value = initialTemplate;
    this.history.value = [initialTemplate];
    this.onChange = options.onChange;
    this.idFactory = options.idFactory ?? randomId;
    this.hostRenderContext.value = options.renderContext;
    if (options.renderContext?.recordIndex !== void 0) {
      this.proofRecordIndex.value = options.renderContext.recordIndex;
    }
    this.repairProofRecordIndex();
  }
  setOnChange(onChange) {
    this.onChange = onChange;
  }
  setHostRenderContext(renderContext) {
    if (this.hostRenderContext.value === renderContext) return;
    this.hostRenderContext.value = renderContext;
    this.proofRecordSelectionTouched.value = false;
    this.proofRecordIndex.value = renderContext?.recordIndex ?? 0;
    this.repairProofRecordIndex();
  }
  setProofMode(active) {
    if (this.proofMode.value === active) return;
    this.proofMode.value = active;
    if (active) {
      this.editingComponentId.value = null;
      this.clearTableSession();
      this.repairProofRecordIndex();
    }
  }
  setProofRecordIndex(index) {
    if (!Number.isInteger(index) || index < 0) return;
    const next = clampRecordIndex(index, this.proofRecords.value.length);
    this.proofRecordSelectionTouched.value = true;
    if (this.proofRecordIndex.value !== next) this.proofRecordIndex.value = next;
  }
  setProofEnvironment(environment) {
    if (environment.now !== void 0) this.proofNow.value = environment.now;
    if (environment.locale !== void 0) this.proofLocale.value = environment.locale;
    if (environment.timeZone !== void 0) this.proofTimeZone.value = environment.timeZone;
  }
  /** Replaces the canonical field model and samples as one undoable template mutation. */
  replaceTemplateData(data) {
    if (this.template.value.data && this.template.value.dataSource === void 0 && this.template.value.dataSet === void 0 && structurallyEqual(this.template.value.data, data))
      return false;
    const { dataSource: _legacyFields, dataSet: _legacyDataSet, ...canonical } = this.template.value;
    void _legacyFields;
    void _legacyDataSet;
    this.commit({ ...canonical, data });
    return true;
  }
  /** Edits presentation metadata while preserving a field's stable id, path and child structure. */
  updateDataField(fieldId, patch) {
    const normalized = this.normalizedTemplateData.value.data;
    let changed = false;
    const update = (fields2) => fields2.map((field) => {
      const children = field.children ? update(field.children) : void 0;
      let next = children === field.children ? field : { ...field, children };
      if (field.id !== fieldId) return next;
      const name = patch.name?.trim();
      const nextName = name ? name : field.name;
      const formatter = patch.removeFormatter ? void 0 : patch.formatter ?? field.formatter;
      if (nextName === field.name && structurallyEqual(formatter, field.formatter)) return next;
      changed = true;
      const { formatter: _currentFormatter, ...withoutFormatter } = next;
      void _currentFormatter;
      next = {
        ...withoutFormatter,
        name: nextName,
        ...formatter ? { formatter } : {}
      };
      return next;
    });
    const fields = update(normalized.fields);
    if (!changed) return false;
    return this.replaceTemplateData({ ...normalized, fields });
  }
  setComponentBinding(componentId, target, expression) {
    const component = this.components.value.find((item) => item.id === componentId);
    if (!component || component.isLock) return false;
    if (!getComponentBindingTargets(component.component).some((item) => item.kind === target.kind)) {
      return false;
    }
    if (target.kind === "table-cell-text" && (component.component !== "RoySimpleTable" || !normalizeSimpleTableProps(component.propValue).cells[target.cellId]))
      return false;
    const bindings = component.bindings ?? [];
    const index = bindings.findIndex((binding2) => sameBindingTarget(binding2.target, target));
    const binding = index >= 0 ? { ...bindings[index], expression } : { id: this.idFactory(), target, expression };
    if (index >= 0 && structurallyEqual(bindings[index].expression, expression)) return false;
    const next = [...bindings];
    if (index >= 0) next[index] = binding;
    else next.push(binding);
    this.updateComponent(componentId, { bindings: next });
    return true;
  }
  removeComponentBinding(componentId, target) {
    const component = this.components.value.find((item) => item.id === componentId);
    if (!component || component.isLock || !component.bindings) return false;
    const bindings = component.bindings.filter(
      (binding) => !sameBindingTarget(binding.target, target)
    );
    if (bindings.length === component.bindings.length) return false;
    this.updateComponent(componentId, { bindings });
    return true;
  }
  removeSampleRecords() {
    const normalized = this.normalizedTemplateData.value.data;
    if (!normalized.sampleRecords) return false;
    const { sampleRecords: _samples, ...data } = normalized;
    void _samples;
    return this.replaceTemplateData(data);
  }
  recordRecentColor(color2) {
    const normalized = color2.trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(normalized)) return;
    this.recentColors.value = [
      normalized,
      ...this.recentColors.value.filter((item) => item !== normalized)
    ].slice(0, RECENT_COLOR_LIMIT);
  }
  syncExternal(template) {
    if (template === this.template.value) {
      this.pendingGestureEchoes = [];
      return;
    }
    if (template === this.lastEmitted) return;
    if (this.pendingGestureEchoes.length > 0) {
      if (structurallyEqual(template, this.template.value)) {
        this.pendingGestureEchoes = [];
        return;
      }
      if (this.pendingGestureEchoes.some((candidate) => structurallyEqual(candidate, template))) {
        return;
      }
      this.pendingGestureEchoes = [];
    }
    this.lastEmitted = null;
    this.template.value = template;
    this.currentPageIndex.value = Math.min(
      this.currentPageIndex.value,
      Math.max(0, template.pages.length - 1)
    );
    this.selectedIds.value = [];
    this.componentToReveal.value = null;
    this.temporaryHand.value = false;
    this.guides.value = [];
    this.selectedGuideId.value = null;
    this.history.value = [template];
    this.historyIndex.value = 0;
    this.editingComponentId.value = null;
    this.tableCellSelection.value = null;
    this.editingTableCell.value = null;
    this.proofMode.value = false;
    this.proofRecordSelectionTouched.value = false;
    this.proofRecordIndex.value = this.hostRenderContext.value?.recordIndex ?? 0;
    this.externalTemplateRevision.value += 1;
    this.repairProofRecordIndex();
    this.gestureStart = null;
  }
  setCurrentPage(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.template.value.pages.length) return;
    if (this.currentPageIndex.value === index) return;
    this.currentPageIndex.value = index;
    this.resetPageSession();
  }
  addPage() {
    const pages = [...this.template.value.pages];
    const page = { id: this.idFactory(), componentData: [] };
    const insertionIndex = Math.min(this.currentPageIndex.value + 1, pages.length);
    pages.splice(insertionIndex, 0, page);
    this.commit({ ...this.template.value, pages }, false, page.id);
    return page.id;
  }
  duplicatePage(index = this.currentPageIndex.value) {
    const source = this.template.value.pages[index];
    if (!source) return null;
    const page = duplicatePage(source, this.idFactory);
    const pages = [...this.template.value.pages];
    pages.splice(index + 1, 0, page);
    this.commit({ ...this.template.value, pages }, false, page.id);
    return page.id;
  }
  deletePage(index = this.currentPageIndex.value) {
    const pages = this.template.value.pages;
    if (pages.length <= 1 || !pages[index]) return;
    const activePageId = this.currentPage.value?.id;
    const next = pages.filter((_, pageIndex) => pageIndex !== index);
    const preferredPageId = activePageId === pages[index]?.id ? next[Math.min(index, next.length - 1)]?.id : activePageId;
    this.commit({ ...this.template.value, pages: next }, false, preferredPageId);
  }
  movePage(fromIndex, toIndex) {
    const pages = this.template.value.pages;
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex < 0 || fromIndex >= pages.length || toIndex < 0 || toIndex >= pages.length || fromIndex === toIndex)
      return;
    const activePageId = this.currentPage.value?.id;
    const next = [...pages];
    const [page] = next.splice(fromIndex, 1);
    if (!page) return;
    next.splice(toIndex, 0, page);
    this.commit({ ...this.template.value, pages: next }, false, activePageId);
  }
  selectComponent(id, additive = false) {
    if (!this.components.value.some((component) => component.id === id)) return;
    if (this.tableCellSelection.value?.componentId !== id) this.clearTableSession();
    this.selectedGuideId.value = null;
    if (!additive) {
      this.selectedIds.value = [id];
      return;
    }
    this.selectedIds.value = this.selectedIds.value.includes(id) ? this.selectedIds.value.filter((selectedId) => selectedId !== id) : [...this.selectedIds.value, id];
  }
  startContentEditing(id) {
    const component = this.components.value.find((item) => item.id === id);
    if (this.proofMode.value || !component || component.isLock || component.component !== "RoySimpleText" && component.component !== "RoyText")
      return false;
    this.selectComponent(id);
    this.activeTool.value = "select";
    this.temporaryHand.value = false;
    this.editingComponentId.value = id;
    this.editingTableCell.value = null;
    return true;
  }
  commitContentEditing(id, propValue) {
    if (this.editingComponentId.value !== id) return;
    this.editingComponentId.value = null;
    this.updateComponent(id, { propValue });
  }
  cancelContentEditing(id) {
    if (this.editingComponentId.value === id) this.editingComponentId.value = null;
  }
  selectTableCell(componentId, row, column, extend = false) {
    if (this.proofMode.value) return false;
    const component = this.components.value.find((item) => item.id === componentId);
    if (!component || component.component !== "RoySimpleTable" || component.isLock) return false;
    const value = normalizeSimpleTableProps(component.propValue);
    const cell = getTableCellAt(value, row, column);
    const bounds = cell ? getTableCellBounds(value, cell.id) : null;
    if (!cell || !bounds) return false;
    this.selectComponent(componentId);
    this.activeTool.value = "select";
    this.temporaryHand.value = false;
    const current = this.tableCellSelection.value;
    this.tableCellSelection.value = extend && current?.componentId === componentId ? { ...current, focusRow: row, focusColumn: column } : {
      componentId,
      anchorRow: bounds.startRow,
      anchorColumn: bounds.startColumn,
      focusRow: row,
      focusColumn: column
    };
    return true;
  }
  startTableCellEditing(componentId, cellId) {
    if (this.proofMode.value) return false;
    const component = this.components.value.find((item) => item.id === componentId);
    if (!component || component.component !== "RoySimpleTable" || component.isLock) return false;
    const value = normalizeSimpleTableProps(component.propValue);
    const bounds = getTableCellBounds(value, cellId);
    if (!value.cells[cellId] || !bounds) return false;
    this.selectTableCell(componentId, bounds.startRow, bounds.startColumn);
    this.editingComponentId.value = null;
    this.editingTableCell.value = { componentId, cellId };
    return true;
  }
  commitTableCellEditing(componentId, cellId, text2) {
    const editing = this.editingTableCell.value;
    if (editing?.componentId !== componentId || editing.cellId !== cellId) return;
    this.editingTableCell.value = null;
    const component = this.components.value.find((item) => item.id === componentId);
    if (!component || component.component !== "RoySimpleTable" || component.isLock) return;
    const current = normalizeSimpleTableProps(component.propValue);
    const next = updateTableCellText(current, cellId, text2);
    if (next !== current) this.updateComponent(componentId, { propValue: next });
  }
  cancelTableCellEditing(componentId, cellId) {
    const editing = this.editingTableCell.value;
    if (editing?.componentId === componentId && editing.cellId === cellId) {
      this.editingTableCell.value = null;
    }
  }
  clearTableCellSelection(componentId) {
    if (componentId && this.tableCellSelection.value?.componentId !== componentId) return;
    this.clearTableSession();
  }
  selectComponents(ids) {
    const available = new Set(this.components.value.map((component) => component.id));
    this.selectedIds.value = [...new Set(ids)].filter((id) => available.has(id));
    this.selectedGuideId.value = null;
    if (this.selectedIds.value.length !== 1 || this.tableCellSelection.value?.componentId !== this.selectedIds.value[0])
      this.clearTableSession();
  }
  clearSelection() {
    this.selectedIds.value = [];
    this.selectedGuideId.value = null;
    this.clearTableSession();
  }
  setActiveTool(tool) {
    if (isShapeDrawingTool(tool)) this.lastDrawingTool.value = tool;
    if (this.activeTool.value === tool) return;
    this.activeTool.value = tool;
    this.cancelAreaSelection();
  }
  setTemporaryHand(active) {
    if (this.temporaryHand.value === active) return;
    this.temporaryHand.value = active;
    this.cancelAreaSelection();
  }
  addGuide(axis, positionMm) {
    if (this.guidesLocked.value || !Number.isFinite(positionMm)) return null;
    const id = this.idFactory();
    this.guides.value = [
      ...this.guides.value,
      {
        id,
        axis,
        positionMm: this.clampGuidePosition(axis, positionMm),
        color: this.activeGuideColor.value
      }
    ];
    this.selectedIds.value = [];
    this.selectedGuideId.value = id;
    this.guidesVisible.value = true;
    return id;
  }
  selectGuide(id) {
    if (id && !this.guides.value.some((guide) => guide.id === id)) return;
    this.selectedIds.value = [];
    this.selectedGuideId.value = id;
  }
  moveGuide(id, positionMm) {
    if (this.guidesLocked.value || !Number.isFinite(positionMm)) return;
    this.guides.value = this.guides.value.map(
      (guide) => guide.id === id ? { ...guide, positionMm: this.clampGuidePosition(guide.axis, positionMm) } : guide
    );
  }
  removeGuide(id) {
    if (this.guidesLocked.value) return;
    this.guides.value = this.guides.value.filter((guide) => guide.id !== id);
    if (this.selectedGuideId.value === id) this.selectedGuideId.value = null;
  }
  removeSelectedGuide() {
    const id = this.selectedGuideId.value;
    if (id) this.removeGuide(id);
  }
  setGuideColor(color2) {
    this.activeGuideColor.value = color2;
    const selected = this.selectedGuideId.value;
    if (!selected || this.guidesLocked.value) return;
    this.guides.value = this.guides.value.map(
      (guide) => guide.id === selected ? { ...guide, color: color2 } : guide
    );
  }
  toggleGuidesVisible() {
    this.guidesVisible.value = !this.guidesVisible.value;
  }
  toggleGuidesLocked() {
    this.guidesLocked.value = !this.guidesLocked.value;
  }
  clearGuides() {
    if (this.guidesLocked.value) return;
    this.guides.value = [];
    this.selectedGuideId.value = null;
  }
  startAreaSelection(left, top) {
    this.isSelectingArea.value = true;
    this.areaSelection.value = {
      style: { left, top, width: 0, height: 0 },
      componentIds: []
    };
  }
  updateAreaSelection(style) {
    this.areaSelection.value = { ...this.areaSelection.value, style };
  }
  finishAreaSelection(componentIds) {
    this.selectComponents(componentIds);
    this.areaSelection.value = { ...this.areaSelection.value, componentIds };
    this.isSelectingArea.value = false;
  }
  cancelAreaSelection() {
    this.isSelectingArea.value = false;
    this.areaSelection.value = {
      style: { top: 0, left: 0, width: 0, height: 0 },
      componentIds: []
    };
  }
  addComponent(component) {
    this.updateCurrentPage((components) => [...components, clone(component)]);
    this.selectComponent(component.id);
  }
  completeDrawnComponent(component, tool) {
    if (this.effectiveTool.value !== tool || component.component !== tool || !isDrawnComponentType(component.component))
      return false;
    this.addComponent(component);
    if (isShapeDrawingTool(tool)) return true;
    this.setActiveTool("select");
    if (tool === "RoySimpleText" || tool === "RoyText") {
      this.startContentEditing(component.id);
    }
    return true;
  }
  requestComponentReveal(id) {
    if (!this.components.value.some((component) => component.id === id)) return;
    this.componentToReveal.value = id;
  }
  finishComponentReveal(id) {
    if (this.componentToReveal.value === id) this.componentToReveal.value = null;
  }
  updateComponent(id, patch, transient = false) {
    const current = this.components.value.find((component) => component.id === id);
    if (!current || current.isLock && Object.keys(patch).some((key) => key !== "isLock")) return;
    this.updateCurrentPage(
      (components) => components.map(
        (component) => component.id === id && hasChanges(component, patch) ? { ...component, ...patch } : component
      ),
      transient
    );
  }
  updateComponentStyle(id, patch, transient = false) {
    const current = this.components.value.find((component) => component.id === id);
    if (!current || current.isLock) return;
    this.updateCurrentPage(
      (components) => components.map(
        (component) => component.id === id && hasChanges(component.style, patch) ? { ...component, style: { ...component.style, ...patch } } : component
      ),
      transient
    );
  }
  updateSelectedStyles(patch, transient = false) {
    const ids = new Set(this.selectedIds.value);
    if (ids.size === 0 || this.hasLockedSelection()) return;
    this.updateCurrentPage(
      (components) => components.map(
        (component) => ids.has(component.id) && hasChanges(component.style, patch) ? { ...component, style: { ...component.style, ...patch } } : component
      ),
      transient
    );
  }
  transformComponent(id, patch, transient = false) {
    const component = this.components.value.find((item) => item.id === id);
    if (!component || component.isLock) return false;
    this.updateComponentStyle(id, patch, transient);
    return true;
  }
  moveSelection(deltaLeft, deltaTop, transient = false) {
    const ids = new Set(this.selectedIds.value);
    if (ids.size === 0 || this.hasLockedSelection()) return;
    this.updateCurrentPage(
      (components) => components.map((component) => {
        if (!ids.has(component.id) || component.isLock) return component;
        return withPosition(
          component,
          number(component.style.left) + deltaLeft,
          number(component.style.top) + deltaTop
        );
      }),
      transient
    );
  }
  deleteSelected() {
    const ids = new Set(this.selectedIds.value);
    if (ids.size === 0 || this.hasLockedSelection()) return;
    if (this.editingComponentId.value && ids.has(this.editingComponentId.value)) {
      this.editingComponentId.value = null;
    }
    if (this.editingTableCell.value && ids.has(this.editingTableCell.value.componentId)) {
      this.clearTableSession();
    }
    this.updateCurrentPage((components) => components.filter((component) => !ids.has(component.id)));
    this.clearSelection();
  }
  copy() {
    if (this.selectedComponents.value.length === 0) return;
    this.clipboard.value = { components: clone(this.selectedComponents.value), isCut: false };
  }
  cut() {
    if (this.selectedComponents.value.length === 0 || this.hasLockedSelection()) return;
    this.clipboard.value = { components: clone(this.selectedComponents.value), isCut: true };
    this.deleteSelected();
  }
  paste(offset = PASTE_OFFSET) {
    const clipboard = this.clipboard.value;
    if (!clipboard) return;
    const pasted = clipboard.components.map((component) => {
      const next = regenerateIds(component, this.idFactory);
      return withPosition(next, number(next.style.left) + offset, number(next.style.top) + offset);
    });
    this.updateCurrentPage((components) => [...components, ...pasted]);
    this.selectComponents(pasted.map((component) => component.id));
    if (clipboard.isCut) this.clipboard.value = null;
  }
  pasteAt(left, top) {
    const clipboard = this.clipboard.value;
    if (!clipboard || !Number.isFinite(left) || !Number.isFinite(top)) return;
    const pasted = clipboard.components.map((component) => regenerateIds(component, this.idFactory));
    const boxes = pasted.map((component) => getComponentRotatedStyle(component.style));
    const selectionLeft = Math.min(...boxes.map((box) => box.left));
    const selectionTop = Math.min(...boxes.map((box) => box.top));
    const selectionRight = Math.max(...boxes.map((box) => box.right));
    const selectionBottom = Math.max(...boxes.map((box) => box.bottom));
    const page = getPageDimensions(this.pageConfig.value);
    const minDeltaLeft = -selectionLeft;
    const minDeltaTop = -selectionTop;
    const maxDeltaLeft = page.width - selectionRight;
    const maxDeltaTop = page.height - selectionBottom;
    const requestedDeltaLeft = left - selectionLeft;
    const requestedDeltaTop = top - selectionTop;
    const deltaLeft = minDeltaLeft <= maxDeltaLeft ? clamp3(requestedDeltaLeft, minDeltaLeft, maxDeltaLeft) : requestedDeltaLeft;
    const deltaTop = minDeltaTop <= maxDeltaTop ? clamp3(requestedDeltaTop, minDeltaTop, maxDeltaTop) : requestedDeltaTop;
    const positioned = pasted.map(
      (component) => withPosition(
        component,
        number(component.style.left) + deltaLeft,
        number(component.style.top) + deltaTop
      )
    );
    this.updateCurrentPage((components) => [...components, ...positioned]);
    this.selectComponents(positioned.map((component) => component.id));
    if (clipboard.isCut) this.clipboard.value = null;
  }
  setLock(locked) {
    const ids = new Set(this.selectedIds.value);
    if (ids.size === 0) return;
    if (locked && this.editingComponentId.value && ids.has(this.editingComponentId.value)) {
      this.editingComponentId.value = null;
    }
    if (locked && this.editingTableCell.value && ids.has(this.editingTableCell.value.componentId)) {
      this.editingTableCell.value = null;
    }
    this.updateCurrentPage(
      (components) => components.map(
        (component) => ids.has(component.id) && Boolean(component.isLock) !== locked ? { ...component, isLock: locked } : component
      )
    );
  }
  toggleLock() {
    const selected = this.selectedComponents.value;
    if (selected.length === 0) return;
    this.setLock(!selected.every((component) => component.isLock));
  }
  moveLayer(action) {
    const selected = new Set(this.selectedIds.value);
    if (selected.size === 0 || this.hasLockedSelection()) return;
    const source = [...this.components.value];
    if (action === "front" || action === "back") {
      const picked = source.filter((component) => selected.has(component.id));
      const rest = source.filter((component) => !selected.has(component.id));
      this.replaceCurrentPage(action === "front" ? [...rest, ...picked] : [...picked, ...rest]);
      return;
    }
    if (action === "forward") {
      for (let index = source.length - 2; index >= 0; index--) {
        if (selected.has(source[index].id) && !selected.has(source[index + 1].id)) {
          ;
          [source[index], source[index + 1]] = [source[index + 1], source[index]];
        }
      }
    } else {
      for (let index = 1; index < source.length; index++) {
        if (selected.has(source[index].id) && !selected.has(source[index - 1].id)) {
          ;
          [source[index], source[index - 1]] = [source[index - 1], source[index]];
        }
      }
    }
    this.replaceCurrentPage(source);
  }
  align(alignment) {
    const selected = this.selectedComponents.value;
    if (selected.length < 2 || this.hasLockedSelection()) return;
    const bounds = selected.map((component) => getComponentRotatedStyle(component.style));
    const left = Math.min(...bounds.map((box) => box.left));
    const right = Math.max(...bounds.map((box) => box.right));
    const top = Math.min(...bounds.map((box) => box.top));
    const bottom = Math.max(...bounds.map((box) => box.bottom));
    const ids = new Set(selected.map((component) => component.id));
    this.updateCurrentPage(
      (components) => components.map((component) => {
        if (!ids.has(component.id) || component.isLock) return component;
        const box = getComponentRotatedStyle(component.style);
        let dx = 0;
        let dy = 0;
        if (alignment === "left") dx = left - box.left;
        if (alignment === "center") dx = (left + right - box.left - box.right) / 2;
        if (alignment === "right") dx = right - box.right;
        if (alignment === "top") dy = top - box.top;
        if (alignment === "middle") dy = (top + bottom - box.top - box.bottom) / 2;
        if (alignment === "bottom") dy = bottom - box.bottom;
        return withPosition(
          component,
          number(component.style.left) + dx,
          number(component.style.top) + dy
        );
      })
    );
  }
  distribute(direction) {
    const selected = this.selectedComponents.value;
    if (selected.length < 3 || this.hasLockedSelection()) return;
    const horizontal = direction === "horizontal";
    const sorted = [...selected].sort((a, b) => {
      const aBox = getComponentRotatedStyle(a.style);
      const bBox = getComponentRotatedStyle(b.style);
      return (horizontal ? aBox.left : aBox.top) - (horizontal ? bBox.left : bBox.top);
    });
    const boxes = sorted.map((component) => getComponentRotatedStyle(component.style));
    const start = horizontal ? boxes[0].left : boxes[0].top;
    const end = horizontal ? boxes.at(-1).right : boxes.at(-1).bottom;
    const occupied = boxes.reduce((sum2, box) => sum2 + (horizontal ? box.width : box.height), 0);
    const gap = (end - start - occupied) / (boxes.length - 1);
    const shifts = /* @__PURE__ */ new Map();
    let cursor = start;
    sorted.forEach((component, index) => {
      const box = boxes[index];
      shifts.set(component.id, cursor - (horizontal ? box.left : box.top));
      cursor += (horizontal ? box.width : box.height) + gap;
    });
    this.updateCurrentPage(
      (components) => components.map((component) => {
        const shift = shifts.get(component.id);
        if (shift === void 0 || component.isLock) return component;
        return withPosition(
          component,
          number(component.style.left) + (horizontal ? shift : 0),
          number(component.style.top) + (horizontal ? 0 : shift)
        );
      })
    );
  }
  group() {
    const selectedIds = new Set(this.selectedIds.value);
    const selected = this.components.value.filter((component) => selectedIds.has(component.id));
    if (selected.length < 2 || this.hasLockedSelection()) return;
    const flattened = selected.flatMap((component) => {
      if (component.component !== "RoyGroup" || !Array.isArray(component.propValue))
        return [clone(component)];
      return getAbsoluteGroupChildren(component).map((child) => clone(child));
    });
    const bounds = flattened.map((component) => getComponentRotatedStyle(component.style));
    const left = Math.min(...bounds.map((box) => box.left));
    const top = Math.min(...bounds.map((box) => box.top));
    const right = Math.max(...bounds.map((box) => box.right));
    const bottom = Math.max(...bounds.map((box) => box.bottom));
    const group = {
      id: this.idFactory(),
      component: "RoyGroup",
      name: "\u7EC4\u5408",
      propValue: flattened.map(
        (child) => withPosition(child, number(child.style.left) - left, number(child.style.top) - top)
      ),
      style: { width: right - left, height: bottom - top, left, top, rotate: 0, opacity: 1 },
      groupStyle: createGroupMetrics(right - left, bottom - top),
      position: {}
    };
    const source = this.components.value;
    const insertionIndex = source.findIndex((component) => selectedIds.has(component.id));
    const next = source.filter((component) => !selectedIds.has(component.id));
    next.splice(insertionIndex, 0, group);
    this.replaceCurrentPage(next);
    this.selectComponent(group.id);
  }
  ungroup() {
    if (this.hasLockedSelection()) return;
    const selectedIds = new Set(this.selectedIds.value);
    const restoredIds = [];
    let changed = false;
    const next = this.components.value.flatMap((component) => {
      if (!selectedIds.has(component.id) || component.component !== "RoyGroup" || !Array.isArray(component.propValue))
        return [component];
      changed = true;
      return getAbsoluteGroupChildren(component).map((child) => {
        restoredIds.push(child.id);
        return clone(child);
      });
    });
    if (!changed) return;
    this.replaceCurrentPage(next);
    this.selectComponents(restoredIds);
  }
  setPageDirection(direction) {
    this.updatePageConfig({ pageDirection: direction });
  }
  updatePageConfig(patch, transient = false) {
    const current = this.pageConfig.value;
    if (!hasChanges(current, patch)) return false;
    const next = { ...current, ...patch };
    if (pageConfigError(next)) return false;
    this.commit({ ...this.template.value, pageConfig: next }, transient);
    this.guides.value = this.guides.value.map((guide) => ({
      ...guide,
      positionMm: this.clampGuidePosition(guide.axis, guide.positionMm)
    }));
    return true;
  }
  setZoom(scale) {
    this.scale.value = Math.min(2, Math.max(0.25, Math.round(scale * 100) / 100));
  }
  setMeasurementUnit(unit) {
    this.measurementUnit.value = unit;
  }
  toggleRuler() {
    this.showRuler.value = !this.showRuler.value;
  }
  beginGesture() {
    if (this.gestureStart) return;
    this.gestureStart = this.template.value;
    this.rememberGestureEcho(this.gestureStart);
  }
  commitGesture() {
    const start = this.gestureStart;
    this.gestureStart = null;
    if (!start || start === this.template.value) return;
    this.pushHistory(this.template.value);
  }
  cancelGesture() {
    const start = this.gestureStart;
    this.gestureStart = null;
    if (!start || start === this.template.value) return;
    this.template.value = start;
    this.emit(start);
    this.repairSelection();
  }
  undo() {
    if (!this.canUndo.value) return;
    this.editingComponentId.value = null;
    this.clearTableSession();
    this.historyIndex.value -= 1;
    this.restoreHistory();
  }
  redo() {
    if (!this.canRedo.value) return;
    this.editingComponentId.value = null;
    this.clearTableSession();
    this.historyIndex.value += 1;
    this.restoreHistory();
  }
  updateCurrentPage(update, transient = false) {
    const pageIndex = this.currentPageIndex.value;
    const current = this.template.value.pages[pageIndex];
    if (!current) return;
    const components = update(current.componentData);
    if (components === current.componentData || components.length === current.componentData.length && components.every((component, index) => component === current.componentData[index]))
      return;
    const pages = this.template.value.pages.map(
      (page, index) => index === pageIndex ? { ...page, componentData: components } : page
    );
    this.commit({ ...this.template.value, pages }, transient);
  }
  replaceCurrentPage(components) {
    this.updateCurrentPage(() => components);
  }
  commit(template, transient = false, preferredPageId) {
    if (template === this.template.value) return;
    const previousPageId = this.currentPage.value?.id;
    this.template.value = template;
    this.repairProofRecordIndex();
    this.repairCurrentPage(preferredPageId);
    this.emit(template);
    if (!transient) this.pushHistory(template);
    if (previousPageId !== this.currentPage.value?.id) this.resetPageSession();
    else this.repairSelection();
  }
  pushHistory(template) {
    const next = this.history.value.slice(0, this.historyIndex.value + 1);
    if (next.at(-1) === template) return;
    next.push(template);
    if (next.length > HISTORY_LIMIT) next.splice(0, next.length - HISTORY_LIMIT);
    this.history.value = next;
    this.historyIndex.value = next.length - 1;
  }
  restoreHistory() {
    const template = this.history.value[this.historyIndex.value];
    if (!template) return;
    const previousPageId = this.currentPage.value?.id;
    this.template.value = template;
    this.repairProofRecordIndex();
    this.repairCurrentPage(previousPageId);
    this.emit(template);
    this.gestureStart = null;
    if (previousPageId !== this.currentPage.value?.id) this.resetPageSession();
    else this.repairSelection();
  }
  emit(template) {
    this.lastEmitted = template;
    if (this.gestureStart) this.rememberGestureEcho(template);
    this.onChange?.(template);
  }
  rememberGestureEcho(template) {
    if (this.pendingGestureEchoes.at(-1) === template) return;
    this.pendingGestureEchoes.push(template);
    if (this.pendingGestureEchoes.length > GESTURE_ECHO_LIMIT) {
      this.pendingGestureEchoes.splice(0, this.pendingGestureEchoes.length - GESTURE_ECHO_LIMIT);
    }
  }
  repairCurrentPage(preferredPageId) {
    const pages = this.template.value.pages;
    const preferredIndex = preferredPageId ? pages.findIndex((page) => page.id === preferredPageId) : -1;
    this.currentPageIndex.value = preferredIndex >= 0 ? preferredIndex : Math.min(this.currentPageIndex.value, Math.max(0, pages.length - 1));
  }
  resetPageSession() {
    this.selectedIds.value = [];
    this.editingComponentId.value = null;
    this.clearTableSession();
    this.componentToReveal.value = null;
    this.guides.value = [];
    this.selectedGuideId.value = null;
    this.cancelAreaSelection();
  }
  repairSelection() {
    const available = new Set(this.components.value.map((component) => component.id));
    const repaired = this.selectedIds.value.filter((id) => available.has(id));
    if (repaired.length !== this.selectedIds.value.length) this.selectedIds.value = repaired;
    const tableSelection = this.tableCellSelection.value;
    if (tableSelection && !available.has(tableSelection.componentId)) this.clearTableSession();
  }
  clearTableSession() {
    this.tableCellSelection.value = null;
    this.editingTableCell.value = null;
  }
  repairProofRecordIndex() {
    const next = clampRecordIndex(this.proofRecordIndex.value, this.proofRecords.value.length);
    if (this.proofRecordIndex.value !== next) this.proofRecordIndex.value = next;
  }
  hasLockedSelection() {
    return this.selectedComponents.value.some((component) => component.isLock);
  }
  clampGuidePosition(axis, positionMm) {
    const { pageDirection, pageWidth, pageHeight } = this.pageConfig.value;
    const width = pageDirection === "l" ? number(pageHeight) : number(pageWidth);
    const height = pageDirection === "l" ? number(pageWidth) : number(pageHeight);
    return Math.min(axis === "x" ? width : height, Math.max(0, positionMm));
  }
};
function clampRecordIndex(index, recordCount) {
  if (recordCount <= 0) return 0;
  return Math.min(Math.max(0, index), recordCount - 1);
}
function sameBindingTarget(left, right) {
  if (left.kind !== right.kind) return false;
  if (left.kind === "table-cell-text" && right.kind === "table-cell-text") {
    return left.cellId === right.cellId;
  }
  return true;
}
function structurallyEqual(left, right) {
  if (left === right) return true;
  return JSON.stringify(left) === JSON.stringify(right);
}
function hasChanges(source, patch) {
  return Object.entries(patch).some(([key, value]) => source[key] !== value);
}
function createEditorStore(initialTemplate, options) {
  return new EditorStore(initialTemplate, options);
}

// src/state/EditorStoreContext.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var EditorStoreContext = createContext(null);
function EditorStoreProvider({ store, children }) {
  return /* @__PURE__ */ jsx(EditorStoreContext.Provider, { value: store, children });
}
function useEditorStore() {
  const store = useContext(EditorStoreContext);
  if (!store) throw new Error("useEditorStore must be used inside EditorStoreProvider");
  return store;
}

// src/hooks/useEditorKeyboard.ts
import { useEffect as useEffect2 } from "react";
var EDITOR_INTERACTIVE_SELECTOR = [
  "[data-ptd-editor-interactive]",
  "[data-ptd-portal-interactive]",
  '[role="menu"]',
  '[role="menuitem"]'
].join(",");
function isEditableTarget(target) {
  if (!target || typeof target !== "object") return false;
  const element = target;
  const tagName = element.tagName?.toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select" || Boolean(element.isContentEditable) || Boolean(element.closest?.('[contenteditable="true"]'));
}
function isEditorInteractiveTarget(target) {
  if (isEditableTarget(target)) return true;
  if (!target || typeof target !== "object") return false;
  const element = target;
  return Boolean(element.closest?.(EDITOR_INTERACTIVE_SELECTOR));
}
function isSelectToolShortcut(key, modifiers = {}) {
  if (modifiers.command || modifiers.alt) return false;
  const normalized = key.toLowerCase();
  return normalized === "v" || normalized === "escape";
}
function isHandToolShortcut(key, modifiers = {}) {
  return !modifiers.command && !modifiers.alt && key.toLowerCase() === "h";
}
function isTemporaryHandKey(key, code = "") {
  return key === " " || key === "Space" || key === "Spacebar" || code === "Space";
}
function getHostCommandShortcut(key, modifiers = {}) {
  if (!modifiers.command) return null;
  const normalized = key.toLowerCase();
  if (normalized === "s") return modifiers.shift ? "saveAs" : "save";
  if (!modifiers.shift && normalized === "n") return "new";
  if (!modifiers.shift && normalized === "o") return "open";
  return null;
}
function useEditorKeyboard(store, rootRef, actions = {}) {
  useEffect2(() => {
    const handleKeyDown = (event) => {
      if (isEditorInteractiveTarget(event.target)) return;
      const root = rootRef.current;
      if (!root || !root.contains(document.activeElement)) return;
      const command = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      const hostCommand = getHostCommandShortcut(key, { command, shift: event.shiftKey });
      if (hostCommand && actions.hostCommands) {
        const state = actions.hostCommands.getState(hostCommand);
        if (state.enabled && !state.pending) {
          event.preventDefault();
          void actions.hostCommands.execute(hostCommand);
        }
        return;
      }
      if (command && key === "z") {
        event.preventDefault();
        if (event.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (command && key === "y") {
        event.preventDefault();
        store.redo();
        return;
      }
      if (command && key === "c") {
        event.preventDefault();
        store.copy();
        return;
      }
      if (command && key === "x") {
        event.preventDefault();
        store.cut();
        return;
      }
      if (command && key === "v") {
        event.preventDefault();
        store.paste();
        return;
      }
      if (command && key === "g") {
        event.preventDefault();
        if (event.shiftKey) store.ungroup();
        else store.group();
        return;
      }
      if (command && key === "l") {
        event.preventDefault();
        store.toggleLock();
        return;
      }
      if (command && key === "]") {
        event.preventDefault();
        store.moveLayer("forward");
        return;
      }
      if (command && key === "[") {
        event.preventDefault();
        store.moveLayer("backward");
        return;
      }
      if (command && key === "r") {
        event.preventDefault();
        store.toggleRuler();
        return;
      }
      if (command && key === ";") {
        event.preventDefault();
        store.toggleGuidesVisible();
        return;
      }
      if (command && (key === "+" || key === "=")) {
        event.preventDefault();
        store.setZoom(store.scale.value + 0.25);
        return;
      }
      if (command && key === "-") {
        event.preventDefault();
        store.setZoom(store.scale.value - 0.25);
        return;
      }
      if (!command && !event.altKey && !event.shiftKey) {
        const panelByKey = {
          f6: "assets",
          f7: "pages",
          f8: "layers"
        };
        const panel = panelByKey[key];
        if (panel && actions.openResource) {
          event.preventDefault();
          actions.openResource(panel);
          return;
        }
        if (key === "f9" && actions.toggleInspector) {
          event.preventDefault();
          actions.toggleInspector();
          return;
        }
      }
      if (!command && !event.altKey && isTemporaryHandKey(event.key, event.code)) {
        event.preventDefault();
        store.setTemporaryHand(true);
        return;
      }
      if (isHandToolShortcut(key, { command, alt: event.altKey })) {
        event.preventDefault();
        store.setActiveTool("hand");
        return;
      }
      if (isSelectToolShortcut(key, { command, alt: event.altKey })) {
        event.preventDefault();
        store.setActiveTool("select");
        return;
      }
      if (key === "delete" || key === "backspace") {
        event.preventDefault();
        if (store.selectedGuideId.value) store.removeSelectedGuide();
        else store.deleteSelected();
        return;
      }
      const distance = event.shiftKey ? 10 : 1;
      const movement = {
        arrowleft: [-distance, 0],
        arrowright: [distance, 0],
        arrowup: [0, -distance],
        arrowdown: [0, distance]
      };
      const delta = movement[key];
      if (delta) {
        const selectedGuide = store.guides.value.find(
          (guide) => guide.id === store.selectedGuideId.value
        );
        if (selectedGuide) {
          const guideDelta = selectedGuide.axis === "x" ? delta[0] : delta[1];
          if (guideDelta !== 0) {
            event.preventDefault();
            store.moveGuide(
              selectedGuide.id,
              Math.max(0, selectedGuide.positionMm + guideDelta / 10)
            );
          }
          return;
        }
        event.preventDefault();
        store.moveSelection(delta[0], delta[1]);
      }
    };
    const handleKeyUp = (event) => {
      if (!store.temporaryHand.value || !isTemporaryHandKey(event.key, event.code)) return;
      event.preventDefault();
      store.setTemporaryHand(false);
    };
    const handleBlur = () => store.setTemporaryHand(false);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      store.setTemporaryHand(false);
    };
  }, [actions, rootRef, store]);
}

// src/hooks/useWorkspaceLayout.ts
import { useCallback as useCallback2, useEffect as useEffect3, useRef as useRef2, useState as useState2 } from "react";
var RESOURCE_PANEL_MIN = 240;
var RESOURCE_PANEL_MAX = 360;
var RESOURCE_PANEL_DEFAULT = 280;
var INSPECTOR_MIN = 280;
var INSPECTOR_MAX = 420;
var DEFAULT_RESOURCE_PANEL = "pages";
function workspaceModeForWidth(width) {
  if (width < 1180) return "compact";
  if (width < 1440) return "standard";
  return "wide";
}
function clampPanelWidth(side, width) {
  const minimum = side === "resources" ? RESOURCE_PANEL_MIN : INSPECTOR_MIN;
  const maximum = side === "resources" ? RESOURCE_PANEL_MAX : INSPECTOR_MAX;
  return Math.min(maximum, Math.max(minimum, Math.round(width)));
}
function useWorkspaceLayout(rootRef) {
  const [mode, setMode] = useState2("wide");
  const [activeResource, setActiveResource] = useState2(DEFAULT_RESOURCE_PANEL);
  const [resourcesOpen, setResourcesOpen] = useState2(true);
  const [inspectorOpen, setInspectorOpen] = useState2(true);
  const [resourceWidth, setResourceWidth] = useState2(RESOURCE_PANEL_DEFAULT);
  const [inspectorWidth, setInspectorWidth] = useState2(304);
  const initializedRef = useRef2(false);
  const modeRef = useRef2(mode);
  const activeResourceRef = useRef2(activeResource);
  const resizeCleanupRef = useRef2(null);
  useEffect3(() => {
    const root = rootRef.current;
    if (!root) return;
    const update = (width) => {
      const nextMode = workspaceModeForWidth(width);
      const previous = modeRef.current;
      modeRef.current = nextMode;
      setMode(nextMode);
      if (!initializedRef.current) {
        initializedRef.current = true;
        setResourcesOpen(nextMode === "wide");
        setInspectorOpen(nextMode !== "compact");
        return;
      }
      if (previous === nextMode) return;
      if (nextMode === "compact") {
        setResourcesOpen(false);
        setInspectorOpen(false);
      } else if (nextMode === "standard") {
        setResourcesOpen(false);
        setInspectorOpen(true);
      } else {
        setResourcesOpen(true);
        setInspectorOpen(true);
      }
    };
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) update(entry.contentRect.width);
    });
    observer.observe(root);
    update(root.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [rootRef]);
  useEffect3(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape" || modeRef.current !== "compact") return;
      setResourcesOpen(false);
      setInspectorOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);
  useEffect3(() => () => resizeCleanupRef.current?.(), []);
  const toggleResource = useCallback2((panel) => {
    const wasActive = activeResourceRef.current === panel;
    activeResourceRef.current = panel;
    setActiveResource(panel);
    setResourcesOpen((open) => {
      const next = wasActive ? !open : true;
      if (next && modeRef.current === "compact") setInspectorOpen(false);
      return next;
    });
  }, []);
  const openResource = useCallback2((panel) => {
    activeResourceRef.current = panel;
    setActiveResource(panel);
    setResourcesOpen(true);
    if (modeRef.current === "compact") setInspectorOpen(false);
  }, []);
  const toggleInspector = useCallback2(() => {
    setInspectorOpen((open) => {
      const next = !open;
      if (next && modeRef.current === "compact") setResourcesOpen(false);
      return next;
    });
  }, []);
  const openInspector = useCallback2(() => {
    setInspectorOpen(true);
    if (modeRef.current === "compact") setResourcesOpen(false);
  }, []);
  const closeOverlay = useCallback2(() => {
    if (modeRef.current !== "compact") return;
    setResourcesOpen(false);
    setInspectorOpen(false);
  }, []);
  const beginResize = useCallback2(
    (side, event) => {
      if (modeRef.current === "compact") return;
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = side === "resources" ? resourceWidth : inspectorWidth;
      const direction = side === "resources" ? 1 : -1;
      const move = (nextEvent) => {
        const nextWidth = startWidth + (nextEvent.clientX - startX) * direction;
        if (side === "resources") setResourceWidth(clampPanelWidth(side, nextWidth));
        else setInspectorWidth(clampPanelWidth(side, nextWidth));
      };
      const cleanup = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", cleanup);
        document.removeEventListener("pointercancel", cleanup);
        resizeCleanupRef.current = null;
      };
      resizeCleanupRef.current?.();
      resizeCleanupRef.current = cleanup;
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", cleanup);
      document.addEventListener("pointercancel", cleanup);
    },
    [inspectorWidth, resourceWidth]
  );
  return {
    mode,
    activeResource,
    resourcesOpen,
    inspectorOpen,
    resourceWidth,
    inspectorWidth,
    toggleResource,
    openResource,
    toggleInspector,
    openInspector,
    closeOverlay,
    beginResize
  };
}

// src/components/AppBar/AppBar.tsx
import { useEffect as useEffect4, useRef as useRef3, useState as useState3 } from "react";
import { useSignals } from "@preact/signals-react/runtime";
import {
  RiArrowDownSLine,
  RiBookOpenLine,
  RiCloseLine,
  RiDatabase2Line,
  RiDeleteBinLine,
  RiFileLine,
  RiFileDownloadLine,
  RiFilePdf2Line,
  RiFileSearchLine,
  RiFileUploadLine,
  RiFolderOpenLine,
  RiFullscreenLine,
  RiGuideLine,
  RiEyeLine,
  RiInformationLine,
  RiKeyboardBoxLine,
  RiLayoutLine,
  RiLoader4Line,
  RiLockUnlockLine,
  RiMenuLine,
  RiPagesLine as RiPagesLine2,
  RiPrinterLine,
  RiRulerLine,
  RiSave2Line,
  RiSave3Line,
  RiSettings3Line
} from "@remixicon/react";

// src/components/AppBar/AppBar.module.css
var AppBar_default = {
  appBar: "AppBar_appBar",
  topBar: "AppBar_topBar",
  brand: "AppBar_brand",
  menuBar: "AppBar_menuBar",
  mobileCategories: "AppBar_mobileCategories",
  actions: "AppBar_actions",
  commandItem: "AppBar_commandItem",
  commandCopy: "AppBar_commandCopy",
  wordmark: "AppBar_wordmark",
  productName: "AppBar_productName",
  menuTrigger: "AppBar_menuTrigger",
  mnemonic: "AppBar_mnemonic",
  mobileMenuTrigger: "AppBar_mobileMenuTrigger",
  quietAction: "AppBar_quietAction",
  primaryAction: "AppBar_primaryAction",
  menuPanel: "AppBar_menuPanel",
  menuPanelClip: "AppBar_menuPanelClip",
  menuPanelInner: "AppBar_menuPanelInner",
  commandGrid: "AppBar_commandGrid",
  pendingIcon: "AppBar_pendingIcon",
  "ptd-app-command-spin": "AppBar_ptd-app-command-spin"
};

// src/components/AppBar/AppBar.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var APP_MENUS = [
  {
    id: "file",
    label: "\u6587\u4EF6",
    mnemonic: "F",
    items: [
      {
        kind: "host",
        command: "new",
        icon: RiFileLine,
        label: "\u65B0\u5EFA\u6A21\u677F",
        description: "\u521B\u5EFA\u4E00\u4E2A\u7A7A\u767D\u7ED3\u6784\u5316\u6587\u6863",
        shortcut: "Ctrl+N"
      },
      {
        kind: "host",
        command: "open",
        icon: RiFolderOpenLine,
        label: "\u6253\u5F00\u6A21\u677F",
        description: "\u8FD4\u56DE\u6587\u4EF6\u5DE5\u4F5C\u53F0\u5E76\u9009\u62E9\u6A21\u677F",
        shortcut: "Ctrl+O"
      },
      {
        kind: "host",
        command: "save",
        icon: RiSave3Line,
        label: "\u4FDD\u5B58\u6A21\u677F",
        description: "\u4FDD\u5B58\u5F53\u524D\u6A21\u677F\u5185\u5BB9",
        shortcut: "Ctrl+S"
      },
      {
        kind: "host",
        command: "saveAs",
        icon: RiSave2Line,
        label: "\u53E6\u5B58\u4E3A",
        description: "\u4EE5\u65B0\u6587\u6863\u4FDD\u5B58\u6A21\u677F\u526F\u672C",
        shortcut: "Ctrl+Shift+S"
      },
      {
        kind: "host",
        command: "importTemplate",
        icon: RiFileUploadLine,
        label: "\u5BFC\u5165\u6A21\u677F JSON",
        description: "\u4ECE\u672C\u5730 JSON \u6253\u5F00\u4E00\u4E2A\u672A\u4FDD\u5B58\u7684\u65B0\u6A21\u677F"
      },
      {
        kind: "host",
        command: "exportTemplate",
        icon: RiFileDownloadLine,
        label: "\u5BFC\u51FA\u6A21\u677F JSON",
        description: "\u4E0B\u8F7D\u5305\u542B\u5F53\u524D\u672A\u4FDD\u5B58\u4FEE\u6539\u7684\u5B8C\u6574\u6A21\u677F"
      },
      {
        kind: "host",
        command: "versionHistory",
        icon: RiPagesLine2,
        label: "\u7248\u672C\u5386\u53F2",
        description: "\u67E5\u770B\u5E76\u6062\u590D\u670D\u52A1\u5668\u5386\u53F2\u7248\u672C"
      },
      {
        kind: "host",
        command: "preview",
        icon: RiEyeLine,
        label: "\u6253\u5370\u9884\u89C8",
        description: "\u67E5\u770B\u81EA\u52A8\u5206\u9875\u540E\u7684\u771F\u5B9E\u7EB8\u9762"
      },
      {
        kind: "host",
        command: "exportDocument",
        icon: RiFilePdf2Line,
        label: "\u5BFC\u51FA PDF",
        description: "\u751F\u6210\u9AD8\u4FDD\u771F\u6253\u5370\u6587\u4EF6"
      },
      {
        kind: "host",
        command: "print",
        icon: RiPrinterLine,
        label: "\u6253\u5370",
        description: "\u4F7F\u7528\u5DF2\u5BFC\u51FA\u7684 PDF \u5B8C\u6210\u6253\u5370"
      }
    ]
  },
  {
    id: "template",
    label: "\u6A21\u677F",
    mnemonic: "T",
    items: [
      {
        kind: "workspace",
        command: "pageSettings",
        icon: RiSettings3Line,
        label: "\u9875\u9762\u8BBE\u7F6E",
        description: "\u914D\u7F6E\u7EB8\u5F20\u3001\u65B9\u5411\u3001\u8FB9\u8DDD\u548C\u9875\u9762\u5916\u89C2"
      },
      {
        kind: "workspace",
        command: "pages",
        icon: RiPagesLine2,
        label: "\u9875\u9762\u7BA1\u7406",
        description: "\u65B0\u589E\u3001\u590D\u5236\u3001\u6392\u5E8F\u548C\u5220\u9664\u6A21\u677F\u9875\u9762"
      },
      {
        kind: "workspace",
        command: "assets",
        icon: RiLayoutLine,
        label: "\u7D20\u6750\u8D44\u6E90",
        description: "\u6D4F\u89C8\u53EF\u63D2\u5165\u6A21\u677F\u7684\u7EC4\u4EF6\u4E0E\u7D20\u6750"
      },
      {
        kind: "workspace",
        command: "data",
        icon: RiDatabase2Line,
        label: "\u6570\u636E\u6E90",
        description: "\u7BA1\u7406\u5B57\u6BB5\u3001\u793A\u4F8B\u6570\u636E\u4E0E\u5185\u5BB9\u7ED1\u5B9A"
      },
      {
        kind: "planned",
        reason: "\u5373\u5C06\u63D0\u4F9B",
        icon: RiFileSearchLine,
        label: "\u6A21\u677F\u68C0\u67E5",
        description: "\u68C0\u67E5\u8D8A\u754C\u3001\u7F3A\u5931\u6570\u636E\u4E0E\u6253\u5370\u98CE\u9669"
      }
    ]
  },
  {
    id: "view",
    label: "\u89C6\u56FE",
    mnemonic: "V",
    items: [
      {
        kind: "editor",
        command: "toggleRuler",
        icon: RiRulerLine,
        label: "\u663E\u793A\u6807\u5C3A",
        description: "\u5207\u6362\u9875\u9762\u6807\u5C3A\u663E\u793A",
        shortcut: "Ctrl+R"
      },
      {
        kind: "editor",
        command: "toggleGuides",
        icon: RiGuideLine,
        label: "\u663E\u793A\u53C2\u8003\u7EBF",
        description: "\u5207\u6362\u53C2\u8003\u7EBF\u663E\u793A",
        shortcut: "Ctrl+;"
      },
      {
        kind: "editor",
        command: "toggleGuidesLock",
        icon: RiLockUnlockLine,
        label: "\u9501\u5B9A / \u89E3\u9501\u53C2\u8003\u7EBF",
        description: "\u4FDD\u62A4\u53C2\u8003\u7EBF\u4F4D\u7F6E\uFF0C\u907F\u514D\u8BEF\u64CD\u4F5C"
      },
      {
        kind: "editor",
        command: "clearGuides",
        icon: RiDeleteBinLine,
        label: "\u6E05\u9664\u5168\u90E8\u53C2\u8003\u7EBF",
        description: "\u79FB\u9664\u5F53\u524D\u9875\u9762\u4E0A\u7684\u5168\u90E8\u53C2\u8003\u7EBF"
      },
      {
        kind: "planned",
        reason: "\u5373\u5C06\u63D0\u4F9B",
        icon: RiFullscreenLine,
        label: "\u9002\u5408\u9875\u9762",
        description: "\u8BA9\u9875\u9762\u5B8C\u6574\u9002\u914D\u5F53\u524D\u5DE5\u4F5C\u533A",
        shortcut: "Ctrl+0"
      }
    ]
  },
  {
    id: "help",
    label: "\u5E2E\u52A9",
    mnemonic: "H",
    items: [
      {
        kind: "host",
        command: "keyboardShortcuts",
        icon: RiKeyboardBoxLine,
        label: "\u5FEB\u6377\u952E",
        description: "\u67E5\u770B\u8BBE\u8BA1\u5668\u5FEB\u6377\u952E\u5217\u8868",
        shortcut: "Ctrl+/"
      },
      {
        kind: "host",
        command: "documentation",
        icon: RiBookOpenLine,
        label: "\u4EA7\u54C1\u4ECB\u7ECD",
        description: "\u4E86\u89E3 Foliq \u7684\u8BBE\u8BA1\u4E0E\u5DE5\u4F5C\u65B9\u5F0F"
      },
      {
        kind: "host",
        command: "about",
        icon: RiInformationLine,
        label: "\u5173\u4E8E Foliq",
        description: "\u67E5\u770B\u4EA7\u54C1\u7248\u672C\u4E0E\u9879\u76EE\u8BF4\u660E"
      }
    ]
  }
];
var UNAVAILABLE = { enabled: false, pending: false, reason: "\u6682\u4E0D\u53EF\u7528" };
function getEditorCommandState(store, command) {
  let enabled = true;
  if (command === "clearGuides")
    enabled = store.guides.value.length > 0 && !store.guidesLocked.value;
  return enabled ? { enabled: true, pending: false } : { enabled: false, pending: false, reason: "\u5F53\u524D\u72B6\u6001\u4E0D\u53EF\u7528" };
}
function runEditorCommand(store, command) {
  if (command === "toggleRuler") store.toggleRuler();
  if (command === "toggleGuides") store.toggleGuidesVisible();
  if (command === "toggleGuidesLock") store.toggleGuidesLocked();
  if (command === "clearGuides") store.clearGuides();
}
function AppBar({ hostCommands, workspace }) {
  useSignals();
  const store = useEditorStore();
  const [isExpanded, setIsExpanded] = useState3(false);
  const [activeMenuId, setActiveMenuId] = useState3("file");
  const appBarRef = useRef3(null);
  const menuButtonRefs = useRef3([]);
  const activeMenu = APP_MENUS.find((menu) => menu.id === activeMenuId) ?? APP_MENUS[0];
  const openMenu = (menuId) => {
    if (menuId) setActiveMenuId(menuId);
    setIsExpanded(true);
  };
  const closeMenu = () => setIsExpanded(false);
  const toggleMenu = (menuId) => {
    if (isExpanded && activeMenuId === menuId) closeMenu();
    else openMenu(menuId);
  };
  useEffect4(() => {
    if (!isExpanded) return;
    const handleOutsidePointerDown = (event) => {
      if (event.target instanceof Node && !appBarRef.current?.contains(event.target)) closeMenu();
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [isExpanded]);
  const getCommandState = (item) => {
    if (item.kind === "host") return hostCommands?.getState(item.command) ?? UNAVAILABLE;
    if (item.kind === "editor") return getEditorCommandState(store, item.command);
    if (item.kind === "workspace") {
      return workspace ? { enabled: true, pending: false } : { enabled: false, pending: false, reason: "\u5F53\u524D\u5DE5\u4F5C\u533A\u4E0D\u53EF\u7528" };
    }
    return { enabled: false, pending: false, reason: item.reason };
  };
  const executeCommand = async (item) => {
    const state = getCommandState(item);
    if (!state.enabled || state.pending) return false;
    if (item.kind === "host") return await hostCommands?.execute(item.command) ?? false;
    if (item.kind === "editor") runEditorCommand(store, item.command);
    if (item.kind === "workspace" && workspace) {
      if (item.command === "pageSettings") {
        store.clearSelection();
        workspace.openInspector();
      } else {
        workspace.openResource(item.command);
      }
    }
    return item.kind !== "planned";
  };
  const executeAndClose = (item) => {
    void executeCommand(item).then((executed) => {
      if (executed) closeMenu();
    });
  };
  const moveMenuFocus = (event, index) => {
    let nextIndex;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % APP_MENUS.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + APP_MENUS.length) % APP_MENUS.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = APP_MENUS.length - 1;
    if (nextIndex === void 0) return;
    event.preventDefault();
    const nextMenu = APP_MENUS[nextIndex];
    if (!nextMenu) return;
    if (isExpanded) openMenu(nextMenu.id);
    menuButtonRefs.current[nextIndex]?.focus();
  };
  const openState = hostCommands?.getState("open") ?? UNAVAILABLE;
  const saveState = hostCommands?.getState("save") ?? UNAVAILABLE;
  return /* @__PURE__ */ jsxs(
    "header",
    {
      ref: appBarRef,
      className: AppBar_default.appBar,
      "data-expanded": isExpanded,
      "data-ptd-editor-interactive": true,
      "data-ptd-region": "app-bar",
      onBlurCapture: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
      },
      onKeyDown: (event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        closeMenu();
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: AppBar_default.topBar, children: [
          /* @__PURE__ */ jsxs("div", { className: AppBar_default.brand, "aria-label": "Foliq \u7ED3\u6784\u5316\u6587\u6863\u8BBE\u8BA1\u5668", children: [
            /* @__PURE__ */ jsx2("span", { className: AppBar_default.wordmark, children: "Foliq" }),
            /* @__PURE__ */ jsx2("span", { className: AppBar_default.productName, children: "\u7ED3\u6784\u5316\u6587\u6863\u8BBE\u8BA1\u5668" })
          ] }),
          /* @__PURE__ */ jsx2("nav", { className: AppBar_default.menuBar, "aria-label": "\u5E94\u7528\u83DC\u5355", children: APP_MENUS.map((menu, index) => /* @__PURE__ */ jsxs(
            "button",
            {
              ref: (node) => {
                menuButtonRefs.current[index] = node;
              },
              type: "button",
              className: AppBar_default.menuTrigger,
              "data-active": isExpanded && activeMenuId === menu.id,
              "aria-controls": "ptd-application-menu",
              "aria-expanded": isExpanded && activeMenuId === menu.id,
              "aria-keyshortcuts": `Alt+${menu.mnemonic}`,
              accessKey: menu.mnemonic.toLowerCase(),
              onClick: () => toggleMenu(menu.id),
              onKeyDown: (event) => moveMenuFocus(event, index),
              children: [
                /* @__PURE__ */ jsxs("span", { children: [
                  menu.label,
                  /* @__PURE__ */ jsxs("span", { className: AppBar_default.mnemonic, children: [
                    "(",
                    menu.mnemonic,
                    ")"
                  ] })
                ] }),
                /* @__PURE__ */ jsx2(RiArrowDownSLine, { "aria-hidden": "true" })
              ]
            },
            menu.id
          )) }),
          /* @__PURE__ */ jsx2(
            "button",
            {
              type: "button",
              className: AppBar_default.mobileMenuTrigger,
              "aria-controls": "ptd-application-menu",
              "aria-expanded": isExpanded,
              "aria-label": isExpanded ? "\u5173\u95ED\u5E94\u7528\u83DC\u5355" : "\u6253\u5F00\u5E94\u7528\u83DC\u5355",
              onClick: () => isExpanded ? closeMenu() : openMenu(activeMenuId),
              children: isExpanded ? /* @__PURE__ */ jsx2(RiCloseLine, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx2(RiMenuLine, { "aria-hidden": "true" })
            }
          ),
          /* @__PURE__ */ jsx2("div", { className: AppBar_default.actions, children: hostCommands?.configured && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                className: AppBar_default.quietAction,
                "data-ptd-control-family": "document-action",
                "data-variant": "secondary",
                disabled: !openState.enabled || openState.pending,
                "aria-busy": openState.pending,
                title: openState.reason,
                onClick: () => void hostCommands.execute("open"),
                children: [
                  openState.pending ? /* @__PURE__ */ jsx2(RiLoader4Line, { className: AppBar_default.pendingIcon, "aria-hidden": "true" }) : /* @__PURE__ */ jsx2(RiFolderOpenLine, { "aria-hidden": "true" }),
                  /* @__PURE__ */ jsx2("span", { children: openState.pending ? "\u6B63\u5728\u8FD4\u56DE" : "\u6587\u4EF6\u5DE5\u4F5C\u53F0" })
                ]
              }
            ),
            /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                className: AppBar_default.primaryAction,
                "data-ptd-control-family": "document-action",
                "data-variant": "primary",
                disabled: !saveState.enabled || saveState.pending,
                "aria-busy": saveState.pending,
                title: saveState.reason,
                onClick: () => void hostCommands.execute("save"),
                children: [
                  saveState.pending ? /* @__PURE__ */ jsx2(RiLoader4Line, { className: AppBar_default.pendingIcon, "aria-hidden": "true" }) : /* @__PURE__ */ jsx2(RiSave3Line, { "aria-hidden": "true" }),
                  /* @__PURE__ */ jsx2("span", { children: saveState.pending ? "\u6B63\u5728\u4FDD\u5B58" : "\u4FDD\u5B58\u6A21\u677F" })
                ]
              }
            )
          ] }) })
        ] }),
        /* @__PURE__ */ jsx2(
          "div",
          {
            id: "ptd-application-menu",
            className: AppBar_default.menuPanel,
            "aria-hidden": !isExpanded,
            "data-ptd-region": "application-menu",
            children: /* @__PURE__ */ jsx2("div", { className: AppBar_default.menuPanelClip, children: /* @__PURE__ */ jsxs("div", { className: AppBar_default.menuPanelInner, "data-open": isExpanded, children: [
              /* @__PURE__ */ jsx2("nav", { className: AppBar_default.mobileCategories, "aria-label": "\u5E94\u7528\u83DC\u5355\u5206\u7C7B", children: APP_MENUS.map((menu) => /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  "data-active": activeMenuId === menu.id,
                  "aria-pressed": activeMenuId === menu.id,
                  tabIndex: isExpanded ? 0 : -1,
                  onClick: () => openMenu(menu.id),
                  children: [
                    menu.label,
                    /* @__PURE__ */ jsxs("span", { children: [
                      "(",
                      menu.mnemonic,
                      ")"
                    ] })
                  ]
                },
                menu.id
              )) }),
              /* @__PURE__ */ jsx2("div", { className: AppBar_default.commandGrid, "aria-label": `${activeMenu.label}\u83DC\u5355\u547D\u4EE4`, children: activeMenu.items.map((item) => {
                const state = getCommandState(item);
                const Icon = state.pending ? RiLoader4Line : item.icon;
                const disabled = !state.enabled || state.pending;
                const stateText = state.pending ? "\u6B63\u5728\u6267\u884C" : state.reason;
                return /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    className: AppBar_default.commandItem,
                    tabIndex: isExpanded ? 0 : -1,
                    disabled,
                    "aria-busy": state.pending,
                    "aria-label": stateText ? `${item.label}\uFF08${stateText}\uFF09` : item.label,
                    title: stateText ? `${item.label} \xB7 ${stateText}` : item.label,
                    onClick: () => executeAndClose(item),
                    children: [
                      /* @__PURE__ */ jsx2(
                        Icon,
                        {
                          className: state.pending ? AppBar_default.pendingIcon : void 0,
                          "aria-hidden": "true"
                        }
                      ),
                      /* @__PURE__ */ jsxs("span", { className: AppBar_default.commandCopy, children: [
                        /* @__PURE__ */ jsx2("strong", { children: item.label }),
                        /* @__PURE__ */ jsx2("span", { children: stateText ?? item.description })
                      ] }),
                      item.shortcut && /* @__PURE__ */ jsx2("kbd", { children: item.shortcut })
                    ]
                  },
                  item.label
                );
              }) })
            ] }) })
          }
        )
      ]
    }
  );
}

// src/components/Canvas/Canvas.tsx
import {
  useCallback as useCallback7,
  useEffect as useEffect10,
  useRef as useRef10,
  useState as useState8
} from "react";
import { useSignals as useSignals5 } from "@preact/signals-react/runtime";
import { getPageDimensions as getPageDimensions2, mmToPx as mmToPx3 } from "@ptd/core";

// src/utils/areaSelection.ts
var AREA_SCROLL_EDGE = 36;
var AREA_SCROLL_MAX_SPEED = 20;
function clamp4(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function canvasPointFromClient(editorRect, clientX, clientY, scale, bounds) {
  const safeScale = scale > 0 ? scale : 1;
  return {
    x: clamp4((clientX - editorRect.left) / safeScale, 0, bounds.width),
    y: clamp4((clientY - editorRect.top) / safeScale, 0, bounds.height)
  };
}
function selectionAreaBetween(start, current) {
  return {
    left: Math.min(start.x, current.x),
    top: Math.min(start.y, current.y),
    width: Math.abs(current.x - start.x),
    height: Math.abs(current.y - start.y)
  };
}
function edgeScrollDelta(pointer, start, end, threshold, maxSpeed) {
  if (threshold <= 0 || maxSpeed <= 0) return 0;
  const center = start + (end - start) / 2;
  if (pointer <= center && pointer < start + threshold) {
    const intensity = clamp4((start + threshold - pointer) / threshold, 0, 1);
    return -Math.ceil(maxSpeed * intensity * intensity);
  }
  if (pointer > center && pointer > end - threshold) {
    const intensity = clamp4((pointer - (end - threshold)) / threshold, 0, 1);
    return Math.ceil(maxSpeed * intensity * intensity);
  }
  return 0;
}
function areaAutoScrollDelta(pointer, viewport, threshold = AREA_SCROLL_EDGE, maxSpeed = AREA_SCROLL_MAX_SPEED) {
  return {
    x: edgeScrollDelta(pointer.x, viewport.left, viewport.right, threshold, maxSpeed),
    y: edgeScrollDelta(pointer.y, viewport.top, viewport.bottom, threshold, maxSpeed)
  };
}

// src/components/Canvas/Area.module.css
var Area_default = {
  area: "Area_area"
};

// src/components/Canvas/Area.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Area({ left, top, width, height }) {
  const variables = {
    "--area-left": `${left}px`,
    "--area-top": `${top}px`,
    "--area-width": `${width}px`,
    "--area-height": `${height}px`
  };
  return /* @__PURE__ */ jsx3("div", { className: Area_default.area, style: variables });
}

// src/components/Canvas/ComponentAdjuster.tsx
import {
  useCallback as useCallback4,
  useEffect as useEffect6,
  useLayoutEffect,
  useMemo as useMemo2,
  useRef as useRef5,
  useState as useState5
} from "react";
import { createPortal } from "react-dom";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  RiBringForward,
  RiDeleteBinLine as RiDeleteBinLine2,
  RiDragMove2Line,
  RiEditLine,
  RiFileCopyLine,
  RiLockLine,
  RiLockUnlockLine as RiLockUnlockLine2
} from "@remixicon/react";

// src/utils/calculateComponentPositionAndSize.ts
var MIN_WIDTH = 30;
var MIN_HEIGHT = 1;
function calculateLeftTop(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint } = pointInfo;
  let newCenterPoint = getCenterPoint(curPosition, symmetricPoint);
  let newTopLeftPoint = calculateRotatedPointCoordinate(curPosition, newCenterPoint, -style.rotate);
  let newBottomRightPoint = calculateRotatedPointCoordinate(
    symmetricPoint,
    newCenterPoint,
    -style.rotate
  );
  let newWidth = newBottomRightPoint.x - newTopLeftPoint.x;
  let newHeight = newBottomRightPoint.y - newTopLeftPoint.y;
  if (needLockProportion) {
    if (newWidth / newHeight > proportion) {
      newTopLeftPoint.x += Math.abs(newWidth - newHeight * proportion);
      newWidth = newHeight * proportion;
    } else {
      newTopLeftPoint.y += Math.abs(newHeight - newWidth / proportion);
      newHeight = newWidth / proportion;
    }
    const rotatedTopLeftPoint = calculateRotatedPointCoordinate(
      newTopLeftPoint,
      newCenterPoint,
      style.rotate
    );
    newCenterPoint = getCenterPoint(rotatedTopLeftPoint, symmetricPoint);
    newTopLeftPoint = calculateRotatedPointCoordinate(
      rotatedTopLeftPoint,
      newCenterPoint,
      -style.rotate
    );
    newBottomRightPoint = calculateRotatedPointCoordinate(
      symmetricPoint,
      newCenterPoint,
      -style.rotate
    );
    newWidth = newBottomRightPoint.x - newTopLeftPoint.x;
    newHeight = newBottomRightPoint.y - newTopLeftPoint.y;
  }
  if (newWidth > 0 && newHeight > 0) {
    style.width = Math.max(Math.round(newWidth), MIN_WIDTH);
    style.height = Math.max(Math.round(newHeight), MIN_HEIGHT);
    style.left = Math.round(newTopLeftPoint.x);
    style.top = Math.round(newTopLeftPoint.y);
  }
}
function calculateRightTop(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint } = pointInfo;
  let newCenterPoint = getCenterPoint(curPosition, symmetricPoint);
  let newTopRightPoint = calculateRotatedPointCoordinate(curPosition, newCenterPoint, -style.rotate);
  let newBottomLeftPoint = calculateRotatedPointCoordinate(
    symmetricPoint,
    newCenterPoint,
    -style.rotate
  );
  let newWidth = newTopRightPoint.x - newBottomLeftPoint.x;
  let newHeight = newBottomLeftPoint.y - newTopRightPoint.y;
  if (needLockProportion) {
    if (newWidth / newHeight > proportion) {
      newTopRightPoint.x -= Math.abs(newWidth - newHeight * proportion);
      newWidth = newHeight * proportion;
    } else {
      newTopRightPoint.y += Math.abs(newHeight - newWidth / proportion);
      newHeight = newWidth / proportion;
    }
    const rotatedTopRightPoint = calculateRotatedPointCoordinate(
      newTopRightPoint,
      newCenterPoint,
      style.rotate
    );
    newCenterPoint = getCenterPoint(rotatedTopRightPoint, symmetricPoint);
    newTopRightPoint = calculateRotatedPointCoordinate(
      rotatedTopRightPoint,
      newCenterPoint,
      -style.rotate
    );
    newBottomLeftPoint = calculateRotatedPointCoordinate(
      symmetricPoint,
      newCenterPoint,
      -style.rotate
    );
    newWidth = newTopRightPoint.x - newBottomLeftPoint.x;
    newHeight = newBottomLeftPoint.y - newTopRightPoint.y;
  }
  if (newWidth > 0 && newHeight > 0) {
    style.width = Math.max(Math.round(newWidth), MIN_WIDTH);
    style.height = Math.max(Math.round(newHeight), MIN_HEIGHT);
    style.left = Math.round(newBottomLeftPoint.x);
    style.top = Math.round(newTopRightPoint.y);
  }
}
function calculateRightBottom(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint } = pointInfo;
  let newCenterPoint = getCenterPoint(curPosition, symmetricPoint);
  let newTopLeftPoint = calculateRotatedPointCoordinate(
    symmetricPoint,
    newCenterPoint,
    -style.rotate
  );
  let newBottomRightPoint = calculateRotatedPointCoordinate(
    curPosition,
    newCenterPoint,
    -style.rotate
  );
  let newWidth = newBottomRightPoint.x - newTopLeftPoint.x;
  let newHeight = newBottomRightPoint.y - newTopLeftPoint.y;
  if (needLockProportion) {
    if (newWidth / newHeight > proportion) {
      newBottomRightPoint.x -= Math.abs(newWidth - newHeight * proportion);
      newWidth = newHeight * proportion;
    } else {
      newBottomRightPoint.y -= Math.abs(newHeight - newWidth / proportion);
      newHeight = newWidth / proportion;
    }
    const rotatedBottomRightPoint = calculateRotatedPointCoordinate(
      newBottomRightPoint,
      newCenterPoint,
      style.rotate
    );
    newCenterPoint = getCenterPoint(rotatedBottomRightPoint, symmetricPoint);
    newTopLeftPoint = calculateRotatedPointCoordinate(symmetricPoint, newCenterPoint, -style.rotate);
    newBottomRightPoint = calculateRotatedPointCoordinate(
      rotatedBottomRightPoint,
      newCenterPoint,
      -style.rotate
    );
    newWidth = newBottomRightPoint.x - newTopLeftPoint.x;
    newHeight = newBottomRightPoint.y - newTopLeftPoint.y;
  }
  if (newWidth > 0 && newHeight > 0) {
    style.width = Math.max(Math.round(newWidth), MIN_WIDTH);
    style.height = Math.max(Math.round(newHeight), MIN_HEIGHT);
    style.left = Math.round(newTopLeftPoint.x);
    style.top = Math.round(newTopLeftPoint.y);
  }
}
function calculateLeftBottom(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint } = pointInfo;
  let newCenterPoint = getCenterPoint(curPosition, symmetricPoint);
  let newTopRightPoint = calculateRotatedPointCoordinate(
    symmetricPoint,
    newCenterPoint,
    -style.rotate
  );
  let newBottomLeftPoint = calculateRotatedPointCoordinate(
    curPosition,
    newCenterPoint,
    -style.rotate
  );
  let newWidth = newTopRightPoint.x - newBottomLeftPoint.x;
  let newHeight = newBottomLeftPoint.y - newTopRightPoint.y;
  if (needLockProportion) {
    if (newWidth / newHeight > proportion) {
      newBottomLeftPoint.x += Math.abs(newWidth - newHeight * proportion);
      newWidth = newHeight * proportion;
    } else {
      newBottomLeftPoint.y -= Math.abs(newHeight - newWidth / proportion);
      newHeight = newWidth / proportion;
    }
    const rotatedBottomLeftPoint = calculateRotatedPointCoordinate(
      newBottomLeftPoint,
      newCenterPoint,
      style.rotate
    );
    newCenterPoint = getCenterPoint(rotatedBottomLeftPoint, symmetricPoint);
    newTopRightPoint = calculateRotatedPointCoordinate(
      symmetricPoint,
      newCenterPoint,
      -style.rotate
    );
    newBottomLeftPoint = calculateRotatedPointCoordinate(
      rotatedBottomLeftPoint,
      newCenterPoint,
      -style.rotate
    );
    newWidth = newTopRightPoint.x - newBottomLeftPoint.x;
    newHeight = newBottomLeftPoint.y - newTopRightPoint.y;
  }
  if (newWidth > 0 && newHeight > 0) {
    style.width = Math.max(Math.round(newWidth), MIN_WIDTH);
    style.height = Math.max(Math.round(newHeight), MIN_HEIGHT);
    style.left = Math.round(newBottomLeftPoint.x);
    style.top = Math.round(newTopRightPoint.y);
  }
}
function calculateTop(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint, curPoint } = pointInfo;
  const rotatedCurPosition = calculateRotatedPointCoordinate(curPosition, curPoint, -style.rotate);
  const rotatedTopMiddlePoint = calculateRotatedPointCoordinate(
    { x: curPoint.x, y: rotatedCurPosition.y },
    curPoint,
    style.rotate
  );
  const newHeight = Math.sqrt(
    (rotatedTopMiddlePoint.x - symmetricPoint.x) ** 2 + (rotatedTopMiddlePoint.y - symmetricPoint.y) ** 2
  );
  const newCenter = {
    x: rotatedTopMiddlePoint.x - (rotatedTopMiddlePoint.x - symmetricPoint.x) / 2,
    y: rotatedTopMiddlePoint.y + (symmetricPoint.y - rotatedTopMiddlePoint.y) / 2
  };
  let width = style.width;
  if (needLockProportion) width = newHeight * proportion;
  style.width = Math.max(width, MIN_WIDTH);
  style.height = Math.max(Math.round(newHeight), MIN_HEIGHT);
  style.top = Math.round(newCenter.y - newHeight / 2);
  style.left = Math.round(newCenter.x - style.width / 2);
}
function calculateRight(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint, curPoint } = pointInfo;
  const rotatedCurPosition = calculateRotatedPointCoordinate(curPosition, curPoint, -style.rotate);
  const rotatedRightMiddlePoint = calculateRotatedPointCoordinate(
    { x: rotatedCurPosition.x, y: curPoint.y },
    curPoint,
    style.rotate
  );
  const newWidth = Math.sqrt(
    (rotatedRightMiddlePoint.x - symmetricPoint.x) ** 2 + (rotatedRightMiddlePoint.y - symmetricPoint.y) ** 2
  );
  const newCenter = {
    x: rotatedRightMiddlePoint.x - (rotatedRightMiddlePoint.x - symmetricPoint.x) / 2,
    y: rotatedRightMiddlePoint.y + (symmetricPoint.y - rotatedRightMiddlePoint.y) / 2
  };
  let height = style.height;
  if (needLockProportion) height = newWidth / proportion;
  style.height = Math.max(height, MIN_HEIGHT);
  style.width = Math.max(Math.round(newWidth), MIN_WIDTH);
  style.top = Math.round(newCenter.y - style.height / 2);
  style.left = Math.round(newCenter.x - newWidth / 2);
}
function calculateBottom(style, curPosition, proportion, needLockProportion, pointInfo, minHeight = 0) {
  const { symmetricPoint, curPoint } = pointInfo;
  const rotatedCurPosition = calculateRotatedPointCoordinate(curPosition, curPoint, -style.rotate);
  const rotatedBottomMiddlePoint = calculateRotatedPointCoordinate(
    { x: curPoint.x, y: rotatedCurPosition.y },
    curPoint,
    style.rotate
  );
  const newHeight = Math.sqrt(
    (rotatedBottomMiddlePoint.x - symmetricPoint.x) ** 2 + (rotatedBottomMiddlePoint.y - symmetricPoint.y) ** 2
  );
  const newCenter = {
    x: rotatedBottomMiddlePoint.x - (rotatedBottomMiddlePoint.x - symmetricPoint.x) / 2,
    y: rotatedBottomMiddlePoint.y + (symmetricPoint.y - rotatedBottomMiddlePoint.y) / 2
  };
  let width = style.width;
  if (needLockProportion) width = newHeight * proportion;
  style.width = Math.max(width, MIN_WIDTH);
  style.height = Math.max(Math.round(newHeight), MIN_HEIGHT, minHeight);
  style.top = Math.round(newCenter.y - newHeight / 2);
  style.left = Math.round(newCenter.x - style.width / 2);
}
function calculateLeft(style, curPosition, proportion, needLockProportion, pointInfo) {
  const { symmetricPoint, curPoint } = pointInfo;
  const rotatedCurPosition = calculateRotatedPointCoordinate(curPosition, curPoint, -style.rotate);
  const rotatedLeftMiddlePoint = calculateRotatedPointCoordinate(
    { x: rotatedCurPosition.x, y: curPoint.y },
    curPoint,
    style.rotate
  );
  const newWidth = Math.sqrt(
    (rotatedLeftMiddlePoint.x - symmetricPoint.x) ** 2 + (rotatedLeftMiddlePoint.y - symmetricPoint.y) ** 2
  );
  const newCenter = {
    x: rotatedLeftMiddlePoint.x - (rotatedLeftMiddlePoint.x - symmetricPoint.x) / 2,
    y: rotatedLeftMiddlePoint.y + (symmetricPoint.y - rotatedLeftMiddlePoint.y) / 2
  };
  let height = style.height;
  if (needLockProportion) height = newWidth / proportion;
  style.height = Math.max(height, MIN_HEIGHT);
  style.width = Math.max(Math.round(newWidth), MIN_WIDTH);
  style.top = Math.round(newCenter.y - style.height / 2);
  style.left = Math.round(newCenter.x - newWidth / 2);
}
var funcs = {
  lt: calculateLeftTop,
  t: calculateTop,
  rt: calculateRightTop,
  r: calculateRight,
  rb: calculateRightBottom,
  b: calculateBottom,
  lb: calculateLeftBottom,
  l: calculateLeft
};
function calculateComponentPositionAndSize(name, style, curPosition, proportion, needLockProportion, pointInfo, minHeight = 0) {
  funcs[name](style, curPosition, proportion, needLockProportion, pointInfo, minHeight);
}

// src/components/Theme/Theme.module.css
var Theme_default = {
  theme: "Theme_theme"
};

// src/components/Theme/index.ts
var ptdThemeClass = Theme_default.theme;

// src/components/ContentEditor/ContentEditor.tsx
import {
  useCallback as useCallback3,
  useEffect as useEffect5,
  useInsertionEffect,
  useRef as useRef4,
  useState as useState4
} from "react";
import {
  componentStyleToCssVariables,
  injectStylesheet,
  sanitizeRichTextHtml
} from "@ptd/components";
import { Color } from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { FontSize, LineHeight, TextStyle } from "@tiptap/extension-text-style";
import { BubbleMenu } from "@tiptap/react/menus";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  RiAlignCenter,
  RiAlignJustify,
  RiAlignLeft,
  RiAlignRight,
  RiBold,
  RiCloseLine as RiCloseLine2,
  RiDoubleQuotesL,
  RiEraserLine,
  RiItalic,
  RiLink,
  RiListOrdered,
  RiListUnordered,
  RiStrikethrough,
  RiUnderline
} from "@remixicon/react";

// src/config/typography.ts
var CJK_FONT_FAMILY_OPTIONS = [
  ["'Noto Serif SC', 'Noto Serif CJK SC', 'Source Han Serif SC', serif", "Noto Serif SC"],
  ["'Sarasa UI SC', 'Sarasa Gothic SC', 'Microsoft YaHei UI', sans-serif", "Sarasa UI SC"],
  ["'Microsoft YaHei UI', 'Microsoft YaHei', 'PingFang SC', sans-serif", "\u5FAE\u8F6F\u96C5\u9ED1"],
  ["DengXian, 'Microsoft YaHei UI', 'PingFang SC', sans-serif", "\u7B49\u7EBF"],
  ["SimHei, 'STHeiti', 'Heiti SC', sans-serif", "\u9ED1\u4F53"],
  ["SimSun, 'STSong', 'Songti SC', serif", "\u5B8B\u4F53"],
  ["KaiTi, 'STKaiti', 'Kaiti SC', serif", "\u6977\u4F53"],
  ["'FangSong_GB2312', FangSong, 'STFangsong', 'Fangsong SC', serif", "\u4EFF\u5B8B GB2312"]
];
var LATIN_FONT_FAMILY_OPTIONS = [
  ["", "\u8DDF\u968F\u4E2D\u6587\u5B57\u4F53"],
  ["'Outfit', 'Outfit Variable'", "Outfit"],
  ["Arial, 'Helvetica Neue'", "Arial"],
  ["'Times New Roman', Times", "Times New Roman"]
];
var DEFAULT_CJK_FONT_FAMILY = CJK_FONT_FAMILY_OPTIONS[0][0];
function composeFontFamily(cjk, latin = "") {
  return latin ? `${latin}, ${cjk}` : cjk;
}
function resolveFontFamily(fontFamily) {
  if (!fontFamily.trim()) {
    return { cjk: DEFAULT_CJK_FONT_FAMILY, latin: "", recognized: true };
  }
  for (const [cjk] of CJK_FONT_FAMILY_OPTIONS) {
    for (const [latin] of LATIN_FONT_FAMILY_OPTIONS) {
      if (sameFontFamily(fontFamily, composeFontFamily(cjk, latin))) {
        return { cjk, latin, recognized: true };
      }
    }
  }
  return { cjk: fontFamily, latin: "", recognized: false };
}
function sameFontFamily(left, right) {
  return normalizeFontFamily(left) === normalizeFontFamily(right);
}
function normalizeFontFamily(value) {
  return value.replace(/["']/g, "").split(",").map((family) => family.trim().toLowerCase()).filter(Boolean).join(",");
}

// src/components/ContentEditor/ContentEditor.module.css
var ContentEditor_default = {
  editorFrame: "ContentEditor_editorFrame",
  plainEditor: "ContentEditor_plainEditor",
  richContent: "ContentEditor_richContent",
  richEditable: "ContentEditor_richEditable",
  editingHint: "ContentEditor_editingHint",
  richToolbar: "ContentEditor_richToolbar",
  colorWell: "ContentEditor_colorWell",
  toolbarDivider: "ContentEditor_toolbarDivider",
  highlightMark: "ContentEditor_highlightMark",
  linkEditor: "ContentEditor_linkEditor"
};

// src/components/ContentEditor/ContentEditor.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var FONT_SIZES = [
  "8pt",
  "9pt",
  "10pt",
  "10.5pt",
  "11pt",
  "12pt",
  "14pt",
  "16pt",
  "18pt",
  "20pt",
  "24pt",
  "28pt",
  "32pt",
  "36pt",
  "48pt",
  "72pt"
];
var LINE_HEIGHTS = ["1", "1.25", "1.5", "1.75", "2"];
var RICH_TOOLBAR_OPTIONS = {
  strategy: "fixed",
  placement: "top",
  offset: 7,
  shift: { padding: 8 }
};
function isDirectlyEditableComponent(schema) {
  return schema.component === "RoySimpleText" || schema.component === "RoyText";
}
function ContentEditor({ schema }) {
  useInsertionEffect(() => {
    injectStylesheet();
  }, []);
  if (schema.component === "RoySimpleText") return /* @__PURE__ */ jsx4(PlainTextEditor, { schema });
  if (schema.component === "RoyText") return /* @__PURE__ */ jsx4(RichTextEditor, { schema });
  return null;
}
function PlainTextEditor({ schema }) {
  const store = useEditorStore();
  const frameRef = useRef4(null);
  const editableRef = useRef4(null);
  const draftRef = useRef4(textValue(schema.propValue));
  const composingRef = useRef4(false);
  const settledRef = useRef4(false);
  const commit = useCallback3(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    store.commitContentEditing(schema.id, draftRef.current);
  }, [schema.id, store]);
  const cancel = useCallback3(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    store.cancelContentEditing(schema.id);
  }, [schema.id, store]);
  useOutsideCommit(frameRef, commit);
  useEffect5(() => {
    const element = editableRef.current;
    if (!element) return;
    element.textContent = draftRef.current;
    element.focus({ preventScroll: true });
    placeCaretAtEnd(element);
  }, []);
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      ref: frameRef,
      className: `${ContentEditor_default.editorFrame} ptd-simple-text`,
      style: contentEditorStyle(schema),
      "data-ptd-content-editor": true,
      "data-ptd-editor-interactive": true,
      children: [
        /* @__PURE__ */ jsx4(
          "div",
          {
            ref: editableRef,
            className: `${ContentEditor_default.plainEditor} ptd-simple-text__inner`,
            contentEditable: "plaintext-only",
            suppressContentEditableWarning: true,
            role: "textbox",
            "aria-label": "\u6587\u672C\u5185\u5BB9",
            "aria-multiline": "true",
            spellCheck: true,
            onInput: (event) => {
              draftRef.current = event.currentTarget.innerText.replace(/\r\n/g, "\n");
            },
            onCompositionStart: () => {
              composingRef.current = true;
            },
            onCompositionEnd: () => {
              composingRef.current = false;
            },
            onKeyDown: (event) => {
              event.stopPropagation();
              if (event.key === "Escape" && !composingRef.current) {
                event.preventDefault();
                cancel();
              } else if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                commit();
              }
            },
            onPointerDown: (event) => event.stopPropagation(),
            onDoubleClick: (event) => event.stopPropagation()
          }
        ),
        /* @__PURE__ */ jsx4(EditingHint, { label: "\u6587\u672C\u7F16\u8F91" })
      ]
    }
  );
}
function RichTextEditor({ schema }) {
  const store = useEditorStore();
  const rootRef = useRef4(null);
  const settledRef = useRef4(false);
  const composingRef = useRef4(false);
  const [initialContent] = useState4(
    () => sanitizeRichTextHtml(textValue(schema.propValue)) || "<p></p>"
  );
  const [revision, setRevision] = useState4(0);
  const [linkEditorOpen, setLinkEditorOpen] = useState4(false);
  const [linkDraft, setLinkDraft] = useState4("https://");
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        link: { openOnClick: false }
      }),
      TextStyle,
      FontSize,
      LineHeight,
      FontFamily,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] })
    ],
    content: initialContent,
    autofocus: "end",
    editorProps: {
      attributes: {
        "aria-label": "\u5BCC\u6587\u672C\u5185\u5BB9",
        class: `${ContentEditor_default.richEditable} ptd-text__inner`,
        spellcheck: "true"
      }
    },
    onUpdate: () => setRevision((value) => value + 1),
    onSelectionUpdate: () => setRevision((value) => value + 1)
  });
  const commit = useCallback3(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    const html = editor?.getHTML() ?? initialContent;
    store.commitContentEditing(schema.id, sanitizeRichTextHtml(html) || "<p></p>");
  }, [editor, initialContent, schema.id, store]);
  const cancel = useCallback3(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    store.cancelContentEditing(schema.id);
  }, [schema.id, store]);
  useEffect5(() => {
    if (!editor) return;
    const frame = requestAnimationFrame(() => {
      if (!editor.isDestroyed) {
        editor.chain().focus("end", { scrollIntoView: false }).run();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [editor]);
  useOutsideCommit(rootRef, commit);
  if (!editor) return /* @__PURE__ */ jsx4("div", { className: ContentEditor_default.editorFrame, "data-ptd-content-editor": true });
  void revision;
  const inlineFontFamily = textValue(editor.getAttributes("textStyle").fontFamily);
  const inheritedFontFamily = textValue(schema.style.fontFamily) || DEFAULT_CJK_FONT_FAMILY;
  const fontSelection = resolveFontFamily(inlineFontFamily || inheritedFontFamily);
  const applyLink = () => {
    const href = safeLink(linkDraft);
    if (!href) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkEditorOpen(false);
  };
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      ref: rootRef,
      className: `${ContentEditor_default.editorFrame} ptd-text`,
      style: contentEditorStyle(schema),
      "data-ptd-content-editor": true,
      "data-ptd-editor-interactive": true,
      onPointerDown: (event) => {
        event.stopPropagation();
        if (event.button === 0 && event.target instanceof Element && !event.target.closest('[contenteditable="true"]')) {
          editor.chain().focus("end", { scrollIntoView: false }).run();
        }
      },
      onDoubleClick: (event) => event.stopPropagation(),
      onCompositionStart: () => {
        composingRef.current = true;
      },
      onCompositionEnd: () => {
        composingRef.current = false;
      },
      onKeyDownCapture: (event) => {
        event.stopPropagation();
        if (event.key === "Escape" && !composingRef.current && !linkEditorOpen) {
          event.preventDefault();
          cancel();
        }
      },
      children: [
        /* @__PURE__ */ jsx4(EditorContent, { editor, className: ContentEditor_default.richContent }),
        /* @__PURE__ */ jsxs2(
          BubbleMenu,
          {
            editor,
            className: `${ContentEditor_default.richToolbar} ${ptdThemeClass}`,
            "data-ptd-rich-toolbar": true,
            "data-ptd-editor-interactive": true,
            appendTo: appendRichToolbarToBody,
            updateDelay: 40,
            resizeDelay: 80,
            options: RICH_TOOLBAR_OPTIONS,
            shouldShow: ({ from, to }) => from !== to && editor.isEditable,
            onPointerDown: (event) => event.stopPropagation(),
            onClick: (event) => event.stopPropagation(),
            onDoubleClick: (event) => event.stopPropagation(),
            children: [
              /* @__PURE__ */ jsxs2(
                "select",
                {
                  "aria-label": "\u6BB5\u843D\u6837\u5F0F",
                  value: activeBlock(editor),
                  onChange: (event) => {
                    const value = event.target.value;
                    if (value === "paragraph") editor.chain().focus().setParagraph().run();
                    else
                      editor.chain().focus().toggleHeading({ level: Number(value.slice(1)) }).run();
                  },
                  children: [
                    /* @__PURE__ */ jsx4("option", { value: "paragraph", children: "\u6B63\u6587" }),
                    /* @__PURE__ */ jsx4("option", { value: "h1", children: "\u6807\u9898 1" }),
                    /* @__PURE__ */ jsx4("option", { value: "h2", children: "\u6807\u9898 2" }),
                    /* @__PURE__ */ jsx4("option", { value: "h3", children: "\u6807\u9898 3" }),
                    /* @__PURE__ */ jsx4("option", { value: "h4", children: "\u6807\u9898 4" })
                  ]
                }
              ),
              /* @__PURE__ */ jsx4(ToolbarDivider, {}),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u7C97\u4F53",
                  active: editor.isActive("bold"),
                  onRun: () => editor.chain().focus().toggleBold().run(),
                  children: /* @__PURE__ */ jsx4(RiBold, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u659C\u4F53",
                  active: editor.isActive("italic"),
                  onRun: () => editor.chain().focus().toggleItalic().run(),
                  children: /* @__PURE__ */ jsx4(RiItalic, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u4E0B\u5212\u7EBF",
                  active: editor.isActive("underline"),
                  onRun: () => editor.chain().focus().toggleUnderline().run(),
                  children: /* @__PURE__ */ jsx4(RiUnderline, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u5220\u9664\u7EBF",
                  active: editor.isActive("strike"),
                  onRun: () => editor.chain().focus().toggleStrike().run(),
                  children: /* @__PURE__ */ jsx4(RiStrikethrough, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u5F15\u7528",
                  active: editor.isActive("blockquote"),
                  onRun: () => editor.chain().focus().toggleBlockquote().run(),
                  children: /* @__PURE__ */ jsx4(RiDoubleQuotesL, {})
                }
              ),
              /* @__PURE__ */ jsx4(ToolbarDivider, {}),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u5DE6\u5BF9\u9F50",
                  active: editor.isActive({ textAlign: "left" }),
                  onRun: () => editor.chain().focus().setTextAlign("left").run(),
                  children: /* @__PURE__ */ jsx4(RiAlignLeft, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u5C45\u4E2D",
                  active: editor.isActive({ textAlign: "center" }),
                  onRun: () => editor.chain().focus().setTextAlign("center").run(),
                  children: /* @__PURE__ */ jsx4(RiAlignCenter, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u53F3\u5BF9\u9F50",
                  active: editor.isActive({ textAlign: "right" }),
                  onRun: () => editor.chain().focus().setTextAlign("right").run(),
                  children: /* @__PURE__ */ jsx4(RiAlignRight, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u4E24\u7AEF\u5BF9\u9F50",
                  active: editor.isActive({ textAlign: "justify" }),
                  onRun: () => editor.chain().focus().setTextAlign("justify").run(),
                  children: /* @__PURE__ */ jsx4(RiAlignJustify, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u65E0\u5E8F\u5217\u8868",
                  active: editor.isActive("bulletList"),
                  onRun: () => editor.chain().focus().toggleBulletList().run(),
                  children: /* @__PURE__ */ jsx4(RiListUnordered, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u6709\u5E8F\u5217\u8868",
                  active: editor.isActive("orderedList"),
                  onRun: () => editor.chain().focus().toggleOrderedList().run(),
                  children: /* @__PURE__ */ jsx4(RiListOrdered, {})
                }
              ),
              /* @__PURE__ */ jsx4(ToolbarDivider, {}),
              /* @__PURE__ */ jsxs2(
                "select",
                {
                  "aria-label": "\u5B57\u53F7",
                  value: editor.getAttributes("textStyle").fontSize ?? "",
                  onChange: (event) => editor.chain().focus().setFontSize(event.target.value).run(),
                  children: [
                    /* @__PURE__ */ jsx4("option", { value: "", children: "\u5B57\u53F7" }),
                    FONT_SIZES.map((size) => /* @__PURE__ */ jsx4("option", { value: size, children: size }, size))
                  ]
                }
              ),
              /* @__PURE__ */ jsxs2(
                "select",
                {
                  "aria-label": "\u4E2D\u6587\u5B57\u4F53",
                  title: "\u4E2D\u6587\u5B57\u4F53\u6548\u679C\u53D6\u51B3\u4E8E\u672C\u673A\u5DF2\u5B89\u88C5\u5B57\u4F53",
                  value: fontSelection.cjk,
                  onChange: (event) => editor.chain().focus().setFontFamily(composeFontFamily(event.target.value, fontSelection.latin)).run(),
                  children: [
                    !fontSelection.recognized && /* @__PURE__ */ jsx4("option", { value: fontSelection.cjk, children: primaryFontName(fontSelection.cjk) }),
                    CJK_FONT_FAMILY_OPTIONS.map(([value, label]) => /* @__PURE__ */ jsx4("option", { value, children: label }, value))
                  ]
                }
              ),
              /* @__PURE__ */ jsx4(
                "select",
                {
                  "aria-label": "\u897F\u6587\u5B57\u4F53",
                  title: "\u897F\u6587\u5B57\u4F53\u6548\u679C\u53D6\u51B3\u4E8E\u672C\u673A\u5DF2\u5B89\u88C5\u5B57\u4F53",
                  value: fontSelection.latin,
                  onChange: (event) => editor.chain().focus().setFontFamily(composeFontFamily(fontSelection.cjk, event.target.value)).run(),
                  children: LATIN_FONT_FAMILY_OPTIONS.map(([value, label]) => /* @__PURE__ */ jsx4("option", { value, children: label }, value || "follow-cjk"))
                }
              ),
              /* @__PURE__ */ jsxs2(
                "select",
                {
                  "aria-label": "\u884C\u9AD8",
                  value: editor.getAttributes("textStyle").lineHeight ?? "",
                  onChange: (event) => editor.chain().focus().setLineHeight(event.target.value).run(),
                  children: [
                    /* @__PURE__ */ jsx4("option", { value: "", children: "\u884C\u9AD8" }),
                    LINE_HEIGHTS.map((height) => /* @__PURE__ */ jsx4("option", { value: height, children: height }, height))
                  ]
                }
              ),
              /* @__PURE__ */ jsxs2("label", { className: ContentEditor_default.colorWell, title: "\u6587\u5B57\u989C\u8272", children: [
                /* @__PURE__ */ jsx4("span", { children: "\u5B57" }),
                /* @__PURE__ */ jsx4(
                  "input",
                  {
                    type: "color",
                    "aria-label": "\u6587\u5B57\u989C\u8272",
                    value: editor.getAttributes("textStyle").color ?? "#1d2735",
                    onChange: (event) => editor.chain().focus().setColor(event.target.value).run()
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs2("label", { className: ContentEditor_default.colorWell, title: "\u9AD8\u4EAE\u989C\u8272", children: [
                /* @__PURE__ */ jsx4("span", { className: ContentEditor_default.highlightMark, children: "\u5B57" }),
                /* @__PURE__ */ jsx4(
                  "input",
                  {
                    type: "color",
                    "aria-label": "\u9AD8\u4EAE\u989C\u8272",
                    value: editor.getAttributes("highlight").color ?? "#fff1a8",
                    onChange: (event) => editor.chain().focus().setHighlight({ color: event.target.value }).run()
                  }
                )
              ] }),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: editor.isActive("link") ? "\u79FB\u9664\u94FE\u63A5" : "\u6DFB\u52A0\u94FE\u63A5",
                  active: editor.isActive("link"),
                  onRun: () => {
                    if (editor.isActive("link")) editor.chain().focus().unsetLink().run();
                    else {
                      setLinkDraft("https://");
                      setLinkEditorOpen(true);
                    }
                  },
                  children: /* @__PURE__ */ jsx4(RiLink, {})
                }
              ),
              /* @__PURE__ */ jsx4(
                ToolbarButton,
                {
                  label: "\u6E05\u9664\u683C\u5F0F",
                  onRun: () => editor.chain().focus().unsetAllMarks().clearNodes().run(),
                  children: /* @__PURE__ */ jsx4(RiEraserLine, {})
                }
              ),
              linkEditorOpen && /* @__PURE__ */ jsxs2("span", { className: ContentEditor_default.linkEditor, onMouseDown: (event) => event.stopPropagation(), children: [
                /* @__PURE__ */ jsx4(
                  "input",
                  {
                    autoFocus: true,
                    type: "url",
                    "aria-label": "\u94FE\u63A5\u5730\u5740",
                    value: linkDraft,
                    onChange: (event) => setLinkDraft(event.target.value),
                    onKeyDown: (event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        applyLink();
                      } else if (event.key === "Escape") {
                        event.preventDefault();
                        setLinkEditorOpen(false);
                        editor.commands.focus();
                      }
                    }
                  }
                ),
                /* @__PURE__ */ jsx4(
                  "button",
                  {
                    type: "button",
                    "aria-label": "\u5173\u95ED\u94FE\u63A5\u7F16\u8F91",
                    onClick: () => setLinkEditorOpen(false),
                    children: /* @__PURE__ */ jsx4(RiCloseLine2, {})
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx4(EditingHint, { label: "\u5BCC\u6587\u672C\u7F16\u8F91" })
      ]
    }
  );
}
function ToolbarButton({
  label,
  active = false,
  onRun,
  children
}) {
  return /* @__PURE__ */ jsx4(
    "button",
    {
      type: "button",
      "aria-label": label,
      title: label,
      "data-active": active || void 0,
      onPointerDown: (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        onRun();
      },
      onClick: (event) => {
        event.stopPropagation();
        if (event.detail === 0) onRun();
      },
      children
    }
  );
}
function ToolbarDivider() {
  return /* @__PURE__ */ jsx4("span", { className: ContentEditor_default.toolbarDivider, "aria-hidden": "true" });
}
function appendRichToolbarToBody() {
  return document.body;
}
function EditingHint({ label }) {
  return /* @__PURE__ */ jsxs2("span", { className: ContentEditor_default.editingHint, children: [
    label,
    /* @__PURE__ */ jsx4("kbd", { children: "Esc" }),
    "\u53D6\u6D88"
  ] });
}
function useOutsideCommit(rootRef, commit) {
  useEffect5(() => {
    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-ptd-editor-interactive]")) return;
      commit();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [commit, rootRef]);
}
function placeCaretAtEnd(element) {
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}
function activeBlock(editor) {
  for (const level of [1, 2, 3, 4])
    if (editor.isActive("heading", { level })) return `h${level}`;
  return "paragraph";
}
function safeLink(value) {
  const href = value.trim();
  return /^(?:https?:|mailto:)/i.test(href) ? href : null;
}
function primaryFontName(fontFamily) {
  return (fontFamily.split(",")[0]?.trim() || fontFamily).replace(/^["']|["']$/g, "");
}
function textValue(value) {
  return typeof value === "string" ? value : "";
}
function contentEditorStyle(schema) {
  return componentStyleToCssVariables(schema.style);
}

// src/components/Canvas/ComponentAdjuster.module.css
var ComponentAdjuster_default = {
  adjuster: "ComponentAdjuster_adjuster",
  active: "ComponentAdjuster_active",
  editing: "ComponentAdjuster_editing",
  locked: "ComponentAdjuster_locked",
  outOfBoundsMarker: "ComponentAdjuster_outOfBoundsMarker",
  rotate: "ComponentAdjuster_rotate",
  lock: "ComponentAdjuster_lock",
  move: "ComponentAdjuster_move",
  quickBar: "ComponentAdjuster_quickBar",
  quickLabel: "ComponentAdjuster_quickLabel",
  quickAction: "ComponentAdjuster_quickAction",
  tooltip: "ComponentAdjuster_tooltip",
  tooltipArrow: "ComponentAdjuster_tooltipArrow",
  point: "ComponentAdjuster_point",
  container: "ComponentAdjuster_container"
};

// src/components/Canvas/ComponentAdjuster.tsx
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var INITIAL_ANGLE = {
  lt: 0,
  t: 45,
  rt: 90,
  r: 135,
  rb: 180,
  b: 225,
  lb: 270,
  l: 315
};
function getCursor(point, rotate) {
  const index = Math.round(mod360(INITIAL_ANGLE[point] + rotate) / 45) % 8;
  return `${["nw", "n", "ne", "e", "se", "s", "sw", "w"][index]}-resize`;
}
function getPointList(component) {
  if (["RoySimpleTable", "RoyComplexTable"].includes(component)) return ["b"];
  if (component === "RoyLine") return ["r", "l"];
  return ["lt", "t", "rt", "r", "rb", "b", "lb", "l"];
}
function getPointVariables(point, width, height, cursor) {
  const vertical = point.length === 1 && (point === "t" || point === "b");
  const horizontal = point.length === 1 && (point === "l" || point === "r");
  return {
    "--point-left": `${vertical ? width / 2 : point.includes("r") ? width : 0}px`,
    "--point-top": `${horizontal ? Math.floor(height / 2) : point.includes("b") ? height : 0}px`,
    "--point-cursor": cursor
  };
}
function QuickAction({
  label,
  disabled = false,
  danger = false,
  onClick,
  onMouseDown,
  children
}) {
  return /* @__PURE__ */ jsxs3(Tooltip.Root, { children: [
    /* @__PURE__ */ jsx5(Tooltip.Trigger, { asChild: true, children: /* @__PURE__ */ jsx5(
      "button",
      {
        type: "button",
        className: ComponentAdjuster_default.quickAction,
        "data-danger": danger || void 0,
        "aria-label": label,
        disabled,
        onClick,
        onMouseDown: (event) => {
          event.stopPropagation();
          onMouseDown?.(event);
        },
        children
      }
    ) }),
    /* @__PURE__ */ jsx5(Tooltip.Portal, { children: /* @__PURE__ */ jsxs3(Tooltip.Content, { className: `${ComponentAdjuster_default.tooltip} ${ptdThemeClass}`, sideOffset: 7, children: [
      label,
      /* @__PURE__ */ jsx5(Tooltip.Arrow, { className: ComponentAdjuster_default.tooltipArrow })
    ] }) })
  ] });
}
function ComponentAdjuster({
  schema,
  isActive,
  isEditing,
  outOfBounds,
  editorRef,
  scale,
  onMove,
  onMoveEnd,
  children
}) {
  const store = useEditorStore();
  const adjusterRef = useRef5(null);
  const quickBarRef = useRef5(null);
  const cleanupRef = useRef5(null);
  const [quickBarPosition, setQuickBarPosition] = useState5({
    left: 0,
    top: 0,
    componentId: null,
    ready: false
  });
  const style = schema.style;
  const isLocked = schema.isLock ?? false;
  const hasLockedSelection = store.selectedComponents.value.some((component) => component.isLock);
  const showHandles = isActive && !isEditing && !isLocked && !hasLockedSelection;
  const showQuickBar = isActive && !isEditing && store.selectedIds.value.length === 1;
  const revealRequested = store.componentToReveal.value === schema.id;
  const pointList = useMemo2(() => getPointList(schema.component), [schema.component]);
  const cursors = useMemo2(
    () => Object.fromEntries(
      pointList.map((point) => [point, getCursor(point, style.rotate ?? 0)])
    ),
    [pointList, style.rotate]
  );
  useEffect6(
    () => () => {
      cleanupRef.current?.();
    },
    []
  );
  useLayoutEffect(() => {
    if (!revealRequested) return;
    const frame = requestAnimationFrame(() => {
      adjusterRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
      store.finishComponentReveal(schema.id);
    });
    return () => cancelAnimationFrame(frame);
  }, [revealRequested, schema.id, store]);
  useLayoutEffect(() => {
    if (!showQuickBar) return;
    const adjuster = adjusterRef.current;
    const quickBar = quickBarRef.current;
    const editor = editorRef.current;
    const viewport = editor?.closest('[data-ptd-region="canvas-viewport"]');
    if (!adjuster || !quickBar || !viewport) return;
    let frame = 0;
    const updatePosition = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const componentRect = adjuster.getBoundingClientRect();
        const viewportRect = viewport.getBoundingClientRect();
        const quickBarRect = quickBar.getBoundingClientRect();
        const margin = 6;
        const gap = 4;
        const minLeft = viewportRect.left + margin;
        const maxLeft = Math.max(minLeft, viewportRect.right - margin - quickBarRect.width);
        const left = Math.min(Math.max(componentRect.left, minLeft), maxLeft);
        const above = componentRect.top - quickBarRect.height - gap;
        const below = componentRect.bottom + gap;
        const top = above >= viewportRect.top + margin ? above : Math.min(below, viewportRect.bottom - margin - quickBarRect.height);
        setQuickBarPosition(
          (current) => current.left === left && current.top === top && current.componentId === schema.id && current.ready ? current : {
            left,
            top: Math.max(viewportRect.top + margin, top),
            componentId: schema.id,
            ready: true
          }
        );
      });
    };
    updatePosition();
    viewport.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition);
    const observer = new ResizeObserver(updatePosition);
    observer.observe(viewport);
    observer.observe(adjuster);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
      observer.disconnect();
    };
  }, [
    editorRef,
    scale,
    schema.id,
    showQuickBar,
    style.height,
    style.left,
    style.rotate,
    style.top,
    style.width
  ]);
  const startSession = useCallback4(
    (move, finish) => {
      cleanupRef.current?.();
      let ended = false;
      const end = () => {
        if (ended) return;
        ended = true;
        cleanup();
        finish();
      };
      const cleanup = () => {
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", end);
        window.removeEventListener("blur", end);
        cleanupRef.current = null;
      };
      cleanupRef.current = end;
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", end);
      window.addEventListener("blur", end);
    },
    []
  );
  const handleMouseDownOnShape = useCallback4(
    (event) => {
      if (event.button !== 0) return;
      if (event.target instanceof Element && event.target.closest("[data-ptd-editor-interactive]"))
        return;
      event.stopPropagation();
      store.selectComponent(schema.id, event.shiftKey || event.metaKey || event.ctrlKey);
    },
    [schema.id, store]
  );
  const handleRotate = useCallback4(
    (event) => {
      if (isLocked) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = adjusterRef.current?.getBoundingClientRect();
      if (!rect) return;
      store.beginGesture();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const before = Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180 / Math.PI;
      const initial = style.rotate ?? 0;
      startSession(
        (nextEvent) => {
          const after = Math.atan2(nextEvent.clientY - centerY, nextEvent.clientX - centerX) * 180 / Math.PI;
          store.transformComponent(schema.id, { rotate: initial + after - before }, true);
        },
        () => store.commitGesture()
      );
    },
    [isLocked, schema.id, startSession, store, style.rotate]
  );
  const handleResize = useCallback4(
    (point, event) => {
      if (isLocked) return;
      event.preventDefault();
      event.stopPropagation();
      const adjuster = adjusterRef.current;
      const editor = editorRef.current;
      if (!adjuster || !editor) return;
      store.beginGesture();
      const resizeStyle = {
        width: Number.isFinite(style.width) ? style.width : adjuster.clientWidth,
        height: Number.isFinite(style.height) ? style.height : adjuster.clientHeight,
        left: typeof style.left === "number" ? style.left : 0,
        top: typeof style.top === "number" ? style.top : 0,
        rotate: style.rotate ?? 0
      };
      const proportion = resizeStyle.width / resizeStyle.height;
      const center = {
        x: resizeStyle.left + resizeStyle.width / 2,
        y: resizeStyle.top + resizeStyle.height / 2
      };
      const editorRect = editor.getBoundingClientRect();
      const pointRect = event.currentTarget.getBoundingClientRect();
      const curPoint = {
        x: (pointRect.left - editorRect.left + pointRect.width / 2) / scale,
        y: (pointRect.top - editorRect.top + pointRect.height / 2) / scale
      };
      const symmetricPoint = {
        x: center.x - (curPoint.x - center.x),
        y: center.y - (curPoint.y - center.y)
      };
      startSession(
        (nextEvent) => {
          calculateComponentPositionAndSize(
            point,
            resizeStyle,
            {
              x: (nextEvent.clientX - editorRect.left) / scale,
              y: (nextEvent.clientY - editorRect.top) / scale
            },
            proportion,
            schema.component === "RoyGroup",
            { center, curPoint, symmetricPoint },
            ["RoySimpleTable", "RoyComplexTable"].includes(schema.component) ? adjuster.clientHeight : 0
          );
          store.transformComponent(schema.id, { ...resizeStyle }, true);
        },
        () => store.commitGesture()
      );
    },
    [editorRef, isLocked, scale, schema.component, schema.id, startSession, store, style]
  );
  const handleMove = useCallback4(
    (event) => {
      if (!showHandles) return;
      event.preventDefault();
      event.stopPropagation();
      const editor = editorRef.current;
      const adjuster = adjusterRef.current;
      if (!editor || !adjuster) return;
      store.beginGesture();
      const startX = event.clientX;
      const startY = event.clientY;
      const selectedBounds = store.selectedComponents.value.map(
        (component) => getComponentRotatedStyle(component.style)
      );
      const selectionLeft = Math.min(...selectedBounds.map((box) => box.left));
      const selectionTop = Math.min(...selectedBounds.map((box) => box.top));
      const selectionRight = Math.max(...selectedBounds.map((box) => box.right));
      const selectionBottom = Math.max(...selectedBounds.map((box) => box.bottom));
      let lastDeltaLeft = 0;
      let lastDeltaTop = 0;
      startSession(
        (nextEvent) => {
          const totalDeltaLeft = Math.min(
            editor.offsetWidth - selectionRight,
            Math.max(-selectionLeft, (nextEvent.clientX - startX) / scale)
          );
          const totalDeltaTop = Math.min(
            editor.offsetHeight - selectionBottom,
            Math.max(-selectionTop, (nextEvent.clientY - startY) / scale)
          );
          store.moveSelection(totalDeltaLeft - lastDeltaLeft, totalDeltaTop - lastDeltaTop, true);
          lastDeltaLeft = totalDeltaLeft;
          lastDeltaTop = totalDeltaTop;
          onMove?.(nextEvent.clientY > startY, nextEvent.clientX > startX);
        },
        () => {
          onMoveEnd?.();
          store.commitGesture();
        }
      );
    },
    [editorRef, onMove, onMoveEnd, scale, showHandles, startSession, store]
  );
  const variables = {
    "--adjuster-left": `${number2(style.left)}px`,
    "--adjuster-top": `${number2(style.top)}px`,
    "--adjuster-width": `${style.width}px`,
    "--adjuster-height": `${style.height}px`,
    "--adjuster-rotate": `${style.rotate ?? 0}deg`
  };
  const quickBarVariables = {
    "--quick-bar-left": `${quickBarPosition.left}px`,
    "--quick-bar-top": `${quickBarPosition.top}px`
  };
  const duplicate = () => {
    store.copy();
    store.paste();
  };
  return /* @__PURE__ */ jsxs3(Fragment2, { children: [
    /* @__PURE__ */ jsxs3(
      "div",
      {
        ref: adjusterRef,
        "data-ptd-component-id": schema.id,
        style: variables,
        className: `${ComponentAdjuster_default.adjuster} ${isActive ? ComponentAdjuster_default.active : ""} ${isLocked ? ComponentAdjuster_default.locked : ""} ${isEditing ? ComponentAdjuster_default.editing : ""}`,
        "data-editing": isEditing || void 0,
        "data-out-of-bounds": outOfBounds || void 0,
        onMouseDown: handleMouseDownOnShape,
        onDoubleClick: (event) => {
          if (isLocked || !isDirectlyEditableComponent(schema)) return;
          event.preventDefault();
          event.stopPropagation();
          store.startContentEditing(schema.id);
        },
        children: [
          outOfBounds && /* @__PURE__ */ jsx5("span", { className: ComponentAdjuster_default.outOfBoundsMarker, "aria-label": "\u7EC4\u4EF6\u8D85\u51FA\u9875\u9762\u8FB9\u754C", children: "!" }),
          showHandles && !["RoySimpleTable", "RoyComplexTable"].includes(schema.component) && /* @__PURE__ */ jsx5(
            "button",
            {
              type: "button",
              className: ComponentAdjuster_default.rotate,
              onMouseDown: handleRotate,
              "aria-label": "\u65CB\u8F6C\u7EC4\u4EF6"
            }
          ),
          isLocked && /* @__PURE__ */ jsx5("span", { className: ComponentAdjuster_default.lock, title: "\u5DF2\u9501\u5B9A" }),
          showHandles && !showQuickBar && /* @__PURE__ */ jsx5(
            "button",
            {
              type: "button",
              className: ComponentAdjuster_default.move,
              onMouseDown: handleMove,
              "aria-label": "\u79FB\u52A8\u7EC4\u4EF6"
            }
          ),
          showHandles && pointList.map((point) => /* @__PURE__ */ jsx5(
            "button",
            {
              type: "button",
              className: ComponentAdjuster_default.point,
              style: getPointVariables(point, style.width, style.height, cursors[point]),
              onMouseDown: (event) => handleResize(point, event),
              "aria-label": `\u4ECE ${point} \u63A7\u70B9\u8C03\u6574\u5C3A\u5BF8`
            },
            point
          )),
          /* @__PURE__ */ jsx5("div", { className: ComponentAdjuster_default.container, children })
        ]
      }
    ),
    showQuickBar && typeof document !== "undefined" && createPortal(
      /* @__PURE__ */ jsx5(Tooltip.Provider, { delayDuration: 350, skipDelayDuration: 120, children: /* @__PURE__ */ jsxs3(
        "div",
        {
          ref: quickBarRef,
          className: `${ComponentAdjuster_default.quickBar} ${ptdThemeClass}`,
          "data-ready": quickBarPosition.ready && quickBarPosition.componentId === schema.id || void 0,
          style: quickBarVariables,
          onMouseDown: (event) => event.stopPropagation(),
          children: [
            /* @__PURE__ */ jsx5("span", { className: ComponentAdjuster_default.quickLabel, children: schema.name || schema.component }),
            /* @__PURE__ */ jsx5(QuickAction, { label: "\u62D6\u52A8\u7EC4\u4EF6", disabled: isLocked, onMouseDown: handleMove, children: /* @__PURE__ */ jsx5(RiDragMove2Line, {}) }),
            isDirectlyEditableComponent(schema) && /* @__PURE__ */ jsx5(
              QuickAction,
              {
                label: "\u7F16\u8F91\u5185\u5BB9",
                disabled: isLocked,
                onClick: () => store.startContentEditing(schema.id),
                children: /* @__PURE__ */ jsx5(RiEditLine, {})
              }
            ),
            /* @__PURE__ */ jsx5(
              QuickAction,
              {
                label: isLocked ? "\u89E3\u9501\u7EC4\u4EF6" : "\u9501\u5B9A\u7EC4\u4EF6",
                onClick: () => store.setLock(!isLocked),
                children: isLocked ? /* @__PURE__ */ jsx5(RiLockUnlockLine2, {}) : /* @__PURE__ */ jsx5(RiLockLine, {})
              }
            ),
            /* @__PURE__ */ jsx5(QuickAction, { label: "\u590D\u5236\u7EC4\u4EF6", disabled: isLocked, onClick: duplicate, children: /* @__PURE__ */ jsx5(RiFileCopyLine, {}) }),
            /* @__PURE__ */ jsx5(
              QuickAction,
              {
                label: "\u4E0A\u79FB\u4E00\u5C42",
                disabled: isLocked,
                onClick: () => store.moveLayer("forward"),
                children: /* @__PURE__ */ jsx5(RiBringForward, {})
              }
            ),
            /* @__PURE__ */ jsx5(
              QuickAction,
              {
                label: "\u5220\u9664\u7EC4\u4EF6",
                danger: true,
                disabled: isLocked,
                onClick: () => store.deleteSelected(),
                children: /* @__PURE__ */ jsx5(RiDeleteBinLine2, {})
              }
            )
          ]
        }
      ) }),
      document.body
    )
  ] });
}
function number2(value) {
  return typeof value === "number" ? value : 0;
}

// src/components/Canvas/ComponentRenderer.tsx
import { useEffect as useEffect8, useId, useRef as useRef7 } from "react";
import { useSignals as useSignals2 } from "@preact/signals-react/runtime";
import {
  resolveComponentBindings
} from "@ptd/core";
import { createComponentInstance } from "@ptd/components";

// src/components/TableEditor/TableEditor.tsx
import {
  useEffect as useEffect7,
  useMemo as useMemo3,
  useRef as useRef6,
  useState as useState6
} from "react";
import {
  expandTableCellRange,
  getTableCellAt as getTableCellAt2,
  getTableCellBounds as getTableCellBounds2,
  getTableCellIdsInRange,
  normalizeSimpleTableProps as normalizeSimpleTableProps2,
  resizeTableColumn,
  resizeTableRow,
  updateTableCellText as updateTableCellText2
} from "@ptd/core";

// src/components/TableEditor/TableEditor.module.css
var TableEditor_default = {
  editor: "TableEditor_editor",
  table: "TableEditor_table",
  cell: "TableEditor_cell",
  cellContent: "TableEditor_cellContent",
  cellInput: "TableEditor_cellInput",
  columnHandle: "TableEditor_columnHandle",
  rowHandle: "TableEditor_rowHandle",
  selectionReadout: "TableEditor_selectionReadout"
};

// src/components/TableEditor/TableEditor.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function TableEditor({ schema }) {
  const store = useEditorStore();
  const rootRef = useRef6(null);
  const dragRef = useRef6(null);
  const resizeRef = useRef6(null);
  const value = useMemo3(() => normalizeSimpleTableProps2(schema.propValue), [schema.propValue]);
  const selection = store.tableCellSelection.value?.componentId === schema.id ? store.tableCellSelection.value : null;
  const selectedRange = selection ? expandTableCellRange(value, {
    startRow: selection.anchorRow,
    startColumn: selection.anchorColumn,
    endRow: selection.focusRow,
    endColumn: selection.focusColumn
  }) : null;
  const selectedCellIds = new Set(selectedRange ? getTableCellIdsInRange(value, selectedRange) : []);
  const editing = store.editingTableCell.value?.componentId === schema.id ? store.editingTableCell.value : null;
  const totalWidth = sum(value.columnWidths);
  const totalHeight = sum(value.rowHeights);
  useEffect7(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);
  useEffect7(
    () => () => {
      resizeRef.current?.cleanup();
    },
    []
  );
  const selectCell = (row, column, extend = false) => {
    store.selectTableCell(schema.id, row, column, extend);
    rootRef.current?.focus({ preventScroll: true });
  };
  const startEditing = (row, column) => {
    const cell = getTableCellAt2(value, row, column);
    if (cell) store.startTableCellEditing(schema.id, cell.id);
  };
  const navigate = (row, column, extend = false) => {
    selectCell(
      clamp5(row, 0, value.grid.length - 1),
      clamp5(column, 0, value.columnWidths.length - 1),
      extend
    );
  };
  const handleKeyDown = (event) => {
    if (editing || !selection) return;
    const cell = getTableCellAt2(value, selection.focusRow, selection.focusColumn);
    const bounds = cell ? getTableCellBounds2(value, cell.id) : null;
    if (!cell || !bounds) return;
    let target = null;
    switch (event.key) {
      case "ArrowLeft":
        target = { row: selection.focusRow, column: bounds.startColumn - 1 };
        break;
      case "ArrowRight":
        target = { row: selection.focusRow, column: bounds.endColumn + 1 };
        break;
      case "ArrowUp":
        target = { row: bounds.startRow - 1, column: selection.focusColumn };
        break;
      case "ArrowDown":
        target = { row: bounds.endRow + 1, column: selection.focusColumn };
        break;
      case "Tab": {
        const delta = event.shiftKey ? -1 : 1;
        const index = selection.focusRow * value.columnWidths.length + selection.focusColumn + delta;
        const wrapped = (index + value.grid.length * value.columnWidths.length) % (value.grid.length * value.columnWidths.length);
        target = {
          row: Math.floor(wrapped / value.columnWidths.length),
          column: wrapped % value.columnWidths.length
        };
        break;
      }
      case "Enter":
      case "F2":
        event.preventDefault();
        event.stopPropagation();
        store.startTableCellEditing(schema.id, cell.id);
        return;
      case "Delete":
      case "Backspace": {
        event.preventDefault();
        event.stopPropagation();
        let next = value;
        for (const id of selectedCellIds) next = updateTableCellText2(next, id, "");
        if (next !== value) store.updateComponent(schema.id, { propValue: next });
        return;
      }
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        store.clearTableCellSelection(schema.id);
        return;
    }
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    navigate(target.row, target.column, event.shiftKey && event.key !== "Tab");
  };
  const beginTrackResize = (axis, index, event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current?.cleanup();
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const sizes = axis === "column" ? value.columnWidths : value.rowHeights;
    const first = sizes[index];
    const second = sizes[index + 1];
    const pixelSize = axis === "column" ? rect.width : rect.height;
    if (first === void 0 || second === void 0 || pixelSize <= 0) return;
    const start = axis === "column" ? event.clientX : event.clientY;
    const total = sum(sizes);
    let settled = false;
    store.beginGesture();
    const cleanup = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", finish);
      document.removeEventListener("pointercancel", cancel);
      window.removeEventListener("blur", finish);
      resizeRef.current = null;
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      store.commitGesture();
    };
    const cancel = () => {
      if (settled) return;
      settled = true;
      cleanup();
      store.cancelGesture();
    };
    const move = (nextEvent) => {
      const pointer = axis === "column" ? nextEvent.clientX : nextEvent.clientY;
      const requestedDelta = (pointer - start) / pixelSize * total;
      const delta = clamp5(requestedDelta, 8 - first, second - 8);
      let next;
      if (axis === "column") {
        next = resizeTableColumn(value, index, first + delta);
        next = resizeTableColumn(next, index + 1, second - delta);
      } else {
        next = resizeTableRow(value, index, first + delta);
        next = resizeTableRow(next, index + 1, second - delta);
      }
      store.updateComponent(schema.id, { propValue: next }, true);
    };
    resizeRef.current = { cleanup: finish };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", finish);
    document.addEventListener("pointercancel", cancel);
    window.addEventListener("blur", finish);
  };
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      ref: rootRef,
      className: TableEditor_default.editor,
      "data-ptd-table-editor": true,
      "data-ptd-editor-interactive": true,
      role: "group",
      "aria-label": `${schema.name ?? "\u81EA\u7531\u8868\u683C"}\uFF0C\u5355\u5143\u683C\u7F16\u8F91`,
      tabIndex: 0,
      onKeyDown: handleKeyDown,
      onPointerMove: (event) => {
        if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
        const target = document.elementFromPoint(event.clientX, event.clientY);
        const cell = target instanceof Element ? target.closest("[data-table-cell]") : null;
        if (!cell || cell.closest("[data-ptd-table-editor]") !== rootRef.current) return;
        const row = Number(cell.dataset.row);
        const column = Number(cell.dataset.column);
        if (Number.isInteger(row) && Number.isInteger(column)) selectCell(row, column, true);
      },
      onPointerUp: (event) => {
        if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
      },
      onPointerCancel: () => {
        dragRef.current = null;
      },
      children: [
        /* @__PURE__ */ jsxs4("table", { className: TableEditor_default.table, role: "grid", children: [
          /* @__PURE__ */ jsx6("colgroup", { children: value.columnWidths.map((width, index) => /* @__PURE__ */ jsx6("col", { style: trackVariables(width / totalWidth * 100) }, index)) }),
          /* @__PURE__ */ jsx6("tbody", { children: value.grid.map((row, rowIndex) => /* @__PURE__ */ jsx6(
            "tr",
            {
              style: trackVariables((value.rowHeights[rowIndex] ?? 0) / totalHeight * 100),
              children: row.map((cellId, columnIndex) => {
                const cell = value.cells[cellId];
                const bounds = getTableCellBounds2(value, cellId);
                if (!cell || !bounds || bounds.startRow !== rowIndex || bounds.startColumn !== columnIndex)
                  return null;
                const isEditing = editing?.cellId === cellId;
                return /* @__PURE__ */ jsx6(
                  "td",
                  {
                    className: TableEditor_default.cell,
                    style: cellVariables(cell.style),
                    rowSpan: cell.rowSpan,
                    colSpan: cell.colSpan,
                    "data-table-cell": true,
                    "data-cell-id": cellId,
                    "data-row": rowIndex,
                    "data-column": columnIndex,
                    "data-selected": selectedCellIds.has(cellId) || void 0,
                    "data-primary": selection && value.grid[selection.focusRow]?.[selection.focusColumn] === cellId || void 0,
                    "aria-selected": selectedCellIds.has(cellId),
                    onPointerDown: (event) => {
                      if (event.button !== 0 || isEditing) return;
                      event.preventDefault();
                      event.stopPropagation();
                      selectCell(rowIndex, columnIndex, event.shiftKey);
                      dragRef.current = { pointerId: event.pointerId };
                    },
                    onDoubleClick: (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      startEditing(rowIndex, columnIndex);
                    },
                    children: isEditing ? /* @__PURE__ */ jsx6(
                      TableCellInput,
                      {
                        cell,
                        onCommit: (text2) => store.commitTableCellEditing(schema.id, cellId, text2),
                        onCancel: () => store.cancelTableCellEditing(schema.id, cellId)
                      }
                    ) : /* @__PURE__ */ jsx6("div", { className: TableEditor_default.cellContent, children: cell.text })
                  },
                  cellId
                );
              })
            },
            rowIndex
          )) })
        ] }),
        value.columnWidths.slice(0, -1).map((_, index) => /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            className: TableEditor_default.columnHandle,
            style: handleVariables(trackPosition(value.columnWidths, index)),
            "aria-label": `\u8C03\u6574\u7B2C ${index + 1} \u5217\u4E0E\u7B2C ${index + 2} \u5217\u7684\u5BBD\u5EA6`,
            onPointerDown: (event) => beginTrackResize("column", index, event)
          },
          `column-${index}`
        )),
        value.rowHeights.slice(0, -1).map((_, index) => /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            className: TableEditor_default.rowHandle,
            style: handleVariables(trackPosition(value.rowHeights, index)),
            "aria-label": `\u8C03\u6574\u7B2C ${index + 1} \u884C\u4E0E\u7B2C ${index + 2} \u884C\u7684\u9AD8\u5EA6`,
            onPointerDown: (event) => beginTrackResize("row", index, event)
          },
          `row-${index}`
        )),
        selectedRange && /* @__PURE__ */ jsx6(SelectionReadout, { range: selectedRange, count: selectedCellIds.size })
      ]
    }
  );
}
function TableCellInput({
  cell,
  onCommit,
  onCancel
}) {
  const [draft, setDraft] = useState6(cell.text);
  const settledRef = useRef6(false);
  const composingRef = useRef6(false);
  const inputRef = useRef6(null);
  const commit = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onCommit(draft.replace(/\r\n/g, "\n"));
  };
  const cancel = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onCancel();
  };
  useEffect7(() => {
    inputRef.current?.focus({ preventScroll: true });
    inputRef.current?.select();
  }, []);
  return /* @__PURE__ */ jsx6(
    "textarea",
    {
      ref: inputRef,
      className: TableEditor_default.cellInput,
      "aria-label": "\u5355\u5143\u683C\u5185\u5BB9",
      value: draft,
      spellCheck: true,
      "data-ptd-editor-interactive": true,
      onChange: (event) => setDraft(event.target.value),
      onBlur: commit,
      onCompositionStart: () => {
        composingRef.current = true;
      },
      onCompositionEnd: () => {
        composingRef.current = false;
      },
      onPointerDown: (event) => event.stopPropagation(),
      onDoubleClick: (event) => event.stopPropagation(),
      onKeyDown: (event) => {
        event.stopPropagation();
        if (event.key === "Escape" && !composingRef.current) {
          event.preventDefault();
          cancel();
        } else if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          event.preventDefault();
          commit();
        }
      }
    }
  );
}
function SelectionReadout({ range, count }) {
  const rows = range.endRow - range.startRow + 1;
  const columns = range.endColumn - range.startColumn + 1;
  return /* @__PURE__ */ jsxs4("span", { className: TableEditor_default.selectionReadout, "aria-live": "polite", children: [
    rows,
    " \xD7 ",
    columns,
    " \xB7 ",
    count,
    " \u683C"
  ] });
}
function cellVariables(style) {
  return {
    "--table-font-family": style.fontFamily,
    "--table-font-size": `${style.fontSize}px`,
    "--table-font-weight": style.fontWeight,
    "--table-font-style": style.fontStyle,
    "--table-text-decoration": style.textDecoration,
    "--table-color": style.color,
    "--table-background": style.background,
    "--table-horizontal-align": style.horizontalAlign,
    "--table-vertical-align": style.verticalAlign,
    "--table-padding": `${style.padding}px`,
    "--table-border-color": style.borderColor,
    "--table-border-width": `${style.borderWidth}px`,
    "--table-border-style": style.borderStyle
  };
}
function trackVariables(percent) {
  return { "--table-track-size": `${percent}%` };
}
function handleVariables(percent) {
  return { "--table-handle-position": `${percent}%` };
}
function trackPosition(values, boundaryIndex) {
  return sum(values.slice(0, boundaryIndex + 1)) / sum(values) * 100;
}
function sum(values) {
  return values.reduce((total, value) => total + value, 0) || 1;
}
function clamp5(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

// src/components/Canvas/ComponentRenderer.module.css
var ComponentRenderer_default = {
  renderer: "ComponentRenderer_renderer",
  bindingDiagnostic: "ComponentRenderer_bindingDiagnostic",
  groupChild: "ComponentRenderer_groupChild"
};

// src/components/Canvas/ComponentRenderer.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
function ComponentRenderer({ schema, renderContext }) {
  useSignals2();
  const store = useEditorStore();
  const activeContext = renderContext ?? store.proofRenderContext.value;
  const resolution = activeContext ? resolveComponentBindings(schema, store.normalizedTemplateData.value.data, activeContext) : null;
  const renderedSchema = resolution?.component ?? schema;
  if (renderedSchema.component === "RoyGroup") {
    return /* @__PURE__ */ jsx7(GroupRenderer, { schema: renderedSchema, renderContext: activeContext });
  }
  if (!activeContext && renderedSchema.component === "RoySimpleTable" && !renderedSchema.isLock && store.selectedIds.value.length === 1 && store.selectedIds.value[0] === renderedSchema.id) {
    return /* @__PURE__ */ jsx7(TableEditor, { schema: renderedSchema });
  }
  if (!activeContext && store.editingComponentId.value === renderedSchema.id && isDirectlyEditableComponent(renderedSchema)) {
    return /* @__PURE__ */ jsx7(ContentEditor, { schema: renderedSchema });
  }
  return /* @__PURE__ */ jsx7(VanillaRenderer, { schema: renderedSchema, resolution });
}
function GroupRenderer({ schema, renderContext }) {
  const children = getScaledGroupChildren(schema);
  return /* @__PURE__ */ jsx7("div", { className: ComponentRenderer_default.renderer, role: "group", "aria-label": schema.name ?? "\u7EC4\u5408\u7EC4\u4EF6", children: children.map((child) => {
    const variables = {
      "--child-left": `${numeric(child.style.left)}px`,
      "--child-top": `${numeric(child.style.top)}px`,
      "--child-width": `${child.style.width}px`,
      "--child-height": `${child.style.height}px`,
      "--child-rotate": `${child.style.rotate ?? 0}deg`
    };
    return /* @__PURE__ */ jsx7("div", { className: ComponentRenderer_default.groupChild, style: variables, children: /* @__PURE__ */ jsx7(ComponentRenderer, { schema: child, renderContext }) }, child.id);
  }) });
}
function VanillaRenderer({
  schema,
  resolution
}) {
  const containerRef = useRef7(null);
  const instanceRef = useRef7(null);
  const diagnosticId = useId();
  useEffect8(() => {
    const element = containerRef.current;
    if (!element) return;
    const instance = createComponentInstance(withoutOuterTransform(schema));
    if (!instance) return;
    instance.mount(element);
    instanceRef.current = instance;
    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [schema.component]);
  useEffect8(() => {
    instanceRef.current?.update(withoutOuterTransform(schema));
  }, [schema]);
  const diagnosticLabel = bindingDiagnosticLabel(resolution);
  const diagnosticMessages = resolution?.diagnostics.map((item) => item.message).join("\n");
  return /* @__PURE__ */ jsx7(
    "div",
    {
      ref: containerRef,
      className: ComponentRenderer_default.renderer,
      "data-binding-status": resolution?.status,
      "data-binding-diagnostics": diagnosticMessages || void 0,
      "aria-describedby": resolution && resolution.status !== "ready" ? diagnosticId : void 0,
      children: resolution && resolution.status !== "ready" && /* @__PURE__ */ jsx7(
        "span",
        {
          id: diagnosticId,
          className: ComponentRenderer_default.bindingDiagnostic,
          "data-status": resolution.status,
          "data-ptd-binding-diagnostic": true,
          title: diagnosticMessages,
          role: "status",
          children: diagnosticLabel
        }
      )
    }
  );
}
function bindingDiagnosticLabel(resolution) {
  if (!resolution || resolution.status === "ready") return "\u6570\u636E\u5DF2\u5C31\u7EEA";
  return resolution.status === "missing" ? "\u5B57\u6BB5\u7F3A\u5931" : diagnosticSummary(resolution.diagnostics);
}
function diagnosticSummary(diagnostics) {
  if (diagnostics.some((diagnostic) => diagnostic.code === "invalid-binding-target")) {
    return "\u7ED1\u5B9A\u5DF2\u5931\u6548";
  }
  if (diagnostics.some((diagnostic) => diagnostic.code === "type-mismatch")) {
    return "\u5B57\u6BB5\u7C7B\u578B\u4E0D\u5339\u914D";
  }
  return "\u6570\u636E\u65E0\u6548";
}
function withoutOuterTransform(schema) {
  return { ...schema, style: { ...schema.style, rotate: 0 } };
}
function numeric(value) {
  return typeof value === "number" ? value : 0;
}

// src/components/Canvas/EditorLine.tsx
import { forwardRef, useCallback as useCallback5, useImperativeHandle, useRef as useRef8 } from "react";

// src/components/Canvas/EditorLine.module.css
var EditorLine_default = {
  markLine: "EditorLine_markLine",
  line: "EditorLine_line",
  xline: "EditorLine_xline",
  yline: "EditorLine_yline"
};

// src/components/Canvas/EditorLine.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
var DIFF = 3;
var EditorLine = forwardRef(function EditorLine2(_props, ref) {
  const store = useEditorStore();
  const lineRefs = useRef8({ x: null, y: null });
  const hideLines = useCallback5(() => {
    for (const line of Object.values(lineRefs.current)) {
      if (line) line.style.display = "none";
    }
  }, []);
  useImperativeHandle(
    ref,
    () => ({
      hideLines,
      showLines(_isDownward, _isRightward) {
        hideLines();
        if (store.selectedIds.value.length > 1) return;
        const components = store.components.value;
        const current = store.primaryComponent.value;
        if (!current || current.isLock) return;
        const currentBox = getComponentRotatedStyle(current.style);
        for (const component of components) {
          if (component.id === current.id) continue;
          const box = getComponentRotatedStyle(component.style);
          const horizontal = [box.top, (box.top + box.bottom) / 2, box.bottom];
          const vertical = [box.left, (box.left + box.right) / 2, box.right];
          const currentHorizontal = [
            currentBox.top,
            (currentBox.top + currentBox.bottom) / 2,
            currentBox.bottom
          ];
          const currentVertical = [
            currentBox.left,
            (currentBox.left + currentBox.right) / 2,
            currentBox.right
          ];
          const yTarget = horizontal.find(
            (target) => currentHorizontal.some((value) => Math.abs(target - value) <= DIFF)
          );
          const xTarget = vertical.find(
            (target) => currentVertical.some((value) => Math.abs(target - value) <= DIFF)
          );
          if (yTarget !== void 0 && lineRefs.current.x) {
            lineRefs.current.x.style.display = "block";
            lineRefs.current.x.style.top = `${yTarget}px`;
          }
          if (xTarget !== void 0 && lineRefs.current.y) {
            lineRefs.current.y.style.display = "block";
            lineRefs.current.y.style.left = `${xTarget}px`;
          }
          if (yTarget !== void 0 || xTarget !== void 0) {
            const patch = {};
            if (yTarget !== void 0) {
              const matched = currentHorizontal.findIndex(
                (value) => Math.abs(yTarget - value) <= DIFF
              );
              patch.top = numeric2(current.style.top) + yTarget - currentHorizontal[matched];
            }
            if (xTarget !== void 0) {
              const matched = currentVertical.findIndex(
                (value) => Math.abs(xTarget - value) <= DIFF
              );
              patch.left = numeric2(current.style.left) + xTarget - currentVertical[matched];
            }
            store.transformComponent(current.id, patch, true);
            return;
          }
        }
      }
    }),
    [hideLines, store]
  );
  return /* @__PURE__ */ jsxs5("div", { className: EditorLine_default.markLine, children: [
    /* @__PURE__ */ jsx8(
      "div",
      {
        ref: (element) => {
          lineRefs.current.x = element;
        },
        className: `${EditorLine_default.line} ${EditorLine_default.xline}`
      }
    ),
    /* @__PURE__ */ jsx8(
      "div",
      {
        ref: (element) => {
          lineRefs.current.y = element;
        },
        className: `${EditorLine_default.line} ${EditorLine_default.yline}`
      }
    )
  ] });
});
function numeric2(value) {
  return typeof value === "number" ? value : 0;
}

// src/components/Canvas/Ruler.tsx
import {
  useCallback as useCallback6,
  useEffect as useEffect9,
  useRef as useRef9,
  useState as useState7
} from "react";
import { useSignals as useSignals3 } from "@preact/signals-react/runtime";
import {
  MEASUREMENT_UNIT_DEFINITIONS as MEASUREMENT_UNIT_DEFINITIONS2,
  formatMeasurement as formatMeasurement2,
  mmToPx as mmToPx2,
  pxToMm,
  snapMeasurement,
  toDisplayMeasurement as toDisplayMeasurement2
} from "@ptd/core";

// src/components/Canvas/rulerMarks.ts
import {
  MEASUREMENT_UNIT_DEFINITIONS,
  formatMeasurement,
  fromDisplayMeasurement,
  mmToPx,
  toDisplayMeasurement
} from "@ptd/core";
function createRulerMarks(totalMm, scale, unit = "mm") {
  if (!Number.isFinite(totalMm) || totalMm <= 0 || !Number.isFinite(scale) || scale <= 0) return [];
  const definition = MEASUREMENT_UNIT_DEFINITIONS[unit];
  const total = toDisplayMeasurement(mmToPx(totalMm), unit);
  const values = [];
  for (let value = 0; value < total; value += definition.rulerMinorStep) values.push(value);
  if (values.at(-1) !== total) values.push(total);
  return values.map((value) => {
    const endpoint = value === 0 || value === total;
    const major = endpoint || value % definition.rulerMajorStep === 0;
    const canvasPosition = fromDisplayMeasurement(value, unit);
    return {
      value,
      position: canvasPosition * scale,
      kind: endpoint ? "endpoint" : major ? "major" : "minor",
      label: endpoint || value % definition.rulerLabelStep === 0 ? formatMeasurement(canvasPosition, unit) : void 0
    };
  });
}

// src/components/Canvas/Ruler.module.css
var Ruler_default = {
  rulers: "Ruler_rulers",
  horizontal: "Ruler_horizontal",
  vertical: "Ruler_vertical",
  unit: "Ruler_unit",
  guide: "Ruler_guide",
  mark: "Ruler_mark",
  label: "Ruler_label",
  guideStroke: "Ruler_guideStroke",
  guideMarker: "Ruler_guideMarker",
  guidePreview: "Ruler_guidePreview",
  guideLabel: "Ruler_guideLabel"
};

// src/components/Canvas/Ruler.tsx
import { jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
function Ruler({ widthMm, heightMm, scale }) {
  useSignals3();
  const store = useEditorStore();
  const horizontalRef = useRef9(null);
  const verticalRef = useRef9(null);
  const cleanupRef = useRef9(null);
  const [hoverGuide, setHoverGuide] = useState7(null);
  const measurementUnit = store.measurementUnit.value;
  const unitDefinition = MEASUREMENT_UNIT_DEFINITIONS2[measurementUnit];
  const horizontalMarks = createRulerMarks(widthMm, scale, measurementUnit);
  const verticalMarks = createRulerMarks(heightMm, scale, measurementUnit);
  const horizontalTotal = toDisplayMeasurement2(mmToPx2(widthMm), measurementUnit);
  const verticalTotal = toDisplayMeasurement2(mmToPx2(heightMm), measurementUnit);
  useEffect9(() => () => cleanupRef.current?.(), []);
  const positionFromPointer = useCallback6(
    (axis, clientX, clientY) => {
      const ruler = axis === "x" ? horizontalRef.current : verticalRef.current;
      if (!ruler) return 0;
      const rect = ruler.getBoundingClientRect();
      const screenPosition = axis === "x" ? clientX - rect.left : clientY - rect.top;
      const totalCanvasPx = mmToPx2(axis === "x" ? widthMm : heightMm);
      const canvasPosition = Math.min(totalCanvasPx, Math.max(0, screenPosition / scale));
      return pxToMm(snapMeasurement(canvasPosition, measurementUnit));
    },
    [heightMm, measurementUnit, scale, widthMm]
  );
  const startGuideInteraction = useCallback6(
    (axis, event, existingId) => {
      if (store.guidesLocked.value) return;
      event.preventDefault();
      event.stopPropagation();
      setHoverGuide(null);
      const initialPosition = positionFromPointer(axis, event.clientX, event.clientY);
      const guideId = existingId ?? store.addGuide(axis, initialPosition);
      if (!guideId) return;
      store.selectGuide(guideId);
      store.moveGuide(guideId, initialPosition);
      cleanupRef.current?.();
      const move = (nextEvent) => {
        store.moveGuide(guideId, positionFromPointer(axis, nextEvent.clientX, nextEvent.clientY));
      };
      const finish = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", finish);
        document.removeEventListener("pointercancel", finish);
        cleanupRef.current = null;
      };
      cleanupRef.current = finish;
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", finish);
      document.addEventListener("pointercancel", finish);
    },
    [positionFromPointer, store]
  );
  const addGuideAtCenter = (axis) => {
    const total = axis === "x" ? widthMm : heightMm;
    store.addGuide(axis, pxToMm(snapMeasurement(mmToPx2(total / 2), measurementUnit)));
  };
  const previewGuide = (axis, event) => {
    if (store.guidesLocked.value || cleanupRef.current) return;
    setHoverGuide({
      axis,
      positionMm: positionFromPointer(axis, event.clientX, event.clientY)
    });
  };
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: Ruler_default.rulers,
      role: "group",
      "aria-label": `${unitDefinition.accessibleLabel}\u6807\u5C3A\u4E0E\u53C2\u8003\u7EBF`,
      "data-ptd-ruler": measurementUnit,
      children: [
        /* @__PURE__ */ jsx9("span", { className: Ruler_default.unit, children: unitDefinition.label }),
        /* @__PURE__ */ jsx9(
          "button",
          {
            ref: horizontalRef,
            type: "button",
            className: Ruler_default.horizontal,
            "aria-label": "\u4ECE\u6C34\u5E73\u6807\u5C3A\u6DFB\u52A0\u5782\u76F4\u53C2\u8003\u7EBF",
            disabled: store.guidesLocked.value,
            onPointerDown: (event) => startGuideInteraction("x", event),
            onPointerMove: (event) => previewGuide("x", event),
            onPointerLeave: () => setHoverGuide(null),
            onKeyDown: (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                addGuideAtCenter("x");
              }
            },
            children: horizontalMarks.map((mark) => /* @__PURE__ */ jsx9(RulerMarkView, { mark, axis: "x", total: horizontalTotal }, mark.value))
          }
        ),
        /* @__PURE__ */ jsx9(
          "button",
          {
            ref: verticalRef,
            type: "button",
            className: Ruler_default.vertical,
            "aria-label": "\u4ECE\u5782\u76F4\u6807\u5C3A\u6DFB\u52A0\u6C34\u5E73\u53C2\u8003\u7EBF",
            disabled: store.guidesLocked.value,
            onPointerDown: (event) => startGuideInteraction("y", event),
            onPointerMove: (event) => previewGuide("y", event),
            onPointerLeave: () => setHoverGuide(null),
            onKeyDown: (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                addGuideAtCenter("y");
              }
            },
            children: verticalMarks.map((mark) => /* @__PURE__ */ jsx9(RulerMarkView, { mark, axis: "y", total: verticalTotal }, mark.value))
          }
        ),
        store.guidesVisible.value && hoverGuide && /* @__PURE__ */ jsxs6(
          "span",
          {
            className: `${Ruler_default.guide} ${Ruler_default.guidePreview}`,
            "data-axis": hoverGuide.axis,
            "data-color": store.activeGuideColor.value,
            style: createGuidePosition(hoverGuide.positionMm, scale),
            "aria-hidden": "true",
            children: [
              /* @__PURE__ */ jsx9("span", { className: Ruler_default.guideStroke }),
              /* @__PURE__ */ jsx9("span", { className: Ruler_default.guideMarker }),
              /* @__PURE__ */ jsx9("span", { className: Ruler_default.guideLabel, children: formatGuideLabel(hoverGuide.axis, hoverGuide.positionMm, measurementUnit) })
            ]
          }
        ),
        store.guidesVisible.value && store.guides.value.map((guide) => {
          const position = createGuidePosition(guide.positionMm, scale);
          const selected = store.selectedGuideId.value === guide.id;
          return /* @__PURE__ */ jsxs6(
            "button",
            {
              type: "button",
              className: Ruler_default.guide,
              "data-axis": guide.axis,
              "data-color": guide.color,
              "data-selected": selected || void 0,
              "aria-label": `${guide.axis.toUpperCase()} \u8F74\u53C2\u8003\u7EBF ${formatMeasurement2(mmToPx2(guide.positionMm), measurementUnit)} ${unitDefinition.accessibleLabel}`,
              style: position,
              onClick: (event) => {
                event.stopPropagation();
                store.selectGuide(guide.id);
              },
              onDoubleClick: () => store.removeGuide(guide.id),
              onPointerDown: (event) => startGuideInteraction(guide.axis, event, guide.id),
              children: [
                /* @__PURE__ */ jsx9("span", { className: Ruler_default.guideStroke }),
                /* @__PURE__ */ jsx9("span", { className: Ruler_default.guideMarker }),
                /* @__PURE__ */ jsx9("span", { className: Ruler_default.guideLabel, children: formatGuideLabel(guide.axis, guide.positionMm, measurementUnit) })
              ]
            },
            guide.id
          );
        })
      ]
    }
  );
}
function formatGuideLabel(axis, positionMm, unit) {
  return `${axis.toUpperCase()} ${formatMeasurement2(mmToPx2(positionMm), unit)} ${unit}`;
}
function createGuidePosition(positionMm, scale) {
  return { "--guide-position": `${mmToPx2(positionMm) * scale}px` };
}
function RulerMarkView({ mark, axis, total }) {
  const variables = {
    "--ruler-position": `${mark.position}px`
  };
  const edge = mark.value === 0 ? "start" : mark.value === total ? "end" : void 0;
  return /* @__PURE__ */ jsx9(
    "span",
    {
      className: Ruler_default.mark,
      "data-axis": axis,
      "data-kind": mark.kind,
      "data-edge": edge,
      style: variables,
      children: mark.label && /* @__PURE__ */ jsx9("span", { className: Ruler_default.label, children: mark.label })
    }
  );
}

// src/components/Canvas/CanvasContextMenu.tsx
import { useSignals as useSignals4 } from "@preact/signals-react/runtime";
import * as ContextMenu from "@radix-ui/react-context-menu";
import {
  RiArrowRightSLine,
  RiBringForward as RiBringForward2,
  RiBringToFront,
  RiClipboardLine,
  RiDeleteBinLine as RiDeleteBinLine3,
  RiFileCopyLine as RiFileCopyLine2,
  RiGroupLine,
  RiLayoutRightLine,
  RiLockLine as RiLockLine2,
  RiLockUnlockLine as RiLockUnlockLine3,
  RiScissorsCutLine,
  RiSendBackward,
  RiSendToBack,
  RiSplitCellsHorizontal,
  RiStackLine
} from "@remixicon/react";

// src/components/Canvas/CanvasContextMenu.module.css
var CanvasContextMenu_default = {
  content: "CanvasContextMenu_content",
  menuIn: "CanvasContextMenu_menuIn",
  item: "CanvasContextMenu_item",
  subArrow: "CanvasContextMenu_subArrow",
  separator: "CanvasContextMenu_separator"
};

// src/components/Canvas/CanvasContextMenu.tsx
import { Fragment as Fragment3, jsx as jsx10, jsxs as jsxs7 } from "react/jsx-runtime";
function MenuItem({
  label,
  shortcut,
  disabled = false,
  danger = false,
  onSelect,
  children
}) {
  return /* @__PURE__ */ jsxs7(
    ContextMenu.Item,
    {
      className: CanvasContextMenu_default.item,
      disabled,
      "data-danger": danger || void 0,
      onSelect,
      children: [
        children,
        /* @__PURE__ */ jsx10("span", { children: label }),
        shortcut && /* @__PURE__ */ jsx10("kbd", { children: shortcut })
      ]
    }
  );
}
function LayerMenu({ disabled }) {
  const store = useEditorStore();
  return /* @__PURE__ */ jsxs7(ContextMenu.Sub, { children: [
    /* @__PURE__ */ jsxs7(ContextMenu.SubTrigger, { className: CanvasContextMenu_default.item, disabled, children: [
      /* @__PURE__ */ jsx10(RiStackLine, { "aria-hidden": "true" }),
      /* @__PURE__ */ jsx10("span", { children: "\u6392\u5217\u5C42\u7EA7" }),
      /* @__PURE__ */ jsx10(RiArrowRightSLine, { className: CanvasContextMenu_default.subArrow, "aria-hidden": "true" })
    ] }),
    /* @__PURE__ */ jsx10(ContextMenu.Portal, { children: /* @__PURE__ */ jsxs7(
      ContextMenu.SubContent,
      {
        className: `${CanvasContextMenu_default.content} ${ptdThemeClass}`,
        "data-ptd-portal-interactive": true,
        sideOffset: 4,
        collisionPadding: 8,
        children: [
          /* @__PURE__ */ jsx10(MenuItem, { label: "\u4E0A\u79FB\u4E00\u5C42", onSelect: () => store.moveLayer("forward"), children: /* @__PURE__ */ jsx10(RiBringForward2, { "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsx10(MenuItem, { label: "\u4E0B\u79FB\u4E00\u5C42", onSelect: () => store.moveLayer("backward"), children: /* @__PURE__ */ jsx10(RiSendBackward, { "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsx10(ContextMenu.Separator, { className: CanvasContextMenu_default.separator }),
          /* @__PURE__ */ jsx10(MenuItem, { label: "\u7F6E\u4E8E\u9876\u5C42", onSelect: () => store.moveLayer("front"), children: /* @__PURE__ */ jsx10(RiBringToFront, { "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsx10(MenuItem, { label: "\u7F6E\u4E8E\u5E95\u5C42", onSelect: () => store.moveLayer("back"), children: /* @__PURE__ */ jsx10(RiSendToBack, { "aria-hidden": "true" }) })
        ]
      }
    ) })
  ] });
}
function CanvasContextMenu({
  children,
  onOpenInspector,
  onPasteAtContext
}) {
  useSignals4();
  const store = useEditorStore();
  const selected = store.selectedComponents.value;
  const blank = selected.length === 0;
  const hasLocked = selected.some((component) => component.isLock);
  const canGroup = selected.length > 1 && !hasLocked;
  const canUngroup = !hasLocked && selected.length > 0 && selected.every((component) => component.component === "RoyGroup");
  return /* @__PURE__ */ jsxs7(ContextMenu.Root, { children: [
    /* @__PURE__ */ jsx10(ContextMenu.Trigger, { asChild: true, children }),
    /* @__PURE__ */ jsx10(ContextMenu.Portal, { children: /* @__PURE__ */ jsxs7(
      ContextMenu.Content,
      {
        className: `${CanvasContextMenu_default.content} ${ptdThemeClass}`,
        "data-ptd-portal-interactive": true,
        collisionPadding: 8,
        children: [
          /* @__PURE__ */ jsx10(MenuItem, { label: blank ? "\u9875\u9762\u5C5E\u6027" : "\u7EC4\u4EF6\u5C5E\u6027", onSelect: onOpenInspector, children: /* @__PURE__ */ jsx10(RiLayoutRightLine, { "aria-hidden": "true" }) }),
          blank ? /* @__PURE__ */ jsxs7(Fragment3, { children: [
            /* @__PURE__ */ jsx10(ContextMenu.Separator, { className: CanvasContextMenu_default.separator }),
            /* @__PURE__ */ jsx10(
              MenuItem,
              {
                label: "\u7C98\u8D34\u5230\u6B64\u5904",
                shortcut: "Ctrl V",
                disabled: !store.clipboard.value,
                onSelect: onPasteAtContext,
                children: /* @__PURE__ */ jsx10(RiClipboardLine, { "aria-hidden": "true" })
              }
            )
          ] }) : /* @__PURE__ */ jsxs7(Fragment3, { children: [
            /* @__PURE__ */ jsx10(ContextMenu.Separator, { className: CanvasContextMenu_default.separator }),
            /* @__PURE__ */ jsx10(MenuItem, { label: "\u590D\u5236", shortcut: "Ctrl C", onSelect: () => store.copy(), children: /* @__PURE__ */ jsx10(RiFileCopyLine2, { "aria-hidden": "true" }) }),
            /* @__PURE__ */ jsx10(
              MenuItem,
              {
                label: "\u526A\u5207",
                shortcut: "Ctrl X",
                disabled: hasLocked,
                onSelect: () => store.cut(),
                children: /* @__PURE__ */ jsx10(RiScissorsCutLine, { "aria-hidden": "true" })
              }
            ),
            /* @__PURE__ */ jsx10(ContextMenu.Separator, { className: CanvasContextMenu_default.separator }),
            /* @__PURE__ */ jsx10(
              MenuItem,
              {
                label: hasLocked ? "\u89E3\u9501\u6240\u9009\u5BF9\u8C61" : "\u9501\u5B9A\u6240\u9009\u5BF9\u8C61",
                onSelect: () => store.setLock(!hasLocked),
                children: hasLocked ? /* @__PURE__ */ jsx10(RiLockUnlockLine3, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx10(RiLockLine2, { "aria-hidden": "true" })
              }
            ),
            canGroup && /* @__PURE__ */ jsx10(MenuItem, { label: "\u7EC4\u5408\u6240\u9009\u5BF9\u8C61", onSelect: () => store.group(), children: /* @__PURE__ */ jsx10(RiGroupLine, { "aria-hidden": "true" }) }),
            canUngroup && /* @__PURE__ */ jsx10(MenuItem, { label: "\u62C6\u5206\u7EC4\u5408", onSelect: () => store.ungroup(), children: /* @__PURE__ */ jsx10(RiSplitCellsHorizontal, { "aria-hidden": "true" }) }),
            /* @__PURE__ */ jsx10(LayerMenu, { disabled: hasLocked }),
            /* @__PURE__ */ jsx10(ContextMenu.Separator, { className: CanvasContextMenu_default.separator }),
            /* @__PURE__ */ jsx10(
              MenuItem,
              {
                label: "\u5220\u9664",
                shortcut: "Delete",
                disabled: hasLocked,
                danger: true,
                onSelect: () => store.deleteSelected(),
                children: /* @__PURE__ */ jsx10(RiDeleteBinLine3, { "aria-hidden": "true" })
              }
            )
          ] })
        ]
      }
    ) })
  ] });
}

// src/components/Canvas/Canvas.module.css
var Canvas_default = {
  canvasWrapper: "Canvas_canvasWrapper",
  canvas: "Canvas_canvas",
  drawPreview: "Canvas_drawPreview",
  canvasStage: "Canvas_canvasStage",
  safeArea: "Canvas_safeArea"
};

// src/components/Canvas/Canvas.tsx
import { jsx as jsx11, jsxs as jsxs8 } from "react/jsx-runtime";
function Canvas({ onOpenInspector }) {
  useSignals5();
  const store = useEditorStore();
  const wrapperRef = useRef10(null);
  const editorRef = useRef10(null);
  const editorLineRef = useRef10(null);
  const selectionCleanupRef = useRef10(null);
  const drawSessionRef = useRef10(null);
  const panSessionRef = useRef10(null);
  const contextPointRef = useRef10({ x: 0, y: 0 });
  const [drawSession, setDrawSession] = useState8(null);
  const [isPanning, setIsPanning] = useState8(false);
  const components = store.components.value;
  const outOfBoundsIds = new Set(store.outOfBoundsComponents.value.map((component) => component.id));
  const pageConfig = store.pageConfig.value;
  const scale = store.scale.value;
  const selectedIds = store.selectedIds.value;
  const effectiveTool = store.effectiveTool.value;
  const { width: pageWidthPx, height: pageHeightPx } = getPageDimensions2(pageConfig);
  const pageWidthMm = pageConfig.pageDirection === "l" ? pageConfig.pageHeight : pageConfig.pageWidth;
  const pageHeightMm = pageConfig.pageDirection === "l" ? pageConfig.pageWidth : pageConfig.pageHeight;
  useEffect10(
    () => () => {
      selectionCleanupRef.current?.();
    },
    []
  );
  const cancelDrawing = useCallback7(() => {
    drawSessionRef.current = null;
    setDrawSession(null);
  }, []);
  const cancelPanning = useCallback7(() => {
    panSessionRef.current = null;
    setIsPanning(false);
  }, []);
  useEffect10(() => {
    const drawing = drawSessionRef.current;
    if (drawing && drawing.tool !== effectiveTool) cancelDrawing();
    if (panSessionRef.current && effectiveTool !== "hand") cancelPanning();
  }, [cancelDrawing, cancelPanning, effectiveTool]);
  useEffect10(() => {
    const cancelInteractions = () => {
      cancelDrawing();
      cancelPanning();
    };
    window.addEventListener("blur", cancelInteractions);
    return () => window.removeEventListener("blur", cancelInteractions);
  }, [cancelDrawing, cancelPanning]);
  const handleMouseDown = useCallback7(
    (event) => {
      if (effectiveTool !== "select" || event.button !== 0 || event.target !== event.currentTarget)
        return;
      const editor = editorRef.current;
      if (!editor) return;
      event.preventDefault();
      selectionCleanupRef.current?.();
      store.clearSelection();
      const viewport = editor.closest('[data-ptd-region="canvas-viewport"]');
      const canvasBounds = { width: pageWidthPx, height: pageHeightPx };
      const startPoint = canvasPointFromClient(
        editor.getBoundingClientRect(),
        event.clientX,
        event.clientY,
        scale,
        canvasBounds
      );
      let lastPointer = { x: event.clientX, y: event.clientY };
      let hasMoved = false;
      let frame = 0;
      store.startAreaSelection(startPoint.x, startPoint.y);
      const updateArea = () => {
        const currentPoint = canvasPointFromClient(
          editor.getBoundingClientRect(),
          lastPointer.x,
          lastPointer.y,
          scale,
          canvasBounds
        );
        store.updateAreaSelection(selectionAreaBetween(startPoint, currentPoint));
      };
      const scrollViewport = () => {
        if (!viewport || !hasMoved) return;
        const delta = areaAutoScrollDelta(lastPointer, viewport.getBoundingClientRect());
        const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const maxScrollTop = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
        const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, viewport.scrollLeft + delta.x));
        const nextScrollTop = Math.min(maxScrollTop, Math.max(0, viewport.scrollTop + delta.y));
        if (nextScrollLeft === viewport.scrollLeft && nextScrollTop === viewport.scrollTop) return;
        viewport.scrollLeft = nextScrollLeft;
        viewport.scrollTop = nextScrollTop;
        updateArea();
      };
      const tick = () => {
        scrollViewport();
        frame = requestAnimationFrame(tick);
      };
      const cleanup = () => {
        cancelAnimationFrame(frame);
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", up);
        window.removeEventListener("blur", cancel);
        selectionCleanupRef.current = null;
      };
      const move = (nextEvent) => {
        nextEvent.preventDefault();
        hasMoved = true;
        lastPointer = { x: nextEvent.clientX, y: nextEvent.clientY };
        updateArea();
      };
      const up = (nextEvent) => {
        lastPointer = { x: nextEvent.clientX, y: nextEvent.clientY };
        updateArea();
        cleanup();
        const area2 = store.areaSelection.value.style;
        const ids = store.components.value.filter((component) => {
          if (component.isLock) return false;
          const box = getComponentRotatedStyle(component.style);
          return area2.left <= box.left && area2.top <= box.top && box.right <= area2.left + area2.width && box.bottom <= area2.top + area2.height;
        }).map((component) => component.id);
        store.finishAreaSelection(ids);
      };
      const cancel = () => {
        cleanup();
        store.cancelAreaSelection();
      };
      selectionCleanupRef.current?.();
      selectionCleanupRef.current = cancel;
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
      window.addEventListener("blur", cancel);
      frame = requestAnimationFrame(tick);
    },
    [effectiveTool, pageHeightPx, pageWidthPx, scale, store]
  );
  const canvasPoint = useCallback7(
    (clientX, clientY) => {
      const editor = editorRef.current;
      if (!editor) return { x: 0, y: 0 };
      return canvasPointFromClient(editor.getBoundingClientRect(), clientX, clientY, scale, {
        width: pageWidthPx,
        height: pageHeightPx
      });
    },
    [pageHeightPx, pageWidthPx, scale]
  );
  const handlePanPointerDown = useCallback7(
    (event) => {
      if (effectiveTool !== "hand" || event.button !== 0) return;
      const viewport = event.currentTarget.closest(
        '[data-ptd-region="canvas-viewport"]'
      );
      if (!viewport) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      panSessionRef.current = {
        pointerId: event.pointerId,
        viewport,
        clientStart: { x: event.clientX, y: event.clientY },
        scrollStart: { x: viewport.scrollLeft, y: viewport.scrollTop }
      };
      setIsPanning(true);
    },
    [effectiveTool]
  );
  const handlePanPointerMove = useCallback7((event) => {
    const current = panSessionRef.current;
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    current.viewport.scrollLeft = current.scrollStart.x - (event.clientX - current.clientStart.x);
    current.viewport.scrollTop = current.scrollStart.y - (event.clientY - current.clientStart.y);
  }, []);
  const handlePanPointerEnd = useCallback7(
    (event) => {
      if (panSessionRef.current?.pointerId !== event.pointerId) return;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      cancelPanning();
    },
    [cancelPanning]
  );
  const handleDrawPointerDown = useCallback7(
    (event) => {
      if (!isDrawnComponentType(effectiveTool) || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.focus({ preventScroll: true });
      event.currentTarget.setPointerCapture(event.pointerId);
      const start = canvasPoint(event.clientX, event.clientY);
      const session = {
        pointerId: event.pointerId,
        tool: effectiveTool,
        start,
        end: start,
        clientStart: { x: event.clientX, y: event.clientY },
        clientEnd: { x: event.clientX, y: event.clientY },
        constrain: event.shiftKey
      };
      drawSessionRef.current = session;
      setDrawSession(session);
    },
    [canvasPoint, effectiveTool]
  );
  const handleDrawPointerMove = useCallback7(
    (event) => {
      const current = drawSessionRef.current;
      if (!current || current.pointerId !== event.pointerId) return;
      event.preventDefault();
      const next = {
        ...current,
        end: canvasPoint(event.clientX, event.clientY),
        clientEnd: { x: event.clientX, y: event.clientY },
        constrain: event.shiftKey
      };
      drawSessionRef.current = next;
      setDrawSession(next);
    },
    [canvasPoint]
  );
  const handleDrawPointerUp = useCallback7(
    (event) => {
      const current = drawSessionRef.current;
      if (!current || current.pointerId !== event.pointerId) return;
      event.preventDefault();
      const end = canvasPoint(event.clientX, event.clientY);
      const clientEnd = { x: event.clientX, y: event.clientY };
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      cancelDrawing();
      if (store.effectiveTool.value !== current.tool) return;
      if (!isDrawingGestureLongEnough(current.clientStart, clientEnd)) return;
      const component = createDrawnComponentSchema(
        current.tool,
        current.start,
        end,
        { width: pageWidthPx, height: pageHeightPx },
        event.shiftKey
      );
      if (component) store.completeDrawnComponent(component, current.tool);
    },
    [cancelDrawing, canvasPoint, pageHeightPx, pageWidthPx, store]
  );
  const handleDrawPointerCancel = useCallback7(
    (event) => {
      if (drawSessionRef.current?.pointerId !== event.pointerId) return;
      cancelDrawing();
    },
    [cancelDrawing]
  );
  const handleContextMenu = useCallback7(
    (event) => {
      const editor = editorRef.current;
      if (!editor) return;
      contextPointRef.current = canvasPointFromClient(
        editor.getBoundingClientRect(),
        event.clientX,
        event.clientY,
        scale,
        { width: pageWidthPx, height: pageHeightPx }
      );
      const target = event.target.closest("[data-ptd-component-id]");
      const componentId = target?.dataset.ptdComponentId;
      if (!componentId) {
        store.clearSelection();
        return;
      }
      if (!store.selectedIds.value.includes(componentId)) store.selectComponent(componentId);
    },
    [pageHeightPx, pageWidthPx, scale, store]
  );
  const handleContextMenuKeyDown = useCallback7((event) => {
    if (!(event.shiftKey && event.key === "F10" || event.key === "ContextMenu")) return;
    event.preventDefault();
    const target = event.target instanceof HTMLElement ? event.target : event.currentTarget;
    const rect = target.getBoundingClientRect();
    target.dispatchEvent(
      new globalThis.MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2
      })
    );
  }, []);
  const handleMove = useCallback7((isDownward, isRightward) => {
    editorLineRef.current?.showLines(isDownward, isRightward);
  }, []);
  const canvasStyle = {
    "--canvas-width": `${pageWidthPx}px`,
    "--canvas-height": `${pageHeightPx}px`,
    "--canvas-scaled-width": `${pageWidthPx * scale}px`,
    "--canvas-scaled-height": `${pageHeightPx * scale}px`,
    "--canvas-scale": String(scale),
    "--canvas-background": pageConfig.background,
    "--canvas-color": pageConfig.color,
    "--canvas-font-family": pageConfig.fontFamily,
    "--canvas-font-size": `${pageConfig.fontSize}px`,
    "--canvas-line-height": String(pageConfig.lineHeight),
    "--margin-top": `${mmToPx3(pageConfig.pageMarginTop)}px`,
    "--margin-right": `${mmToPx3(pageConfig.pageMarginRight)}px`,
    "--margin-bottom": `${mmToPx3(pageConfig.pageMarginBottom)}px`,
    "--margin-left": `${mmToPx3(pageConfig.pageMarginLeft)}px`
  };
  const area = store.areaSelection.value.style;
  const previewGeometry = drawSession ? drawnComponentGeometry(
    drawSession.tool,
    drawSession.start,
    drawSession.end,
    { width: pageWidthPx, height: pageHeightPx },
    drawSession.constrain
  ) : null;
  const previewStyle = previewGeometry ? {
    "--preview-left": `${previewGeometry.left}px`,
    "--preview-top": `${previewGeometry.top}px`,
    "--preview-width": `${previewGeometry.width}px`,
    "--preview-height": `${previewGeometry.height}px`,
    "--preview-rotate": `${previewGeometry.rotate}deg`
  } : void 0;
  return /* @__PURE__ */ jsx11(
    "div",
    {
      ref: wrapperRef,
      className: Canvas_default.canvasWrapper,
      style: canvasStyle,
      "data-effective-tool": effectiveTool,
      "data-panning": isPanning || void 0,
      onPointerDown: handlePanPointerDown,
      onPointerMove: handlePanPointerMove,
      onPointerUp: handlePanPointerEnd,
      onPointerCancel: handlePanPointerEnd,
      onLostPointerCapture: handlePanPointerEnd,
      children: /* @__PURE__ */ jsxs8("div", { className: Canvas_default.canvasStage, children: [
        store.showRuler.value && /* @__PURE__ */ jsx11(Ruler, { widthMm: pageWidthMm, heightMm: pageHeightMm, scale }),
        /* @__PURE__ */ jsx11(
          CanvasContextMenu,
          {
            onOpenInspector,
            onPasteAtContext: () => store.pasteAt(contextPointRef.current.x, contextPointRef.current.y),
            children: /* @__PURE__ */ jsxs8(
              "div",
              {
                ref: editorRef,
                id: "ptd-designer-canvas",
                className: Canvas_default.canvas,
                "data-ptd-region": "paper",
                "data-effective-tool": effectiveTool,
                "aria-label": "\u8BBE\u8BA1\u7EB8\u5F20",
                tabIndex: 0,
                onContextMenu: handleContextMenu,
                onKeyDown: handleContextMenuKeyDown,
                onMouseDown: handleMouseDown,
                onPointerDown: handleDrawPointerDown,
                onPointerMove: handleDrawPointerMove,
                onPointerUp: handleDrawPointerUp,
                onPointerCancel: handleDrawPointerCancel,
                onLostPointerCapture: handleDrawPointerCancel,
                children: [
                  components.map((schema) => /* @__PURE__ */ jsx11(
                    ComponentAdjuster,
                    {
                      schema,
                      isActive: selectedIds.includes(schema.id),
                      isEditing: store.editingComponentId.value === schema.id || store.editingTableCell.value?.componentId === schema.id,
                      outOfBounds: outOfBoundsIds.has(schema.id),
                      editorRef,
                      scale,
                      onMove: handleMove,
                      onMoveEnd: () => editorLineRef.current?.hideLines(),
                      children: /* @__PURE__ */ jsx11(ComponentRenderer, { schema })
                    },
                    schema.id
                  )),
                  previewGeometry && drawSession && /* @__PURE__ */ jsx11(
                    "div",
                    {
                      className: Canvas_default.drawPreview,
                      "data-tool": drawSession.tool,
                      style: previewStyle,
                      "aria-hidden": "true"
                    }
                  ),
                  store.isSelectingArea.value && /* @__PURE__ */ jsx11(Area, { ...area }),
                  /* @__PURE__ */ jsx11(EditorLine, { ref: editorLineRef }),
                  /* @__PURE__ */ jsx11("div", { className: Canvas_default.safeArea, "data-ptd-page-safe-area": true, "aria-hidden": "true" })
                ]
              }
            )
          }
        )
      ] })
    }
  );
}

// src/components/FloatingToolDock/FloatingToolDock.tsx
import {
  useCallback as useCallback9,
  useEffect as useEffect12,
  useId as useId2,
  useRef as useRef12,
  useState as useState10
} from "react";
import { useSignals as useSignals7 } from "@preact/signals-react/runtime";
import * as Tooltip3 from "@radix-ui/react-tooltip";
import {
  RiArrowDownSLine as RiArrowDownSLine2,
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
  RiCursorLine,
  RiHand as RiHand2,
  RiLayoutRightLine as RiLayoutRightLine2,
  RiMore2Line
} from "@remixicon/react";

// src/components/Sidebar/ComponentToolPicker.tsx
import {
  useCallback as useCallback8,
  useEffect as useEffect11,
  useLayoutEffect as useLayoutEffect2,
  useMemo as useMemo4,
  useRef as useRef11,
  useState as useState9
} from "react";
import { createPortal as createPortal2 } from "react-dom";
import { RiCheckLine, RiCloseLine as RiCloseLine3, RiSearchLine } from "@remixicon/react";

// src/components/Sidebar/componentToolPickerGeometry.ts
var PICKER_GUTTER = 8;
var PICKER_MAX_WIDTH = 264;
var PICKER_MIN_WIDTH = 176;
var PICKER_MIN_HEIGHT = 160;
function componentToolPickerGeometry(trigger, bounds, contentHeight) {
  const availableWidth = Math.max(0, bounds.width - PICKER_GUTTER * 2);
  const width = Math.min(PICKER_MAX_WIDTH, Math.max(PICKER_MIN_WIDTH, availableWidth));
  const triggerCenter = (trigger.left + trigger.right) / 2;
  const left = Math.max(
    bounds.left + PICKER_GUTTER,
    Math.min(triggerCenter - width / 2, bounds.right - width - PICKER_GUTTER)
  );
  const maxHeight = Math.max(PICKER_MIN_HEIGHT, trigger.top - bounds.top - PICKER_GUTTER * 2);
  const measuredHeight = Math.min(contentHeight || 480, maxHeight);
  const top = Math.max(bounds.top + PICKER_GUTTER, trigger.top - measuredHeight - PICKER_GUTTER);
  return { left, top, width, maxHeight };
}

// src/components/Sidebar/ComponentToolPicker.module.css
var ComponentToolPicker_default = {
  picker: "ComponentToolPicker_picker",
  header: "ComponentToolPicker_header",
  sectionHeader: "ComponentToolPicker_sectionHeader",
  closeButton: "ComponentToolPicker_closeButton",
  search: "ComponentToolPicker_search",
  body: "ComponentToolPicker_body",
  section: "ComponentToolPicker_section",
  grid: "ComponentToolPicker_grid",
  item: "ComponentToolPicker_item",
  check: "ComponentToolPicker_check",
  emptyState: "ComponentToolPicker_emptyState",
  visuallyHidden: "ComponentToolPicker_visuallyHidden"
};

// src/components/Sidebar/ComponentToolPicker.tsx
import { jsx as jsx12, jsxs as jsxs9 } from "react/jsx-runtime";
function ComponentToolPicker({
  id,
  activeTool,
  recentTypes,
  triggerRef,
  onSelect,
  onClose
}) {
  const pickerRef = useRef11(null);
  const searchRef = useRef11(null);
  const [query, setQuery] = useState9("");
  const [geometry, setGeometry] = useState9(null);
  const headingId = `${id}-heading`;
  const resultId = `${id}-result`;
  const availableItems = useMemo4(() => searchAvailableComponentCatalog(query), [query]);
  const recentItems = useMemo4(
    () => recentTypes.map(findAvailableCatalogItem).filter((item) => Boolean(item)),
    [recentTypes]
  );
  const groupedItems = useMemo4(
    () => catalogGroups.map((group) => ({
      group,
      items: availableItems.filter((item) => item.group === group.id)
    })).filter(({ items }) => items.length > 0),
    [availableItems]
  );
  const updatePosition = useCallback8(() => {
    const trigger = triggerRef.current;
    const picker = pickerRef.current;
    if (!trigger || !picker) return;
    const designer = trigger.closest('[data-ptd-region="designer"]');
    const triggerRect = trigger.getBoundingClientRect();
    const bounds = designer?.getBoundingClientRect() ?? {
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight
    };
    setGeometry(componentToolPickerGeometry(triggerRect, bounds, picker.scrollHeight));
  }, [triggerRef]);
  useLayoutEffect2(() => {
    const frame = requestAnimationFrame(() => {
      updatePosition();
      searchRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [updatePosition]);
  useLayoutEffect2(() => {
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [availableItems.length, recentItems.length, updatePosition]);
  useEffect11(() => {
    const trigger = triggerRef.current;
    const picker = pickerRef.current;
    const designer = trigger?.closest('[data-ptd-region="designer"]');
    const observer = new ResizeObserver(updatePosition);
    if (trigger) observer.observe(trigger);
    if (picker) observer.observe(picker);
    if (designer) observer.observe(designer);
    const closeOnOutsidePointer = (event) => {
      const target = event.target;
      if (pickerRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.pointerType === "mouse") onClose(false);
    };
    const reposition = () => updatePosition();
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [onClose, triggerRef, updatePosition]);
  if (typeof document === "undefined") return null;
  const choose = (item) => {
    onSelect(item);
    onClose(false);
  };
  const handleKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault();
      onClose(true);
      return;
    }
    const target = event.target;
    if (!target.closest("[data-component-type]")) return;
    const items = Array.from(
      event.currentTarget.querySelectorAll("[data-component-type]")
    );
    const currentIndex = items.indexOf(target.closest("button"));
    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (currentIndex + 1 + items.length) % items.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = items.length - 1;
    }
    if (nextIndex === null || items.length === 0) return;
    event.preventDefault();
    items[nextIndex]?.focus();
  };
  const pickerStyle = {
    "--ptd-picker-left": `${geometry?.left ?? 0}px`,
    "--ptd-picker-top": `${geometry?.top ?? 0}px`,
    "--ptd-picker-width": `${geometry?.width ?? 264}px`,
    "--ptd-picker-max-height": `${geometry?.maxHeight ?? 480}px`
  };
  return createPortal2(
    /* @__PURE__ */ jsxs9(
      "div",
      {
        ref: pickerRef,
        id,
        className: `${ComponentToolPicker_default.picker} ${ptdThemeClass}`,
        style: pickerStyle,
        role: "dialog",
        "aria-modal": "false",
        "aria-labelledby": headingId,
        "data-positioned": Boolean(geometry) || void 0,
        "data-ptd-region": "component-tool-picker",
        "data-ptd-editor-interactive": "true",
        onKeyDown: handleKeyDown,
        onPointerDown: (event) => event.stopPropagation(),
        children: [
          /* @__PURE__ */ jsxs9("header", { className: ComponentToolPicker_default.header, children: [
            /* @__PURE__ */ jsxs9("span", { children: [
              /* @__PURE__ */ jsx12("strong", { id: headingId, children: "\u7EC4\u4EF6\u5DE5\u5177" }),
              /* @__PURE__ */ jsxs9("small", { children: [
                availableItems.length,
                " \u9879\u53EF\u7528"
              ] })
            ] }),
            /* @__PURE__ */ jsx12(
              "button",
              {
                type: "button",
                className: ComponentToolPicker_default.closeButton,
                "aria-label": "\u5173\u95ED\u7EC4\u4EF6\u5DE5\u5177\u9009\u62E9\u5668",
                onClick: () => onClose(true),
                children: /* @__PURE__ */ jsx12(RiCloseLine3, { "aria-hidden": "true" })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs9("label", { className: ComponentToolPicker_default.search, children: [
            /* @__PURE__ */ jsx12(RiSearchLine, { "aria-hidden": "true" }),
            /* @__PURE__ */ jsx12("span", { className: ComponentToolPicker_default.visuallyHidden, children: "\u641C\u7D22\u7EC4\u4EF6" }),
            /* @__PURE__ */ jsx12(
              "input",
              {
                ref: searchRef,
                type: "search",
                value: query,
                placeholder: "\u641C\u7D22\u540D\u79F0\u3001\u7C7B\u578B\u6216\u7528\u9014",
                "aria-describedby": resultId,
                onChange: (event) => setQuery(event.target.value)
              }
            )
          ] }),
          /* @__PURE__ */ jsxs9("span", { id: resultId, className: ComponentToolPicker_default.visuallyHidden, "aria-live": "polite", children: [
            "\u627E\u5230 ",
            availableItems.length,
            " \u4E2A\u53EF\u7528\u7EC4\u4EF6"
          ] }),
          /* @__PURE__ */ jsxs9("div", { className: ComponentToolPicker_default.body, children: [
            !query.trim() && recentItems.length > 0 && /* @__PURE__ */ jsx12(PickerSection, { title: "\u6700\u8FD1\u4F7F\u7528", meta: `${recentItems.length} \u9879`, children: /* @__PURE__ */ jsx12("div", { className: ComponentToolPicker_default.grid, children: recentItems.map((item) => /* @__PURE__ */ jsx12(
              PickerItem,
              {
                item,
                active: activeTool === item.type,
                onSelect: () => choose(item)
              },
              `recent-${item.id}`
            )) }) }),
            groupedItems.map(({ group, items }) => /* @__PURE__ */ jsx12(PickerSection, { title: group.name, meta: `${items.length} \u9879`, children: /* @__PURE__ */ jsx12("div", { className: ComponentToolPicker_default.grid, children: items.map((item) => /* @__PURE__ */ jsx12(
              PickerItem,
              {
                item,
                active: activeTool === item.type,
                onSelect: () => choose(item)
              },
              item.id
            )) }) }, group.id)),
            availableItems.length === 0 && /* @__PURE__ */ jsxs9("div", { className: ComponentToolPicker_default.emptyState, children: [
              /* @__PURE__ */ jsx12("strong", { children: "\u6CA1\u6709\u5339\u914D\u7684\u7EC4\u4EF6" }),
              /* @__PURE__ */ jsx12("span", { children: "\u6362\u4E00\u4E2A\u540D\u79F0\u3001\u7C7B\u578B\u6216\u4F7F\u7528\u573A\u666F\u8BD5\u8BD5\u3002" }),
              /* @__PURE__ */ jsx12("button", { type: "button", onClick: () => setQuery(""), children: "\u6E05\u9664\u641C\u7D22" })
            ] })
          ] })
        ]
      }
    ),
    document.body
  );
}
function PickerSection({
  title,
  meta,
  children
}) {
  return /* @__PURE__ */ jsxs9("section", { className: ComponentToolPicker_default.section, children: [
    /* @__PURE__ */ jsxs9("header", { className: ComponentToolPicker_default.sectionHeader, children: [
      /* @__PURE__ */ jsx12("strong", { children: title }),
      /* @__PURE__ */ jsx12("span", { children: meta })
    ] }),
    children
  ] });
}
function PickerItem({
  item,
  active,
  onSelect
}) {
  const Icon = item.icon;
  return /* @__PURE__ */ jsxs9(
    "button",
    {
      type: "button",
      className: ComponentToolPicker_default.item,
      "data-component-type": item.type,
      "aria-pressed": active,
      "aria-label": `${item.name}\uFF1A${item.description}`,
      title: item.description,
      onClick: onSelect,
      children: [
        /* @__PURE__ */ jsx12(Icon, { "aria-hidden": "true" }),
        /* @__PURE__ */ jsx12("span", { children: item.name }),
        active && /* @__PURE__ */ jsx12(RiCheckLine, { className: ComponentToolPicker_default.check, "aria-label": "\u5F53\u524D\u5DE5\u5177" })
      ]
    }
  );
}

// src/components/Toolbar/Toolbar.tsx
import { useSignals as useSignals6 } from "@preact/signals-react/runtime";
import * as Tooltip2 from "@radix-ui/react-tooltip";
import { formatMeasurement as formatMeasurement3, getPageDimensions as getPageDimensions3, mmToPx as mmToPx4 } from "@ptd/core";
import {
  RiAlignItemBottomLine,
  RiAlignItemHorizontalCenterLine,
  RiAlignItemLeftLine,
  RiAlignItemRightLine,
  RiAlignItemTopLine,
  RiAlignItemVerticalCenterLine,
  RiClipboardLine as RiClipboardLine2,
  RiContractLeftRightLine,
  RiContractUpDownLine,
  RiDeleteBinLine as RiDeleteBinLine4,
  RiGroupLine as RiGroupLine2,
  RiHand,
  RiLandscapeLine,
  RiLockLine as RiLockLine3,
  RiLockUnlockLine as RiLockUnlockLine4,
  RiRuler2Line
} from "@remixicon/react";

// src/components/Toolbar/toolGuidance.ts
function getToolGuidance(tool, temporaryHand = false) {
  if (tool === "select") return null;
  if (tool === "hand") {
    return {
      tool: "hand",
      kind: "HAND",
      name: temporaryHand ? "\u4E34\u65F6\u6293\u624B\u5DE5\u5177" : "\u6293\u624B\u5DE5\u5177",
      instruction: "\u62D6\u52A8\u753B\u5E03\u5E73\u79FB",
      shiftHint: null,
      secondaryHint: temporaryHand ? { key: "Space", label: "\u677E\u5F00\u8FD4\u56DE" } : { key: "Space", label: "\u4E34\u65F6\u6293\u624B" },
      escapeHint: "\u9009\u62E9"
    };
  }
  const item = findAvailableCatalogItem(tool);
  if (!item) return null;
  const isText = tool === "RoySimpleText" || tool === "RoyText";
  const isShape = isDrawingComponentType(tool);
  const name = tool === "RoySimpleText" ? "\u6587\u672C\u6846\u5DE5\u5177" : `${item.name}\u5DE5\u5177`;
  return {
    tool,
    kind: isText ? "TEXT" : isShape ? "DRAW" : "FRAME",
    name,
    instruction: `\u62D6\u52A8\u753B\u5E03\u521B\u5EFA${isText ? "\u6587\u672C\u6846" : item.name}`,
    shiftHint: isShape && tool !== "RoyLine" ? "\u7B49\u6BD4" : null,
    secondaryHint: null,
    escapeHint: "\u53D6\u6D88"
  };
}

// src/components/Toolbar/Toolbar.module.css
var Toolbar_default = {
  toolbar: "Toolbar_toolbar",
  context: "Toolbar_context",
  group: "Toolbar_group",
  metrics: "Toolbar_metrics",
  guideColors: "Toolbar_guideColors",
  toolGuidance: "Toolbar_toolGuidance",
  contextIdentity: "Toolbar_contextIdentity",
  rule: "Toolbar_rule",
  toolButton: "Toolbar_toolButton",
  guideColor: "Toolbar_guideColor",
  portraitIcon: "Toolbar_portraitIcon",
  metric: "Toolbar_metric",
  pageMetric: "Toolbar_pageMetric",
  tooltip: "Toolbar_tooltip",
  tooltipArrow: "Toolbar_tooltipArrow",
  contextName: "Toolbar_contextName",
  secondaryAction: "Toolbar_secondaryAction"
};

// src/components/Toolbar/Toolbar.tsx
import { Fragment as Fragment4, jsx as jsx13, jsxs as jsxs10 } from "react/jsx-runtime";
var GUIDE_COLORS = [
  { value: "cobalt", label: "\u58A8\u84DD\u53C2\u8003\u7EBF" },
  { value: "vermilion", label: "\u6731\u7EA2\u53C2\u8003\u7EBF" },
  { value: "emerald", label: "\u7FE0\u7EFF\u53C2\u8003\u7EBF" },
  { value: "amber", label: "\u7425\u73C0\u53C2\u8003\u7EBF" }
];
function ToolButton({
  label,
  shortcut,
  children,
  onClick,
  disabled = false,
  pressed,
  danger,
  secondary
}) {
  return /* @__PURE__ */ jsxs10(Tooltip2.Root, { children: [
    /* @__PURE__ */ jsx13(Tooltip2.Trigger, { asChild: true, children: /* @__PURE__ */ jsx13(
      "button",
      {
        type: "button",
        className: `${Toolbar_default.toolButton} ${secondary ? Toolbar_default.secondaryAction : ""}`,
        "aria-label": label,
        "aria-pressed": pressed,
        "data-danger": danger || void 0,
        disabled,
        onClick,
        children
      }
    ) }),
    /* @__PURE__ */ jsx13(Tooltip2.Portal, { children: /* @__PURE__ */ jsxs10(Tooltip2.Content, { className: `${Toolbar_default.tooltip} ${ptdThemeClass}`, sideOffset: 7, children: [
      /* @__PURE__ */ jsx13("span", { children: label }),
      shortcut && /* @__PURE__ */ jsx13("kbd", { children: shortcut }),
      /* @__PURE__ */ jsx13(Tooltip2.Arrow, { className: Toolbar_default.tooltipArrow })
    ] }) })
  ] });
}
function GuideColorButton({
  color: color2,
  label,
  active
}) {
  const store = useEditorStore();
  return /* @__PURE__ */ jsxs10(Tooltip2.Root, { children: [
    /* @__PURE__ */ jsx13(Tooltip2.Trigger, { asChild: true, children: /* @__PURE__ */ jsx13(
      "button",
      {
        type: "button",
        className: Toolbar_default.guideColor,
        "data-color": color2,
        "data-active": active || void 0,
        "aria-label": label,
        "aria-pressed": active,
        onClick: () => store.setGuideColor(color2),
        children: /* @__PURE__ */ jsx13("span", {})
      }
    ) }),
    /* @__PURE__ */ jsx13(Tooltip2.Portal, { children: /* @__PURE__ */ jsxs10(Tooltip2.Content, { className: `${Toolbar_default.tooltip} ${ptdThemeClass}`, sideOffset: 7, children: [
      label,
      /* @__PURE__ */ jsx13(Tooltip2.Arrow, { className: Toolbar_default.tooltipArrow })
    ] }) })
  ] });
}
function Toolbar() {
  useSignals6();
  const store = useEditorStore();
  const selected = store.selectedComponents.value;
  const selectedGuide = store.guides.value.find((guide) => guide.id === store.selectedGuideId.value);
  const toolGuidance = getToolGuidance(store.effectiveTool.value, store.temporaryHand.value);
  return /* @__PURE__ */ jsx13(Tooltip2.Provider, { delayDuration: 400, skipDelayDuration: 120, children: /* @__PURE__ */ jsx13("nav", { className: Toolbar_default.toolbar, "aria-label": "\u5F53\u524D\u4E0A\u4E0B\u6587\u547D\u4EE4", "data-ptd-region": "context-shelf", children: /* @__PURE__ */ jsx13("div", { className: Toolbar_default.context, children: toolGuidance ? /* @__PURE__ */ jsx13(ActiveToolContext, { guidance: toolGuidance }) : selectedGuide ? /* @__PURE__ */ jsx13(GuideContext, {}) : selected.length > 1 ? /* @__PURE__ */ jsx13(MultiContext, {}) : selected.length === 1 ? /* @__PURE__ */ jsx13(SingleContext, {}) : /* @__PURE__ */ jsx13(PageContext, {}) }) }) });
}
function ActiveToolContext({ guidance }) {
  const item = guidance.tool === "hand" ? null : findAvailableCatalogItem(guidance.tool);
  const Icon = item?.icon;
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx13(ContextIdentity, { kind: guidance.kind, name: guidance.name }),
    /* @__PURE__ */ jsx13("span", { className: Toolbar_default.rule }),
    /* @__PURE__ */ jsxs10("div", { className: Toolbar_default.toolGuidance, "aria-label": `${guidance.name}\u64CD\u4F5C\u63D0\u793A`, children: [
      Icon ? /* @__PURE__ */ jsx13(Icon, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx13(RiHand, { "aria-hidden": "true" }),
      /* @__PURE__ */ jsx13("strong", { children: guidance.instruction }),
      guidance.shiftHint && /* @__PURE__ */ jsxs10("span", { children: [
        /* @__PURE__ */ jsx13("kbd", { children: "Shift" }),
        " ",
        guidance.shiftHint
      ] }),
      guidance.secondaryHint && /* @__PURE__ */ jsxs10("span", { children: [
        /* @__PURE__ */ jsx13("kbd", { children: guidance.secondaryHint.key }),
        " ",
        guidance.secondaryHint.label
      ] }),
      /* @__PURE__ */ jsxs10("span", { children: [
        /* @__PURE__ */ jsx13("kbd", { children: "Esc" }),
        " ",
        guidance.escapeHint
      ] })
    ] })
  ] });
}
function ContextIdentity({
  kind,
  name,
  compactPersistent = false
}) {
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: Toolbar_default.contextIdentity,
      "data-compact-persistent": compactPersistent || void 0,
      children: [
        kind && /* @__PURE__ */ jsx13("span", { children: kind }),
        /* @__PURE__ */ jsx13("strong", { className: Toolbar_default.contextName, children: name })
      ]
    }
  );
}
function PageContext() {
  const store = useEditorStore();
  const page = store.pageConfig.value;
  const dimensions = getPageDimensions3(page);
  const unit = store.measurementUnit.value;
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx13(ContextIdentity, { kind: "PAGE", name: page.title || "\u672A\u547D\u540D\u6A21\u677F" }),
    /* @__PURE__ */ jsx13("span", { className: Toolbar_default.rule }),
    /* @__PURE__ */ jsxs10("div", { className: Toolbar_default.group, "aria-label": "\u9875\u9762\u65B9\u5411", children: [
      /* @__PURE__ */ jsx13(
        ToolButton,
        {
          label: "\u7EB5\u5411\u9875\u9762",
          pressed: page.pageDirection === "p",
          onClick: () => store.setPageDirection("p"),
          children: /* @__PURE__ */ jsx13(RiLandscapeLine, { className: Toolbar_default.portraitIcon })
        }
      ),
      /* @__PURE__ */ jsx13(
        ToolButton,
        {
          label: "\u6A2A\u5411\u9875\u9762",
          pressed: page.pageDirection === "l",
          onClick: () => store.setPageDirection("l"),
          children: /* @__PURE__ */ jsx13(RiLandscapeLine, {})
        }
      )
    ] }),
    /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label: "\u663E\u793A\u6216\u9690\u85CF\u6807\u5C3A",
        pressed: store.showRuler.value,
        onClick: () => store.toggleRuler(),
        children: /* @__PURE__ */ jsx13(RiRuler2Line, {})
      }
    ),
    store.clipboard.value && /* @__PURE__ */ jsx13(ToolButton, { label: "\u7C98\u8D34\u7EC4\u4EF6", shortcut: "Ctrl V", onClick: () => store.paste(), secondary: true, children: /* @__PURE__ */ jsx13(RiClipboardLine2, {}) }),
    /* @__PURE__ */ jsxs10("span", { className: Toolbar_default.pageMetric, children: [
      formatMeasurement3(dimensions.width, unit),
      " \xD7 ",
      formatMeasurement3(dimensions.height, unit),
      " ",
      unit
    ] })
  ] });
}
function SingleContext() {
  const store = useEditorStore();
  const component = store.primaryComponent.value;
  const catalogItem = findAvailableCatalogItem(component.component);
  const style = component.style;
  const componentTypeName = component.component === "RoyGroup" ? "\u7EC4\u5408" : catalogItem?.name ?? "\u7EC4\u4EF6";
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx13(ContextIdentity, { name: componentTypeName, compactPersistent: true }),
    /* @__PURE__ */ jsxs10("div", { className: Toolbar_default.metrics, "aria-label": "\u7EC4\u4EF6\u51E0\u4F55", children: [
      /* @__PURE__ */ jsx13(Metric, { label: "X", value: style.left }),
      /* @__PURE__ */ jsx13(Metric, { label: "Y", value: style.top }),
      /* @__PURE__ */ jsx13(Metric, { label: "W", value: style.width }),
      /* @__PURE__ */ jsx13(Metric, { label: "H", value: style.height })
    ] })
  ] });
}
function Metric({ label, value }) {
  const store = useEditorStore();
  const shown = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return /* @__PURE__ */ jsxs10("span", { className: Toolbar_default.metric, children: [
    /* @__PURE__ */ jsx13("small", { children: label }),
    formatMeasurement3(shown, store.measurementUnit.value)
  ] });
}
function MultiContext() {
  const store = useEditorStore();
  const selected = store.selectedComponents.value;
  const locked = selected.some((component) => component.isLock);
  const alignments = [
    ["left", "\u5DE6\u5BF9\u9F50", /* @__PURE__ */ jsx13(RiAlignItemLeftLine, {}, "left")],
    ["center", "\u6C34\u5E73\u5C45\u4E2D", /* @__PURE__ */ jsx13(RiAlignItemHorizontalCenterLine, {}, "center")],
    ["right", "\u53F3\u5BF9\u9F50", /* @__PURE__ */ jsx13(RiAlignItemRightLine, {}, "right")],
    ["top", "\u9876\u90E8\u5BF9\u9F50", /* @__PURE__ */ jsx13(RiAlignItemTopLine, {}, "top")],
    ["middle", "\u5782\u76F4\u5C45\u4E2D", /* @__PURE__ */ jsx13(RiAlignItemVerticalCenterLine, {}, "middle")],
    ["bottom", "\u5E95\u90E8\u5BF9\u9F50", /* @__PURE__ */ jsx13(RiAlignItemBottomLine, {}, "bottom")]
  ];
  const distributions = [
    ["horizontal", "\u6C34\u5E73\u7B49\u8DDD\u5206\u5E03", /* @__PURE__ */ jsx13(RiContractLeftRightLine, {}, "horizontal")],
    ["vertical", "\u5782\u76F4\u7B49\u8DDD\u5206\u5E03", /* @__PURE__ */ jsx13(RiContractUpDownLine, {}, "vertical")]
  ];
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx13(ContextIdentity, { kind: "MULTI", name: `${selected.length} \u4E2A\u5BF9\u8C61` }),
    /* @__PURE__ */ jsx13("span", { className: Toolbar_default.rule }),
    /* @__PURE__ */ jsx13("div", { className: Toolbar_default.group, "aria-label": "\u5BF9\u9F50", children: alignments.map(([alignment, label, icon]) => /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label,
        disabled: locked,
        onClick: () => store.align(alignment),
        children: icon
      },
      alignment
    )) }),
    /* @__PURE__ */ jsx13("div", { className: Toolbar_default.group, "aria-label": "\u5206\u5E03", children: distributions.map(([direction, label, icon]) => /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label,
        disabled: locked || selected.length < 3,
        onClick: () => store.distribute(direction),
        secondary: true,
        children: icon
      },
      direction
    )) }),
    /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label: "\u7EC4\u5408\u6240\u9009\u5BF9\u8C61",
        shortcut: "Ctrl G",
        disabled: locked,
        onClick: () => store.group(),
        children: /* @__PURE__ */ jsx13(RiGroupLine2, {})
      }
    ),
    /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label: `\u5220\u9664 ${selected.length} \u4E2A\u7EC4\u4EF6`,
        danger: true,
        disabled: locked,
        onClick: () => store.deleteSelected(),
        children: /* @__PURE__ */ jsx13(RiDeleteBinLine4, {})
      }
    )
  ] });
}
function GuideContext() {
  const store = useEditorStore();
  const guide = store.guides.value.find((item) => item.id === store.selectedGuideId.value);
  const unit = store.measurementUnit.value;
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx13(
      ContextIdentity,
      {
        kind: "GUIDE",
        name: `${guide.axis.toUpperCase()} ${formatMeasurement3(mmToPx4(guide.positionMm), unit)} ${unit}`
      }
    ),
    /* @__PURE__ */ jsx13("div", { className: Toolbar_default.guideColors, "aria-label": "\u53C2\u8003\u7EBF\u989C\u8272", children: GUIDE_COLORS.map((item) => /* @__PURE__ */ jsx13(
      GuideColorButton,
      {
        color: item.value,
        label: item.label,
        active: guide.color === item.value
      },
      item.value
    )) }),
    /* @__PURE__ */ jsx13("span", { className: Toolbar_default.rule }),
    /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label: store.guidesLocked.value ? "\u89E3\u9501\u53C2\u8003\u7EBF" : "\u9501\u5B9A\u53C2\u8003\u7EBF",
        pressed: store.guidesLocked.value,
        onClick: () => store.toggleGuidesLocked(),
        children: store.guidesLocked.value ? /* @__PURE__ */ jsx13(RiLockUnlockLine4, {}) : /* @__PURE__ */ jsx13(RiLockLine3, {})
      }
    ),
    /* @__PURE__ */ jsx13(
      ToolButton,
      {
        label: "\u5220\u9664\u53C2\u8003\u7EBF",
        shortcut: "Delete",
        danger: true,
        disabled: store.guidesLocked.value,
        onClick: () => store.removeSelectedGuide(),
        children: /* @__PURE__ */ jsx13(RiDeleteBinLine4, {})
      }
    )
  ] });
}

// src/components/FloatingToolDock/FloatingToolDock.module.css
var FloatingToolDock_default = {
  floatingDock: "FloatingToolDock_floatingDock",
  mainDock: "FloatingToolDock_mainDock",
  group: "FloatingToolDock_group",
  separator: "FloatingToolDock_separator",
  dockButton: "FloatingToolDock_dockButton",
  groupedToolDisclosure: "FloatingToolDock_groupedToolDisclosure",
  groupedToolMenu: "FloatingToolDock_groupedToolMenu",
  moreButton: "FloatingToolDock_moreButton",
  groupedTool: "FloatingToolDock_groupedTool",
  groupedToolPrimary: "FloatingToolDock_groupedToolPrimary",
  groupedToolMenuTitle: "FloatingToolDock_groupedToolMenuTitle",
  tooltip: "FloatingToolDock_tooltip",
  tooltipArrow: "FloatingToolDock_tooltipArrow"
};

// src/components/FloatingToolDock/FloatingToolDock.tsx
import { jsx as jsx14, jsxs as jsxs11 } from "react/jsx-runtime";
var DOCK_COMPONENT_TOOL_TYPES = [
  "RoyImage",
  "RoySimpleTable"
];
var DRAW_TOOL_TYPES = [
  "RoyLine",
  "RoyRect",
  "RoyCircle",
  "RoyStar"
];
var TEXT_TOOL_TYPES = [
  "RoySimpleText",
  "RoyText"
];
function isTextToolType(type) {
  return type === "RoySimpleText" || type === "RoyText";
}
function FloatingToolDock({
  mode,
  inspectorOpen,
  onToggleInspector
}) {
  useSignals7();
  const store = useEditorStore();
  const moreButtonRef = useRef12(null);
  const pickerId = useId2();
  const [lastTextTool, setLastTextTool] = useState10("RoySimpleText");
  const [pickerOpen, setPickerOpen] = useState10(false);
  const [recentTypes, setRecentTypes] = useState10([]);
  const activeTool = store.activeTool.value;
  const effectiveTool = store.effectiveTool.value;
  const textTools = TEXT_TOOL_TYPES.map(findAvailableCatalogItem).filter(
    (item) => Boolean(item)
  );
  const dockComponentTools = DOCK_COMPONENT_TOOL_TYPES.map(findAvailableCatalogItem).filter(
    (item) => Boolean(item)
  );
  const pickerDetailActive = activeTool === "RoyQRCode" || activeTool === "RoyBarCode" || mode === "compact" && (activeTool === "RoyImage" || activeTool === "RoySimpleTable");
  const activate = (item, remember = false) => {
    if (!isDrawnComponentType(item.type)) return;
    store.setActiveTool(item.type);
    if (isTextToolType(item.type)) setLastTextTool(item.type);
    if (remember) {
      setRecentTypes((current) => rememberRecentComponentType(current, item.type));
    }
  };
  const closePicker = useCallback9((restoreFocus) => {
    setPickerOpen(false);
    if (restoreFocus) requestAnimationFrame(() => moreButtonRef.current?.focus());
  }, []);
  return /* @__PURE__ */ jsxs11("div", { className: FloatingToolDock_default.floatingDock, "data-mode": mode, "data-ptd-region": "floating-tool-dock", children: [
    /* @__PURE__ */ jsx14(Toolbar, {}),
    /* @__PURE__ */ jsxs11(Tooltip3.Provider, { delayDuration: 400, skipDelayDuration: 120, children: [
      /* @__PURE__ */ jsxs11("nav", { className: FloatingToolDock_default.mainDock, "aria-label": "\u753B\u5E03\u5DE5\u5177\u4E0E\u5386\u53F2", children: [
        /* @__PURE__ */ jsxs11("div", { className: FloatingToolDock_default.group, role: "group", "aria-label": "\u5386\u53F2", children: [
          /* @__PURE__ */ jsx14(
            DockButton,
            {
              label: "\u64A4\u9500",
              shortcut: "Ctrl Z",
              disabled: !store.canUndo.value,
              onClick: () => store.undo(),
              children: /* @__PURE__ */ jsx14(RiArrowGoBackLine, {})
            }
          ),
          /* @__PURE__ */ jsx14(
            DockButton,
            {
              label: "\u91CD\u505A",
              shortcut: "Ctrl Y",
              disabled: !store.canRedo.value,
              onClick: () => store.redo(),
              children: /* @__PURE__ */ jsx14(RiArrowGoForwardLine, {})
            }
          )
        ] }),
        /* @__PURE__ */ jsx14("span", { className: FloatingToolDock_default.separator, "aria-hidden": "true" }),
        /* @__PURE__ */ jsxs11("div", { className: FloatingToolDock_default.group, role: "group", "aria-label": "\u4EA4\u4E92\u5DE5\u5177", children: [
          /* @__PURE__ */ jsx14(
            DockButton,
            {
              label: "\u9009\u62E9\u5DE5\u5177",
              shortcut: "V",
              pressed: effectiveTool === "select",
              onClick: () => store.setActiveTool("select"),
              children: /* @__PURE__ */ jsx14(RiCursorLine, {})
            }
          ),
          /* @__PURE__ */ jsx14(
            DockButton,
            {
              label: "\u6293\u624B\u5DE5\u5177",
              shortcut: "H",
              pressed: effectiveTool === "hand",
              onClick: () => store.setActiveTool("hand"),
              children: /* @__PURE__ */ jsx14(RiHand2, {})
            }
          )
        ] }),
        /* @__PURE__ */ jsx14("span", { className: FloatingToolDock_default.separator, "aria-hidden": "true" }),
        /* @__PURE__ */ jsxs11("div", { className: FloatingToolDock_default.group, role: "group", "aria-label": "\u521B\u5EFA\u5DE5\u5177", children: [
          textTools.length > 0 && /* @__PURE__ */ jsx14(
            GroupedDockTool,
            {
              groupLabel: "\u6587\u672C\u5DE5\u5177",
              activeType: lastTextTool,
              items: textTools,
              pressed: isTextToolType(effectiveTool),
              onSelect: (type) => {
                const item = findAvailableCatalogItem(type);
                if (item) activate(item);
              }
            }
          ),
          /* @__PURE__ */ jsx14(ShapeToolGroup, {}),
          dockComponentTools.map((item) => {
            const Icon = item.icon;
            return /* @__PURE__ */ jsx14(
              DockButton,
              {
                label: `${item.name}\u5DE5\u5177`,
                compactOptional: true,
                pressed: effectiveTool === item.type,
                onClick: () => activate(item),
                children: /* @__PURE__ */ jsx14(Icon, {})
              },
              item.type
            );
          }),
          /* @__PURE__ */ jsx14(
            DockButton,
            {
              buttonRef: moreButtonRef,
              label: pickerDetailActive ? `\u66F4\u591A\u7EC4\u4EF6\uFF0C\u5F53\u524D\u4E3A${findAvailableCatalogItem(activeTool)?.name ?? "\u7EC4\u4EF6"}\u5DE5\u5177` : "\u66F4\u591A\u7EC4\u4EF6",
              className: FloatingToolDock_default.moreButton,
              expanded: pickerOpen,
              controls: pickerId,
              popupKind: "dialog",
              detailActive: pickerDetailActive,
              onClick: () => setPickerOpen((current) => !current),
              children: /* @__PURE__ */ jsx14(RiMore2Line, {})
            }
          )
        ] }),
        /* @__PURE__ */ jsx14("span", { className: FloatingToolDock_default.separator, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx14("div", { className: FloatingToolDock_default.group, role: "group", "aria-label": "\u5DE5\u4F5C\u533A", children: /* @__PURE__ */ jsx14(
          DockButton,
          {
            label: inspectorOpen ? "\u6536\u8D77\u5C5E\u6027\u9762\u677F" : "\u6253\u5F00\u5C5E\u6027\u9762\u677F",
            pressed: inspectorOpen,
            onClick: onToggleInspector,
            children: /* @__PURE__ */ jsx14(RiLayoutRightLine2, {})
          }
        ) })
      ] }),
      pickerOpen && /* @__PURE__ */ jsx14(
        ComponentToolPicker,
        {
          id: pickerId,
          activeTool,
          recentTypes,
          triggerRef: moreButtonRef,
          onSelect: (item) => activate(item, true),
          onClose: closePicker
        }
      )
    ] })
  ] });
}
function DockButton({
  label,
  children,
  onClick,
  buttonRef,
  shortcut,
  disabled = false,
  pressed,
  expanded,
  controls,
  popupKind,
  detailActive,
  compactOptional,
  className
}) {
  return /* @__PURE__ */ jsxs11(Tooltip3.Root, { children: [
    /* @__PURE__ */ jsx14(Tooltip3.Trigger, { asChild: true, children: /* @__PURE__ */ jsx14(
      "button",
      {
        ref: buttonRef,
        type: "button",
        className: `${FloatingToolDock_default.dockButton} ${className ?? ""}`,
        "aria-label": label,
        "aria-pressed": pressed,
        "aria-expanded": expanded,
        "aria-controls": controls,
        "aria-haspopup": popupKind,
        "data-detail-active": detailActive || void 0,
        "data-compact-optional": compactOptional || void 0,
        disabled,
        onClick,
        children
      }
    ) }),
    /* @__PURE__ */ jsx14(Tooltip3.Portal, { children: /* @__PURE__ */ jsxs11(Tooltip3.Content, { className: `${FloatingToolDock_default.tooltip} ${ptdThemeClass}`, side: "top", sideOffset: 9, children: [
      /* @__PURE__ */ jsx14("span", { children: label }),
      shortcut && /* @__PURE__ */ jsx14("kbd", { children: shortcut }),
      /* @__PURE__ */ jsx14(Tooltip3.Arrow, { className: FloatingToolDock_default.tooltipArrow })
    ] }) })
  ] });
}
function ShapeToolGroup() {
  useSignals7();
  const store = useEditorStore();
  const activeShape = isDrawingComponentType(store.activeTool.value) ? store.activeTool.value : store.lastDrawingTool.value;
  const shapeItems = DRAW_TOOL_TYPES.map(findAvailableCatalogItem).filter(
    (item) => Boolean(item)
  );
  return /* @__PURE__ */ jsx14(
    GroupedDockTool,
    {
      groupLabel: "\u56FE\u5F62\u5DE5\u5177",
      activeType: activeShape,
      items: shapeItems,
      pressed: isDrawingComponentType(store.effectiveTool.value),
      onSelect: (type) => {
        if (isDrawingComponentType(type)) store.setActiveTool(type);
      }
    }
  );
}
function GroupedDockTool({
  groupLabel,
  activeType,
  items,
  pressed,
  onSelect
}) {
  const menuId = useId2();
  const [open, setOpen] = useState10(false);
  const rootRef = useRef12(null);
  const disclosureRef = useRef12(null);
  const menuRef = useRef12(null);
  const activeItem = findAvailableCatalogItem(activeType);
  useEffect12(() => {
    if (!open) return;
    const selected = menuRef.current?.querySelector(
      `[data-tool-type="${activeType}"]`
    );
    selected?.focus();
    const closeOnOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [activeType, open]);
  if (!activeItem) return null;
  const ActiveIcon = activeItem.icon;
  const handleMenuKeyDown = (event) => {
    const menuItems = Array.from(
      event.currentTarget.querySelectorAll('[role="menuitemradio"]')
    );
    const currentIndex = menuItems.indexOf(document.activeElement);
    if ((event.key === "Enter" || event.key === " ") && currentIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();
      menuItems[currentIndex]?.click();
      return;
    }
    let nextIndex = null;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1 + menuItems.length) % menuItems.length;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = menuItems.length - 1;
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      disclosureRef.current?.focus();
      return;
    }
    if (nextIndex === null || menuItems.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    menuItems[nextIndex]?.focus();
  };
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      ref: rootRef,
      className: FloatingToolDock_default.groupedTool,
      role: "group",
      "aria-label": groupLabel,
      "data-active-tool": pressed || void 0,
      children: [
        /* @__PURE__ */ jsx14(
          DockButton,
          {
            className: FloatingToolDock_default.groupedToolPrimary,
            label: `\u4F7F\u7528${activeItem.name}\u5DE5\u5177`,
            pressed,
            onClick: () => onSelect(activeType),
            children: /* @__PURE__ */ jsx14(ActiveIcon, {})
          }
        ),
        /* @__PURE__ */ jsxs11(Tooltip3.Root, { children: [
          /* @__PURE__ */ jsx14(Tooltip3.Trigger, { asChild: true, children: /* @__PURE__ */ jsx14(
            "button",
            {
              ref: disclosureRef,
              type: "button",
              className: FloatingToolDock_default.groupedToolDisclosure,
              "aria-label": `\u9009\u62E9${groupLabel}`,
              "aria-haspopup": "menu",
              "aria-expanded": open,
              "aria-controls": menuId,
              onClick: () => setOpen((current) => !current),
              children: /* @__PURE__ */ jsx14(RiArrowDownSLine2, { "aria-hidden": "true" })
            }
          ) }),
          /* @__PURE__ */ jsx14(Tooltip3.Portal, { children: /* @__PURE__ */ jsxs11(
            Tooltip3.Content,
            {
              className: `${FloatingToolDock_default.tooltip} ${ptdThemeClass}`,
              side: "top",
              sideOffset: 9,
              children: [
                `\u9009\u62E9${groupLabel}`,
                /* @__PURE__ */ jsx14(Tooltip3.Arrow, { className: FloatingToolDock_default.tooltipArrow })
              ]
            }
          ) })
        ] }),
        open && /* @__PURE__ */ jsxs11(
          "div",
          {
            ref: menuRef,
            id: menuId,
            className: FloatingToolDock_default.groupedToolMenu,
            role: "menu",
            "aria-label": `\u9009\u62E9${groupLabel}`,
            "data-ptd-editor-interactive": "true",
            onKeyDown: handleMenuKeyDown,
            children: [
              /* @__PURE__ */ jsx14("span", { className: FloatingToolDock_default.groupedToolMenuTitle, children: groupLabel }),
              items.map((item) => {
                const Icon = item.icon;
                return /* @__PURE__ */ jsxs11(
                  "button",
                  {
                    type: "button",
                    role: "menuitemradio",
                    "aria-checked": activeType === item.type,
                    "data-tool-type": item.type,
                    onClick: () => {
                      onSelect(item.type);
                      setOpen(false);
                    },
                    children: [
                      /* @__PURE__ */ jsx14(Icon, { "aria-hidden": "true" }),
                      /* @__PURE__ */ jsx14("span", { children: item.name })
                    ]
                  },
                  item.type
                );
              })
            ]
          }
        )
      ]
    }
  );
}

// src/components/PropertyInspector/PropertyInspector.tsx
import { useEffect as useEffect14, useState as useState12 } from "react";
import { useSignals as useSignals10 } from "@preact/signals-react/runtime";
import {
  BAR_CODE_FORMATS,
  DEFAULT_PAGE_CONFIG,
  PAGE_SIZES,
  barCodeContentError,
  formatMeasurement as formatMeasurement4,
  getPageDimensions as getPageDimensions4,
  imageSourceError,
  mmToPx as mmToPx5,
  normalizeBarCodeProps as normalizeBarCodeProps2,
  normalizeImageProps,
  normalizeQRCodeProps as normalizeQRCodeProps2,
  qrCodeContentError,
  pxToMm as pxToMm2
} from "@ptd/core";
import { RiDeleteBinLine as RiDeleteBinLine5, RiLandscapeLine as RiLandscapeLine2, RiRuler2Line as RiRuler2Line2, RiUpload2Line } from "@remixicon/react";

// src/components/Panel/Panel.module.css
var Panel_default = {
  root: "Panel_root",
  header: "Panel_header",
  tools: "Panel_tools",
  body: "Panel_body",
  footer: "Panel_footer"
};

// src/components/Panel/Panel.tsx
import { jsx as jsx15, jsxs as jsxs12 } from "react/jsx-runtime";
function PanelRoot({ className, ...props }) {
  return /* @__PURE__ */ jsx15("div", { className: [Panel_default.root, className].filter(Boolean).join(" "), ...props });
}
function PanelHeader({
  title,
  meta,
  children
}) {
  return /* @__PURE__ */ jsxs12("header", { className: Panel_default.header, children: [
    /* @__PURE__ */ jsxs12("div", { children: [
      /* @__PURE__ */ jsx15("h2", { children: title }),
      meta && /* @__PURE__ */ jsx15("span", { children: meta })
    ] }),
    children
  ] });
}
function PanelTools({ className, ...props }) {
  return /* @__PURE__ */ jsx15("div", { className: [Panel_default.tools, className].filter(Boolean).join(" "), ...props });
}
function PanelBody({ className, ...props }) {
  return /* @__PURE__ */ jsx15("div", { className: [Panel_default.body, className].filter(Boolean).join(" "), ...props });
}
function PanelFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx15("footer", { className: [Panel_default.footer, className].filter(Boolean).join(" "), ...props });
}

// src/components/PropertyInspector/InspectorControls.tsx
import {
  useEffect as useEffect13,
  useId as useId3,
  useMemo as useMemo5,
  useRef as useRef13,
  useState as useState11
} from "react";
import { useSignals as useSignals8 } from "@preact/signals-react/runtime";
import { fromDisplayMeasurement as fromDisplayMeasurement2, getMeasurementStep, toDisplayMeasurement as toDisplayMeasurement3 } from "@ptd/core";
import {
  RiAddLine,
  RiArrowDownSLine as RiArrowDownSLine3,
  RiCloseLine as RiCloseLine4,
  RiRestartLine,
  RiSubtractLine as RiSubtractLine2
} from "@remixicon/react";

// src/components/PropertyInspector/inspectorColors.ts
import {
  normalizeBarCodeProps,
  normalizeQRCodeProps,
  normalizeSimpleTableProps as normalizeSimpleTableProps3
} from "@ptd/core";

// src/components/PropertyInspector/propertyValue.ts
var TEXT_COMPONENTS = /* @__PURE__ */ new Set(["RoySimpleText", "RoyText"]);
function isEditableTextPropValue(component) {
  return TEXT_COMPONENTS.has(component.component) && (typeof component.propValue === "string" || typeof component.propValue === "number" && Number.isFinite(component.propValue));
}
function parseTextPropValue(currentValue, draft) {
  if (typeof currentValue === "string") return draft;
  if (draft.trim() === "") return null;
  const parsed = Number(draft);
  return Number.isFinite(parsed) ? parsed : null;
}
function parseFiniteNumber(draft, limits = {}) {
  if (draft.trim() === "") return null;
  const parsed = Number(draft);
  if (!Number.isFinite(parsed)) return null;
  if (limits.min !== void 0 && parsed < limits.min) return null;
  if (limits.max !== void 0 && parsed > limits.max) return null;
  return parsed;
}
function scrubNumberValue(start, deltaX, options = {}) {
  const baseStep = options.step ?? 1;
  const multiplier = (options.shiftKey ? 10 : 1) * (options.altKey ? 0.1 : 1);
  const ticks = Math.trunc(deltaX / (options.pixelsPerStep ?? 2));
  const next = Math.round((start + ticks * baseStep * multiplier) * 1e4) / 1e4;
  return Math.min(
    options.max ?? Number.POSITIVE_INFINITY,
    Math.max(options.min ?? Number.NEGATIVE_INFINITY, next)
  );
}
function isHexColor(value) {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
}
function normalizeHexColor(value) {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(normalized)) return normalized;
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(normalized);
  return short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : null;
}

// src/components/PropertyInspector/inspectorColors.ts
var STYLE_COLOR_KEYS = ["color", "background", "borderColor"];
function deriveDocumentColors(template, limit = 24) {
  const usage = /* @__PURE__ */ new Map();
  let order = 0;
  const add = (value) => {
    const color2 = normalizeHexColor(value);
    if (!color2) return;
    const current = usage.get(color2);
    if (current) {
      current.count += 1;
      return;
    }
    usage.set(color2, { count: 1, order });
    order += 1;
  };
  add(template.pageConfig.background);
  add(template.pageConfig.color);
  for (const page of template.pages) {
    for (const component of page.componentData) collectComponentColors(component, add);
  }
  return [...usage.entries()].sort((left, right) => right[1].count - left[1].count || left[1].order - right[1].order).slice(0, limit).map(([color2]) => color2);
}
function collectComponentColors(component, add) {
  for (const key of STYLE_COLOR_KEYS) add(component.style[key]);
  if (component.component === "RoyQRCode") {
    const value = normalizeQRCodeProps(component.propValue);
    add(value.colorDark);
    add(value.colorLight);
  } else if (component.component === "RoyBarCode") {
    add(normalizeBarCodeProps(component.propValue).colorDark);
  } else if (component.component === "RoySimpleTable") {
    const value = normalizeSimpleTableProps3(component.propValue);
    for (const cell of Object.values(value.cells)) {
      add(cell.style.color);
      add(cell.style.background);
      add(cell.style.borderColor);
    }
  } else if (component.component === "RoyGroup" && Array.isArray(component.propValue)) {
    for (const child of component.propValue) {
      if (isComponentSchema(child)) collectComponentColors(child, add);
    }
  }
}
function isComponentSchema(value) {
  if (!value || typeof value !== "object") return false;
  const source = value;
  return typeof source.id === "string" && typeof source.component === "string" && !!source.style;
}

// src/components/PropertyInspector/PropertyInspector.module.css
var PropertyInspector_default = {
  inspector: "PropertyInspector_inspector",
  inspectorPanel: "PropertyInspector_inspectorPanel",
  inspectorBody: "PropertyInspector_inspectorBody",
  inspectorView: "PropertyInspector_inspectorView",
  inspectorFooter: "PropertyInspector_inspectorFooter",
  section: "PropertyInspector_section",
  sectionHeading: "PropertyInspector_sectionHeading",
  disclosure: "PropertyInspector_disclosure",
  sectionBody: "PropertyInspector_sectionBody",
  advancedWrap: "PropertyInspector_advancedWrap",
  disclosureBody: "PropertyInspector_disclosureBody",
  fieldGrid: "PropertyInspector_fieldGrid",
  field: "PropertyInspector_field",
  fieldLabel: "PropertyInspector_fieldLabel",
  scrubLabel: "PropertyInspector_scrubLabel",
  numberControl: "PropertyInspector_numberControl",
  unit: "PropertyInspector_unit",
  fieldError: "PropertyInspector_fieldError",
  textControl: "PropertyInspector_textControl",
  textArea: "PropertyInspector_textArea",
  selectControl: "PropertyInspector_selectControl",
  segmented: "PropertyInspector_segmented",
  portraitIcon: "PropertyInspector_portraitIcon",
  colorControl: "PropertyInspector_colorControl",
  colorWell: "PropertyInspector_colorWell",
  colorMenuTrigger: "PropertyInspector_colorMenuTrigger",
  colorPalette: "PropertyInspector_colorPalette",
  colorPaletteActions: "PropertyInspector_colorPaletteActions",
  transparentSwatch: "PropertyInspector_transparentSwatch",
  colorPaletteSection: "PropertyInspector_colorPaletteSection",
  colorSwatchGrid: "PropertyInspector_colorSwatchGrid",
  colorSwatch: "PropertyInspector_colorSwatch",
  structuredNotice: "PropertyInspector_structuredNotice",
  summary: "PropertyInspector_summary",
  pageWarning: "PropertyInspector_pageWarning",
  contentEditor: "PropertyInspector_contentEditor",
  tableContentEditor: "PropertyInspector_tableContentEditor",
  tableSummary: "PropertyInspector_tableSummary",
  tableActionGrid: "PropertyInspector_tableActionGrid",
  tableActionRow: "PropertyInspector_tableActionRow",
  tableSubheading: "PropertyInspector_tableSubheading",
  tableWideField: "PropertyInspector_tableWideField",
  tableFieldGrid: "PropertyInspector_tableFieldGrid",
  tableSegmented: "PropertyInspector_tableSegmented",
  assetActions: "PropertyInspector_assetActions",
  assetAction: "PropertyInspector_assetAction",
  validationMessage: "PropertyInspector_validationMessage",
  readoutGrid: "PropertyInspector_readoutGrid",
  readoutList: "PropertyInspector_readoutList",
  readout: "PropertyInspector_readout",
  swatch: "PropertyInspector_swatch",
  footerSetting: "PropertyInspector_footerSetting",
  footerIcon: "PropertyInspector_footerIcon",
  footerCopy: "PropertyInspector_footerCopy",
  switchControl: "PropertyInspector_switchControl",
  footerAction: "PropertyInspector_footerAction",
  visuallyHidden: "PropertyInspector_visuallyHidden"
};

// src/components/PropertyInspector/InspectorControls.tsx
import { Fragment as Fragment5, jsx as jsx16, jsxs as jsxs13 } from "react/jsx-runtime";
function InspectorField({
  label,
  labelControl,
  wide = false,
  children
}) {
  return /* @__PURE__ */ jsxs13("div", { className: PropertyInspector_default.field, "data-wide": wide || void 0, children: [
    labelControl ?? /* @__PURE__ */ jsx16("span", { className: PropertyInspector_default.fieldLabel, children: label }),
    children
  ] });
}
function InspectorTextInput({
  label,
  value,
  disabled,
  onValue,
  onStart,
  onFinish,
  onCancel,
  placeholder,
  error,
  wide = false,
  spellCheck,
  inputMode
}) {
  const errorId = useId3();
  const cancelRef = useRef13(false);
  return /* @__PURE__ */ jsx16(InspectorField, { label, wide, children: /* @__PURE__ */ jsxs13(Fragment5, { children: [
    /* @__PURE__ */ jsx16(
      "input",
      {
        className: PropertyInspector_default.textControl,
        type: "text",
        inputMode,
        spellCheck,
        "aria-label": label,
        "aria-invalid": Boolean(error) || void 0,
        "aria-describedby": error ? errorId : void 0,
        value,
        placeholder,
        disabled,
        onFocus: () => {
          cancelRef.current = false;
          onStart();
        },
        onChange: (event) => onValue(event.target.value),
        onKeyDown: (event) => {
          if (event.key !== "Escape" || !onCancel) return;
          event.preventDefault();
          cancelRef.current = true;
          onCancel();
          event.currentTarget.blur();
        },
        onBlur: () => {
          if (cancelRef.current) {
            cancelRef.current = false;
            return;
          }
          onFinish();
        }
      }
    ),
    error && /* @__PURE__ */ jsx16("span", { id: errorId, className: PropertyInspector_default.fieldError, role: "status", children: error })
  ] }) });
}
function InspectorTextArea({
  label,
  value,
  disabled,
  onValue,
  onStart,
  onFinish,
  onCancel,
  placeholder,
  error,
  wide = true,
  spellCheck,
  rows
}) {
  const errorId = useId3();
  const cancelRef = useRef13(false);
  return /* @__PURE__ */ jsx16(InspectorField, { label, wide, children: /* @__PURE__ */ jsxs13(Fragment5, { children: [
    /* @__PURE__ */ jsx16(
      "textarea",
      {
        className: PropertyInspector_default.textArea,
        "aria-label": label,
        "aria-invalid": Boolean(error) || void 0,
        "aria-describedby": error ? errorId : void 0,
        value,
        rows,
        placeholder,
        spellCheck,
        disabled,
        onFocus: () => {
          cancelRef.current = false;
          onStart();
        },
        onChange: (event) => onValue(event.target.value),
        onKeyDown: (event) => {
          if (event.key !== "Escape" || !onCancel) return;
          event.preventDefault();
          cancelRef.current = true;
          onCancel();
          event.currentTarget.blur();
        },
        onBlur: () => {
          if (cancelRef.current) {
            cancelRef.current = false;
            return;
          }
          onFinish();
        }
      }
    ),
    error && /* @__PURE__ */ jsx16("span", { id: errorId, className: PropertyInspector_default.fieldError, role: "status", children: error })
  ] }) });
}
function InspectorFileAction({
  label,
  icon,
  accept,
  disabled,
  onFile
}) {
  return /* @__PURE__ */ jsxs13("label", { className: PropertyInspector_default.assetAction, "data-disabled": disabled || void 0, children: [
    icon,
    /* @__PURE__ */ jsx16("span", { children: label }),
    /* @__PURE__ */ jsx16(
      "input",
      {
        className: PropertyInspector_default.visuallyHidden,
        type: "file",
        accept,
        disabled,
        onChange: (event) => {
          onFile(event.currentTarget.files?.[0]);
          event.currentTarget.value = "";
        }
      }
    )
  ] });
}
function InspectorNumberInput({
  label,
  value,
  disabled,
  placeholder,
  unit,
  step = 1,
  scrubStep = step,
  min,
  max,
  onStart,
  onFinish,
  onCancel,
  onValue
}) {
  const [editing, setEditing] = useState11(false);
  const [draft, setDraft] = useState11("");
  const [scrubbing, setScrubbing] = useState11(false);
  const inputRef = useRef13(null);
  const inputCancelRef = useRef13(false);
  const scrubRef = useRef13(null);
  const errorId = useId3();
  const shownValue = editing ? draft : value === null ? "" : formatNumber(value);
  const parsedDraft = parseFiniteNumber(draft, { min, max });
  const invalid = editing && parsedDraft === null;
  const scrubbable = !disabled && value !== null;
  const apply = (nextDraft) => {
    const parsed = parseFiniteNumber(nextDraft, { min, max });
    if (parsed !== null) onValue(parsed);
  };
  const nudge = (direction) => {
    if (value === null) return;
    const next = clamp6(roundForStep(value + direction * step, step), min, max);
    onStart();
    onValue(next);
    onFinish();
  };
  const clearPointerCapture = (target, pointerId) => {
    scrubRef.current = null;
    if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
  };
  const cancelScrub = (target, pointerId) => {
    const session = scrubRef.current;
    if (!session || session.pointerId !== pointerId) return;
    clearPointerCapture(target, pointerId);
    if (!session.moved) return;
    setScrubbing(false);
    onCancel();
  };
  const handleScrubPointerDown = (event) => {
    if (!scrubbable || value === null || event.button !== 0 || event.pointerType === "touch") return;
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    scrubRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startValue: value,
      lastValue: value,
      moved: false
    };
  };
  const handleScrubPointerMove = (event) => {
    const session = scrubRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - session.startX;
    if (!session.moved) {
      if (Math.abs(deltaX) < 3) return;
      session.moved = true;
      setScrubbing(true);
      onStart();
    }
    const next = scrubNumberValue(session.startValue, deltaX, {
      step: scrubStep,
      min,
      max,
      shiftKey: event.shiftKey,
      altKey: event.altKey
    });
    if (next === session.lastValue) return;
    session.lastValue = next;
    onValue(next);
  };
  const handleScrubPointerUp = (event) => {
    const session = scrubRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    const moved = session.moved;
    clearPointerCapture(event.currentTarget, event.pointerId);
    if (moved) {
      setScrubbing(false);
      onFinish();
      return;
    }
    inputRef.current?.focus({ preventScroll: true });
  };
  const handleScrubKeyDown = (event) => {
    const session = scrubRef.current;
    if (event.key !== "Escape" || !session) return;
    event.preventDefault();
    cancelScrub(event.currentTarget, session.pointerId);
  };
  return /* @__PURE__ */ jsx16(
    InspectorField,
    {
      label,
      labelControl: /* @__PURE__ */ jsx16(
        "button",
        {
          type: "button",
          className: PropertyInspector_default.scrubLabel,
          tabIndex: -1,
          "aria-label": `${label}\uFF0C\u5DE6\u53F3\u62D6\u52A8\u8C03\u6574\u6570\u503C`,
          title: scrubbable ? "\u5DE6\u53F3\u62D6\u52A8\u8C03\u6574\uFF1BShift \u52A0\u901F\uFF0CAlt \u7CBE\u8C03" : void 0,
          disabled: !scrubbable,
          "data-active": scrubbing || void 0,
          onPointerDown: handleScrubPointerDown,
          onPointerMove: handleScrubPointerMove,
          onPointerUp: handleScrubPointerUp,
          onPointerCancel: (event) => cancelScrub(event.currentTarget, event.pointerId),
          onLostPointerCapture: (event) => cancelScrub(event.currentTarget, event.pointerId),
          onKeyDown: handleScrubKeyDown,
          children: label
        }
      ),
      children: /* @__PURE__ */ jsxs13(Fragment5, { children: [
        /* @__PURE__ */ jsxs13(
          "div",
          {
            className: PropertyInspector_default.numberControl,
            "data-disabled": disabled || void 0,
            "data-has-unit": Boolean(unit) || void 0,
            children: [
              /* @__PURE__ */ jsx16(
                "button",
                {
                  type: "button",
                  "aria-label": `${label}\u51CF\u5C11${step}`,
                  disabled: disabled || value === null,
                  onClick: () => nudge(-1),
                  children: /* @__PURE__ */ jsx16(RiSubtractLine2, { "aria-hidden": "true" })
                }
              ),
              /* @__PURE__ */ jsx16(
                "input",
                {
                  ref: inputRef,
                  type: "text",
                  inputMode: "decimal",
                  "aria-label": label,
                  "aria-invalid": invalid || void 0,
                  "aria-describedby": invalid ? errorId : void 0,
                  value: shownValue,
                  placeholder,
                  disabled,
                  onFocus: (event) => {
                    inputCancelRef.current = false;
                    setEditing(true);
                    setDraft(event.currentTarget.value);
                    onStart();
                  },
                  onChange: (event) => {
                    setDraft(event.target.value);
                    apply(event.target.value);
                  },
                  onKeyDown: (event) => {
                    if (event.key !== "Escape") return;
                    event.preventDefault();
                    inputCancelRef.current = true;
                    setEditing(false);
                    onCancel();
                    event.currentTarget.blur();
                  },
                  onBlur: (event) => {
                    if (inputCancelRef.current) {
                      inputCancelRef.current = false;
                      setEditing(false);
                      return;
                    }
                    apply(event.currentTarget.value);
                    setEditing(false);
                    onFinish();
                  }
                }
              ),
              unit && /* @__PURE__ */ jsx16("span", { className: PropertyInspector_default.unit, children: unit }),
              /* @__PURE__ */ jsx16(
                "button",
                {
                  type: "button",
                  "aria-label": `${label}\u589E\u52A0${step}`,
                  disabled: disabled || value === null,
                  onClick: () => nudge(1),
                  children: /* @__PURE__ */ jsx16(RiAddLine, { "aria-hidden": "true" })
                }
              )
            ]
          }
        ),
        invalid && /* @__PURE__ */ jsx16("span", { id: errorId, className: PropertyInspector_default.fieldError, role: "status", children: numberRangeMessage(min, max, unit) })
      ] })
    }
  );
}
function InspectorMetricInput({
  label,
  canvasValue,
  unit,
  disabled,
  placeholder,
  minCanvasPx,
  maxCanvasPx,
  onStart,
  onFinish,
  onCancel,
  onCanvasValue
}) {
  const step = getMeasurementStep(unit);
  return /* @__PURE__ */ jsx16(
    InspectorNumberInput,
    {
      label,
      value: canvasValue === null ? null : toDisplayMeasurement3(canvasValue, unit),
      placeholder,
      unit,
      step,
      scrubStep: step,
      min: minCanvasPx === void 0 ? void 0 : toDisplayMeasurement3(minCanvasPx, unit),
      max: maxCanvasPx === void 0 ? void 0 : toDisplayMeasurement3(maxCanvasPx, unit),
      disabled,
      onStart,
      onFinish,
      onCancel,
      onValue: (value) => onCanvasValue(fromDisplayMeasurement2(value, unit))
    }
  );
}
function InspectorSegmentedInput({
  label,
  value,
  options,
  disabled = false,
  wide = false,
  onValue
}) {
  return /* @__PURE__ */ jsx16(InspectorField, { label, wide, children: /* @__PURE__ */ jsx16("div", { className: PropertyInspector_default.segmented, role: "group", "aria-label": label, "data-count": options.length, children: options.map((option) => /* @__PURE__ */ jsxs13(
    "button",
    {
      type: "button",
      "aria-pressed": option.value === value,
      disabled,
      onClick: () => onValue(option.value),
      children: [
        option.icon,
        /* @__PURE__ */ jsx16("span", { children: option.label })
      ]
    },
    option.value
  )) }) });
}
function InspectorSelectInput({
  label,
  value,
  options,
  disabled,
  wide = false,
  onStart,
  onFinish,
  onValue
}) {
  return /* @__PURE__ */ jsx16(InspectorField, { label, wide, children: /* @__PURE__ */ jsx16(
    "select",
    {
      className: PropertyInspector_default.selectControl,
      "aria-label": label,
      value,
      disabled,
      onFocus: onStart,
      onBlur: onFinish,
      onChange: (event) => onValue(event.target.value),
      children: options.map(([optionValue, optionLabel]) => /* @__PURE__ */ jsx16("option", { value: optionValue, children: optionLabel }, optionValue))
    }
  ) });
}
function InspectorColorControl({
  label,
  value,
  disabled,
  placeholder,
  defaultValue,
  allowTransparent = false,
  wide = false,
  onStart,
  onFinish,
  onCancel,
  onValue
}) {
  useSignals8();
  const store = useEditorStore();
  const template = store.template.value;
  const recentColors = store.recentColors.value;
  const documentColors = useMemo5(() => deriveDocumentColors(template), [template]);
  const [editing, setEditing] = useState11(false);
  const [draft, setDraft] = useState11("");
  const [open, setOpen] = useState11(false);
  const cancelRef = useRef13(false);
  const errorId = useId3();
  const normalizedValue = normalizeHexColor(value);
  const normalizedDefault = normalizeHexColor(defaultValue);
  const fallback = normalizedValue ?? normalizedDefault ?? "#ffffff";
  const shownValue = editing ? draft : value ?? "";
  const normalizedDraft = normalizeHexColor(shownValue);
  const transparentDraft = allowTransparent && shownValue.trim().toLowerCase() === "transparent";
  const valid = Boolean(normalizedDraft) || transparentDraft;
  const invalid = editing && shownValue.trim() !== "" && !valid;
  const swatchStyle = { "--field-color": normalizedDraft ?? fallback };
  useEffect13(() => {
    if (!disabled) return;
    const frame = window.requestAnimationFrame(() => setOpen(false));
    return () => window.cancelAnimationFrame(frame);
  }, [disabled]);
  const apply = (next) => {
    const normalized = normalizeHexColor(next);
    if (normalized) {
      onValue(normalized);
      return normalized;
    }
    if (allowTransparent && next.trim().toLowerCase() === "transparent") {
      onValue("transparent");
      return "transparent";
    }
    return null;
  };
  const applyDiscrete = (next) => {
    if (disabled || next === value) return;
    onStart();
    onValue(next);
    onFinish();
    const normalized = normalizeHexColor(next);
    if (normalized) store.recordRecentColor(normalized);
  };
  return /* @__PURE__ */ jsx16(InspectorField, { label, wide: wide || open, children: /* @__PURE__ */ jsxs13(Fragment5, { children: [
    /* @__PURE__ */ jsxs13(
      "div",
      {
        className: PropertyInspector_default.colorControl,
        "data-disabled": disabled || void 0,
        "data-transparent": value === "transparent" || value === null || void 0,
        children: [
          /* @__PURE__ */ jsxs13("label", { className: PropertyInspector_default.colorWell, style: swatchStyle, children: [
            /* @__PURE__ */ jsxs13("span", { className: PropertyInspector_default.visuallyHidden, children: [
              label,
              "\u8272\u677F"
            ] }),
            /* @__PURE__ */ jsx16(
              "input",
              {
                type: "color",
                "aria-label": `${label}\u8272\u677F`,
                value: fallback,
                disabled,
                onFocus: onStart,
                onBlur: (event) => {
                  onFinish();
                  store.recordRecentColor(event.currentTarget.value);
                },
                onChange: (event) => onValue(event.target.value)
              }
            )
          ] }),
          /* @__PURE__ */ jsx16(
            "input",
            {
              type: "text",
              spellCheck: false,
              "aria-label": `${label}\u989C\u8272\u503C`,
              "aria-invalid": invalid || void 0,
              "aria-describedby": invalid ? errorId : void 0,
              value: shownValue,
              placeholder: placeholder ?? "#RRGGBB",
              disabled,
              onFocus: (event) => {
                cancelRef.current = false;
                setEditing(true);
                setDraft(event.currentTarget.value);
                onStart();
              },
              onChange: (event) => {
                setDraft(event.target.value);
                apply(event.target.value);
              },
              onKeyDown: (event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                cancelRef.current = true;
                setEditing(false);
                onCancel();
                event.currentTarget.blur();
              },
              onBlur: (event) => {
                if (cancelRef.current) {
                  cancelRef.current = false;
                  setEditing(false);
                  return;
                }
                const next = apply(event.currentTarget.value);
                setEditing(false);
                onFinish();
                const normalized = normalizeHexColor(next);
                if (normalized) store.recordRecentColor(normalized);
              }
            }
          ),
          /* @__PURE__ */ jsx16(
            "button",
            {
              type: "button",
              className: PropertyInspector_default.colorMenuTrigger,
              "aria-label": `${label}\u989C\u8272\u9009\u9879`,
              "aria-expanded": open,
              disabled,
              onClick: () => setOpen((current) => !current),
              children: open ? /* @__PURE__ */ jsx16(RiCloseLine4, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx16(RiArrowDownSLine3, { "aria-hidden": "true" })
            }
          )
        ]
      }
    ),
    invalid && /* @__PURE__ */ jsx16("span", { id: errorId, className: PropertyInspector_default.fieldError, role: "status", children: "\u8BF7\u8F93\u5165\u4E09\u4F4D\u6216\u516D\u4F4D HEX \u989C\u8272" }),
    open && /* @__PURE__ */ jsxs13("div", { className: PropertyInspector_default.colorPalette, "aria-label": `${label}\u989C\u8272\u9762\u677F`, children: [
      /* @__PURE__ */ jsxs13("div", { className: PropertyInspector_default.colorPaletteActions, children: [
        allowTransparent && /* @__PURE__ */ jsxs13(
          "button",
          {
            type: "button",
            "data-active": value === "transparent" || void 0,
            onClick: () => applyDiscrete("transparent"),
            children: [
              /* @__PURE__ */ jsx16("span", { className: PropertyInspector_default.transparentSwatch, "aria-hidden": "true" }),
              "\u65E0\u8272"
            ]
          }
        ),
        normalizedDefault && /* @__PURE__ */ jsxs13(
          "button",
          {
            type: "button",
            disabled: normalizedValue === normalizedDefault,
            onClick: () => applyDiscrete(normalizedDefault),
            children: [
              /* @__PURE__ */ jsx16(RiRestartLine, { "aria-hidden": "true" }),
              "\u6062\u590D"
            ]
          }
        )
      ] }),
      recentColors.length > 0 && /* @__PURE__ */ jsx16(
        ColorPaletteSection,
        {
          title: "\u6700\u8FD1\u4F7F\u7528",
          colors: recentColors,
          current: normalizedValue,
          onColor: applyDiscrete
        }
      ),
      /* @__PURE__ */ jsx16(
        ColorPaletteSection,
        {
          title: "\u6587\u6863\u989C\u8272",
          colors: documentColors,
          current: normalizedValue,
          onColor: applyDiscrete
        }
      )
    ] })
  ] }) });
}
function ColorPaletteSection({
  title,
  colors,
  current,
  onColor
}) {
  return /* @__PURE__ */ jsxs13("div", { className: PropertyInspector_default.colorPaletteSection, children: [
    /* @__PURE__ */ jsx16("span", { children: title }),
    /* @__PURE__ */ jsx16("div", { className: PropertyInspector_default.colorSwatchGrid, children: colors.map((color2) => /* @__PURE__ */ jsx16(
      "button",
      {
        type: "button",
        className: PropertyInspector_default.colorSwatch,
        style: { "--field-color": color2 },
        "aria-label": `${title} ${color2}`,
        "aria-pressed": color2 === current,
        title: color2,
        onClick: () => onColor(color2)
      },
      color2
    )) })
  ] });
}
function formatNumber(value) {
  return String(Math.round(value * 100) / 100);
}
function numberRangeMessage(min, max, unit) {
  const suffix = unit ? ` ${unit}` : "";
  if (min !== void 0 && max !== void 0) {
    return `\u8BF7\u8F93\u5165 ${formatNumber(min)}\u2013${formatNumber(max)}${suffix} \u4E4B\u95F4\u7684\u6570\u503C`;
  }
  if (min !== void 0) return `\u8BF7\u8F93\u5165\u4E0D\u5C0F\u4E8E ${formatNumber(min)}${suffix} \u7684\u6570\u503C`;
  if (max !== void 0) return `\u8BF7\u8F93\u5165\u4E0D\u5927\u4E8E ${formatNumber(max)}${suffix} \u7684\u6570\u503C`;
  return "\u8BF7\u8F93\u5165\u6709\u6548\u6570\u503C";
}
function roundForStep(value, step) {
  const decimals = String(step).split(".")[1]?.length ?? 0;
  return Number(value.toFixed(decimals));
}
function clamp6(value, min, max) {
  return Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? Number.NEGATIVE_INFINITY, value));
}

// src/components/PropertyInspector/TableContentFields.tsx
import { useMemo as useMemo6 } from "react";
import { useSignals as useSignals9 } from "@preact/signals-react/runtime";
import {
  deleteTableColumn,
  deleteTableRow,
  expandTableCellRange as expandTableCellRange2,
  getTableCellAt as getTableCellAt3,
  getTableCellIdsInRange as getTableCellIdsInRange2,
  insertTableColumn,
  insertTableRow,
  mergeTableCells,
  normalizeSimpleTableProps as normalizeSimpleTableProps4,
  resizeTableColumn as resizeTableColumn2,
  resizeTableRow as resizeTableRow2,
  splitTableCell,
  updateTableCellText as updateTableCellText3,
  updateTableCellsStyle
} from "@ptd/core";
import { Fragment as Fragment6, jsx as jsx17, jsxs as jsxs14 } from "react/jsx-runtime";
function TableContentFields({
  component,
  disabled,
  onStart,
  onFinish,
  onCancel,
  onValue,
  onDiscreteValue
}) {
  useSignals9();
  const store = useEditorStore();
  const measurementUnit = store.measurementUnit.value;
  const value = useMemo6(() => normalizeSimpleTableProps4(component.propValue), [component.propValue]);
  const selection = store.tableCellSelection.value?.componentId === component.id ? store.tableCellSelection.value : null;
  const range = selection ? expandTableCellRange2(value, {
    startRow: selection.anchorRow,
    startColumn: selection.anchorColumn,
    endRow: selection.focusRow,
    endColumn: selection.focusColumn
  }) : null;
  const cellIds = range ? getTableCellIdsInRange2(value, range) : [];
  const cells = cellIds.flatMap((id) => value.cells[id] ? [value.cells[id]] : []);
  const primary = selection ? getTableCellAt3(value, selection.focusRow, selection.focusColumn) : null;
  const primaryStyle = primary?.style;
  const fontSelection = resolveFontFamily(primaryStyle?.fontFamily ?? "");
  const rowIndex = clamp7(selection?.focusRow ?? value.grid.length - 1, 0, value.grid.length - 1);
  const columnIndex = clamp7(
    selection?.focusColumn ?? value.columnWidths.length - 1,
    0,
    value.columnWidths.length - 1
  );
  const applyDiscrete = (next, row = rowIndex, column = columnIndex) => {
    if (next === value) return;
    onDiscreteValue(next);
    store.selectTableCell(
      component.id,
      clamp7(row, 0, next.grid.length - 1),
      clamp7(column, 0, next.columnWidths.length - 1)
    );
  };
  const patchCells = (patch, discrete = true) => {
    if (cellIds.length === 0) return;
    const next = updateTableCellsStyle(value, cellIds, patch);
    if (discrete) onDiscreteValue(next);
    else onValue(next);
  };
  const insertRowAt = (index) => applyDiscrete(insertTableRow(value, index), index, columnIndex);
  const insertColumnAt = (index) => applyDiscrete(insertTableColumn(value, index), rowIndex, index);
  return /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableContentEditor, children: [
    /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableSummary, role: "status", children: [
      /* @__PURE__ */ jsxs14("span", { children: [
        value.grid.length,
        " \u884C \xD7 ",
        value.columnWidths.length,
        " \u5217"
      ] }),
      /* @__PURE__ */ jsx17("strong", { children: cellIds.length > 0 ? `\u5DF2\u9009 ${cellIds.length} \u683C` : "\u9009\u62E9\u753B\u5E03\u4E2D\u7684\u5355\u5143\u683C" })
    ] }),
    /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableActionGrid, "aria-label": "\u8868\u683C\u7ED3\u6784\u547D\u4EE4", children: [
      /* @__PURE__ */ jsx17("button", { type: "button", disabled, onClick: () => insertRowAt(rowIndex), children: "\u4E0A\u65B9\u63D2\u5165\u884C" }),
      /* @__PURE__ */ jsx17("button", { type: "button", disabled, onClick: () => insertRowAt(rowIndex + 1), children: "\u4E0B\u65B9\u63D2\u5165\u884C" }),
      /* @__PURE__ */ jsx17("button", { type: "button", disabled, onClick: () => insertColumnAt(columnIndex), children: "\u5DE6\u4FA7\u63D2\u5165\u5217" }),
      /* @__PURE__ */ jsx17("button", { type: "button", disabled, onClick: () => insertColumnAt(columnIndex + 1), children: "\u53F3\u4FA7\u63D2\u5165\u5217" }),
      /* @__PURE__ */ jsx17(
        "button",
        {
          type: "button",
          disabled: disabled || value.grid.length <= 1,
          "data-danger": true,
          onClick: () => applyDiscrete(deleteTableRow(value, rowIndex), rowIndex - 1, columnIndex),
          children: "\u5220\u9664\u5F53\u524D\u884C"
        }
      ),
      /* @__PURE__ */ jsx17(
        "button",
        {
          type: "button",
          disabled: disabled || value.columnWidths.length <= 1,
          "data-danger": true,
          onClick: () => applyDiscrete(deleteTableColumn(value, columnIndex), rowIndex, columnIndex - 1),
          children: "\u5220\u9664\u5F53\u524D\u5217"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableActionRow, children: [
      /* @__PURE__ */ jsx17(
        "button",
        {
          type: "button",
          disabled: disabled || !range || cellIds.length <= 1,
          onClick: () => {
            if (range)
              applyDiscrete(mergeTableCells(value, range), range.startRow, range.startColumn);
          },
          children: "\u5408\u5E76\u6240\u9009\u5355\u5143\u683C"
        }
      ),
      /* @__PURE__ */ jsx17(
        "button",
        {
          type: "button",
          disabled: disabled || !primary || primary.rowSpan === 1 && primary.colSpan === 1,
          onClick: () => {
            if (primary) applyDiscrete(splitTableCell(value, primary.id));
          },
          children: "\u62C6\u5206\u5355\u5143\u683C"
        }
      )
    ] }),
    primary ? /* @__PURE__ */ jsxs14(Fragment6, { children: [
      /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableSubheading, children: [
        /* @__PURE__ */ jsx17("span", { children: "\u5355\u5143\u683C" }),
        /* @__PURE__ */ jsx17("small", { children: cells.length > 1 ? "\u6DF7\u5408\u503C \xB7 \u4FEE\u6539\u5C06\u5E94\u7528\u5230\u6240\u9009\u533A\u57DF" : primary.id })
      ] }),
      cells.length === 1 && /* @__PURE__ */ jsx17(
        InspectorTextArea,
        {
          label: "\u5355\u5143\u683C\u5185\u5BB9",
          value: primary.text,
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (text2) => onValue(updateTableCellText3(value, primary.id, text2))
        }
      ),
      /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableFieldGrid, children: [
        /* @__PURE__ */ jsx17(
          InspectorMetricInput,
          {
            label: "\u5F53\u524D\u884C\u9AD8",
            canvasValue: value.rowHeights[rowIndex] ?? 0,
            unit: measurementUnit,
            minCanvasPx: 8,
            disabled,
            onStart,
            onFinish,
            onCancel,
            onCanvasValue: (next) => onValue(resizeTableRow2(value, rowIndex, next))
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorMetricInput,
          {
            label: "\u5F53\u524D\u5217\u5BBD",
            canvasValue: value.columnWidths[columnIndex] ?? 0,
            unit: measurementUnit,
            minCanvasPx: 8,
            disabled,
            onStart,
            onFinish,
            onCancel,
            onCanvasValue: (next) => onValue(resizeTableColumn2(value, columnIndex, next))
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorNumberInput,
          {
            label: "\u5B57\u53F7",
            value: primaryStyle?.fontSize ?? 10,
            unit: "pt",
            min: 1,
            disabled,
            onStart,
            onFinish,
            onCancel,
            onValue: (next) => patchCells({ fontSize: next }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorMetricInput,
          {
            label: "\u5185\u8FB9\u8DDD",
            canvasValue: primaryStyle?.padding ?? 4,
            unit: measurementUnit,
            minCanvasPx: 0,
            disabled,
            onStart,
            onFinish,
            onCancel,
            onCanvasValue: (next) => patchCells({ padding: next }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorSelectInput,
          {
            label: "\u4E2D\u6587\u5B57\u4F53",
            value: fontSelection.cjk,
            options: CJK_FONT_FAMILY_OPTIONS,
            disabled,
            onStart,
            onFinish,
            onValue: (font) => patchCells({ fontFamily: composeFontFamily(font, fontSelection.latin) }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorSelectInput,
          {
            label: "\u897F\u6587\u5B57\u4F53",
            value: fontSelection.latin,
            options: LATIN_FONT_FAMILY_OPTIONS,
            disabled,
            onStart,
            onFinish,
            onValue: (font) => patchCells({ fontFamily: composeFontFamily(fontSelection.cjk, font) }, false)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableSegmented, "aria-label": "\u5B57\u5F62\u6837\u5F0F", children: [
        /* @__PURE__ */ jsx17(
          ToggleButton,
          {
            label: "\u7C97\u4F53",
            active: primaryStyle?.fontWeight === "bold",
            disabled,
            onClick: () => patchCells({
              fontWeight: primaryStyle?.fontWeight === "bold" ? "normal" : "bold"
            })
          }
        ),
        /* @__PURE__ */ jsx17(
          ToggleButton,
          {
            label: "\u659C\u4F53",
            active: primaryStyle?.fontStyle === "italic",
            disabled,
            onClick: () => patchCells({
              fontStyle: primaryStyle?.fontStyle === "italic" ? "normal" : "italic"
            })
          }
        ),
        /* @__PURE__ */ jsx17(
          ToggleButton,
          {
            label: "\u4E0B\u5212\u7EBF",
            active: primaryStyle?.textDecoration === "underline",
            disabled,
            onClick: () => patchCells({
              textDecoration: primaryStyle?.textDecoration === "underline" ? "none" : "underline"
            })
          }
        ),
        /* @__PURE__ */ jsx17(
          ToggleButton,
          {
            label: "\u5220\u9664\u7EBF",
            active: primaryStyle?.textDecoration === "line-through",
            disabled,
            onClick: () => patchCells({
              textDecoration: primaryStyle?.textDecoration === "line-through" ? "none" : "line-through"
            })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.tableFieldGrid, children: [
        /* @__PURE__ */ jsx17(
          InspectorSegmentedInput,
          {
            label: "\u6C34\u5E73\u5BF9\u9F50",
            value: primaryStyle?.horizontalAlign ?? "left",
            disabled,
            wide: true,
            options: [
              { value: "left", label: "\u5DE6" },
              { value: "center", label: "\u4E2D" },
              { value: "right", label: "\u53F3" }
            ],
            onValue: (next) => patchCells({ horizontalAlign: next })
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorSegmentedInput,
          {
            label: "\u5782\u76F4\u5BF9\u9F50",
            value: primaryStyle?.verticalAlign ?? "middle",
            disabled,
            wide: true,
            options: [
              { value: "top", label: "\u4E0A" },
              { value: "middle", label: "\u4E2D" },
              { value: "bottom", label: "\u4E0B" }
            ],
            onValue: (next) => patchCells({ verticalAlign: next })
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorColorControl,
          {
            label: "\u6587\u5B57\u989C\u8272",
            value: primaryStyle?.color ?? "#1d2735",
            defaultValue: "#1d2735",
            disabled,
            onStart,
            onFinish,
            onCancel,
            onValue: (next) => patchCells({ color: next }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorColorControl,
          {
            label: "\u80CC\u666F\u989C\u8272",
            value: primaryStyle?.background ?? "#ffffff",
            defaultValue: "#ffffff",
            disabled,
            onStart,
            onFinish,
            onCancel,
            onValue: (next) => patchCells({ background: next }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorColorControl,
          {
            label: "\u8FB9\u6846\u989C\u8272",
            value: primaryStyle?.borderColor ?? "#8d99a8",
            defaultValue: "#8d99a8",
            disabled,
            onStart,
            onFinish,
            onCancel,
            onValue: (next) => patchCells({ borderColor: next }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorMetricInput,
          {
            label: "\u8FB9\u6846\u5BBD\u5EA6",
            canvasValue: primaryStyle?.borderWidth ?? 1,
            unit: measurementUnit,
            minCanvasPx: 0,
            disabled,
            onStart,
            onFinish,
            onCancel,
            onCanvasValue: (next) => patchCells({ borderWidth: next }, false)
          }
        ),
        /* @__PURE__ */ jsx17(
          InspectorSegmentedInput,
          {
            label: "\u8FB9\u6846\u6837\u5F0F",
            value: primaryStyle?.borderStyle ?? "solid",
            disabled,
            wide: true,
            options: [
              { value: "solid", label: "\u5B9E\u7EBF" },
              { value: "dashed", label: "\u865A\u7EBF" },
              { value: "dotted", label: "\u70B9\u7EBF" },
              { value: "none", label: "\u65E0" }
            ],
            onValue: (next) => patchCells({ borderStyle: next })
          }
        )
      ] })
    ] }) : /* @__PURE__ */ jsxs14("div", { className: PropertyInspector_default.structuredNotice, role: "note", children: [
      /* @__PURE__ */ jsx17("strong", { children: "\u5728\u753B\u5E03\u4E2D\u9009\u62E9\u5355\u5143\u683C" }),
      /* @__PURE__ */ jsx17("span", { children: "\u62D6\u52A8\u53EF\u9009\u62E9\u533A\u57DF\uFF1B\u53CC\u51FB\u5355\u5143\u683C\u76F4\u63A5\u7F16\u8F91\u6587\u5B57\u3002" })
    ] })
  ] });
}
function ToggleButton({
  label,
  active,
  disabled,
  onClick
}) {
  return /* @__PURE__ */ jsx17("button", { type: "button", "aria-pressed": active, disabled, onClick, children: label });
}
function clamp7(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

// src/components/PropertyInspector/PropertyInspector.tsx
import { Fragment as Fragment7, jsx as jsx18, jsxs as jsxs15 } from "react/jsx-runtime";
function SectionHeading({ title, meta }) {
  return /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.sectionHeading, children: [
    /* @__PURE__ */ jsx18("span", { children: title }),
    meta && /* @__PURE__ */ jsx18("small", { children: meta })
  ] });
}
function InspectorSection({
  title,
  meta,
  children
}) {
  return /* @__PURE__ */ jsxs15("section", { className: PropertyInspector_default.section, children: [
    /* @__PURE__ */ jsx18(SectionHeading, { title, meta }),
    /* @__PURE__ */ jsx18("div", { className: PropertyInspector_default.sectionBody, children })
  ] });
}
function InspectorDisclosure({
  title,
  meta,
  children
}) {
  return /* @__PURE__ */ jsxs15("details", { className: PropertyInspector_default.disclosure, children: [
    /* @__PURE__ */ jsxs15("summary", { children: [
      /* @__PURE__ */ jsx18("span", { children: title }),
      meta && /* @__PURE__ */ jsx18("small", { children: meta })
    ] }),
    /* @__PURE__ */ jsx18("div", { className: PropertyInspector_default.disclosureBody, children })
  ] });
}
function InspectorShell({
  label,
  title,
  meta,
  page = false,
  children,
  footer
}) {
  return /* @__PURE__ */ jsx18("aside", { className: PropertyInspector_default.inspector, "aria-label": label, "data-ptd-region": "inspector", children: /* @__PURE__ */ jsxs15(PanelRoot, { className: PropertyInspector_default.inspectorPanel, children: [
    /* @__PURE__ */ jsx18(PanelHeader, { title, meta }),
    /* @__PURE__ */ jsx18(PanelBody, { className: PropertyInspector_default.inspectorBody, children: /* @__PURE__ */ jsx18(
      "div",
      {
        className: PropertyInspector_default.inspectorView,
        "data-ptd-region": page ? "page-inspector" : void 0,
        children
      }
    ) }),
    footer && /* @__PURE__ */ jsx18(PanelFooter, { className: PropertyInspector_default.inspectorFooter, children: footer })
  ] }) });
}
var GEOMETRY_FIELDS = [
  { key: "left", label: "X" },
  { key: "top", label: "Y" },
  { key: "width", label: "\u5BBD\u5EA6" },
  { key: "height", label: "\u9AD8\u5EA6" }
];
var CONTENT_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleText",
  "RoyText",
  "RoySimpleTable",
  "RoyComplexTable",
  "RoyImage",
  "RoyQRCode",
  "RoyBarCode"
]);
var TYPOGRAPHY_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleText",
  "RoyText",
  "RoyComplexTable"
]);
var ALIGNMENT_COMPONENTS = /* @__PURE__ */ new Set(["RoySimpleText"]);
var BACKGROUND_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleText",
  "RoyText",
  "RoyComplexTable",
  "RoyLine",
  "RoyRect",
  "RoyCircle",
  "RoyStar",
  "RoyImage",
  "RoyBarCode"
]);
var BORDER_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleText",
  "RoyText",
  "RoyComplexTable",
  "RoyRect",
  "RoyCircle",
  "RoyImage",
  "RoyQRCode",
  "RoyBarCode"
]);
var BORDER_STYLE_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleText",
  "RoyText",
  "RoyRect",
  "RoyCircle",
  "RoyImage",
  "RoyQRCode",
  "RoyBarCode"
]);
var RADIUS_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleText",
  "RoyText",
  "RoyRect",
  "RoyImage"
]);
var CONFIGURABLE_CONTENT_COMPONENTS = /* @__PURE__ */ new Set([
  "RoySimpleTable",
  "RoyImage",
  "RoyQRCode",
  "RoyBarCode"
]);
function PropertyInspector() {
  useSignals10();
  const store = useEditorStore();
  const selected = store.selectedComponents.value;
  if (selected.length === 0) return /* @__PURE__ */ jsx18(PageInspector, {});
  if (selected.length > 1) return /* @__PURE__ */ jsx18(BatchInspector, { components: selected });
  return /* @__PURE__ */ jsx18(SingleInspector, { component: selected[0] });
}
function PageInspector() {
  const store = useEditorStore();
  const page = store.pageConfig.value;
  const dimensions = getPageDimensions4(page);
  const unit = store.measurementUnit.value;
  const [titleDraft, setTitleDraft] = useState12(null);
  const [marginsLinked, setMarginsLinked] = useState12(true);
  const start = () => store.beginGesture();
  const finish = () => store.commitGesture();
  const cancel = () => store.cancelGesture();
  const update = (patch) => store.updatePageConfig(patch, true);
  const updateDiscrete = (patch) => {
    start();
    update(patch);
    finish();
  };
  const pageWidthMm = page.pageDirection === "l" ? page.pageHeight : page.pageWidth;
  const pageHeightMm = page.pageDirection === "l" ? page.pageWidth : page.pageHeight;
  const pageMetric = `${formatMeasurement4(dimensions.width, unit)} \xD7 ${formatMeasurement4(dimensions.height, unit)} ${unit}`;
  useEffect14(() => () => store.commitGesture(), [store]);
  const updatePageSize = (pageSize) => {
    const preset = PAGE_SIZES[pageSize];
    updateDiscrete(
      preset ? {
        pageSize,
        pageWidth: preset.w,
        pageHeight: preset.h,
        pageCurHeight: preset.h
      } : { pageSize: "custom" }
    );
  };
  const updateMargin = (key, canvasValue) => {
    const value = pxToMm2(canvasValue);
    update(
      marginsLinked ? {
        pageMarginTop: value,
        pageMarginRight: value,
        pageMarginBottom: value,
        pageMarginLeft: value
      } : { [key]: value }
    );
  };
  const marginMax = (key) => {
    if (marginsLinked) return Math.min(pageWidthMm, pageHeightMm) / 2 - 0.01;
    if (key === "pageMarginLeft") return pageWidthMm - page.pageMarginRight - 0.01;
    if (key === "pageMarginRight") return pageWidthMm - page.pageMarginLeft - 0.01;
    if (key === "pageMarginTop") return pageHeightMm - page.pageMarginBottom - 0.01;
    return pageHeightMm - page.pageMarginTop - 0.01;
  };
  return /* @__PURE__ */ jsxs15(
    InspectorShell,
    {
      label: "\u6587\u6863\u9875\u9762\u8BBE\u7F6E",
      title: "\u6587\u6863\u9875\u9762\u8BBE\u7F6E",
      meta: page.title || "\u672A\u547D\u540D\u6A21\u677F",
      page: true,
      footer: /* @__PURE__ */ jsx18(
        FooterSetting,
        {
          icon: /* @__PURE__ */ jsx18(RiRuler2Line2, {}),
          label: "\u9875\u9762\u6807\u5C3A",
          detail: "\u62D6\u51FA\u53C2\u8003\u7EBF",
          checked: store.showRuler.value,
          onChange: () => store.toggleRuler()
        }
      ),
      children: [
        store.outOfBoundsComponents.value.length > 0 && /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.pageWarning, role: "status", children: [
          /* @__PURE__ */ jsxs15("strong", { children: [
            store.outOfBoundsComponents.value.length,
            " \u4E2A\u7EC4\u4EF6\u8D85\u51FA\u9875\u9762"
          ] }),
          /* @__PURE__ */ jsx18("span", { children: "\u7EC4\u4EF6\u4F4D\u7F6E\u548C\u5C3A\u5BF8\u5DF2\u4FDD\u7559\uFF1B\u53EF\u5728\u753B\u5E03\u4E2D\u9010\u4E00\u8C03\u6574\u3002" })
        ] }),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u6587\u6863", meta: "\u6240\u6709\u9875\u9762\u5171\u7528", children: /* @__PURE__ */ jsx18(
          InspectorTextInput,
          {
            label: "\u6A21\u677F\u6807\u9898",
            wide: true,
            value: titleDraft ?? page.title,
            disabled: false,
            onStart: () => {
              setTitleDraft(page.title);
              start();
            },
            onValue: (title) => {
              setTitleDraft(title);
              update({ title });
            },
            onFinish: () => {
              setTitleDraft(null);
              finish();
            },
            onCancel: () => {
              setTitleDraft(null);
              cancel();
            }
          }
        ) }),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u9875\u9762\u65B9\u5411", meta: page.pageDirection === "p" ? "\u7EB5\u5411" : "\u6A2A\u5411", children: /* @__PURE__ */ jsx18(
          InspectorSegmentedInput,
          {
            label: "\u9875\u9762\u65B9\u5411",
            value: page.pageDirection,
            options: [
              {
                value: "p",
                label: "\u7EB5\u5411",
                icon: /* @__PURE__ */ jsx18(RiLandscapeLine2, { className: PropertyInspector_default.portraitIcon, "aria-hidden": "true" })
              },
              { value: "l", label: "\u6A2A\u5411", icon: /* @__PURE__ */ jsx18(RiLandscapeLine2, { "aria-hidden": "true" }) }
            ],
            onValue: (value) => store.setPageDirection(value)
          }
        ) }),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u9875\u9762\u89C4\u683C", meta: pageMetric, children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          /* @__PURE__ */ jsx18(
            InspectorSelectInput,
            {
              label: "\u7EB8\u5F20\u89C4\u683C",
              wide: true,
              value: page.pageSize,
              disabled: false,
              options: [
                ...Object.values(PAGE_SIZES).map(
                  (size) => [size.name, `${size.name} \xB7 ${size.w} \xD7 ${size.h} mm`]
                ),
                ["custom", "\u81EA\u5B9A\u4E49\u5C3A\u5BF8"]
              ],
              onStart: start,
              onFinish: finish,
              onValue: (value) => updatePageSize(value)
            }
          ),
          page.pageSize === "custom" && /* @__PURE__ */ jsxs15(Fragment7, { children: [
            /* @__PURE__ */ jsx18(
              InspectorMetricInput,
              {
                label: "\u9875\u9762\u5BBD\u5EA6",
                canvasValue: mmToPx5(page.pageWidth),
                unit,
                minCanvasPx: 1,
                disabled: false,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onCanvasValue: (value) => update({ pageWidth: pxToMm2(value) })
              }
            ),
            /* @__PURE__ */ jsx18(
              InspectorMetricInput,
              {
                label: "\u9875\u9762\u9AD8\u5EA6",
                canvasValue: mmToPx5(page.pageHeight),
                unit,
                minCanvasPx: 1,
                disabled: false,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onCanvasValue: (value) => update({ pageHeight: pxToMm2(value), pageCurHeight: pxToMm2(value) })
              }
            )
          ] })
        ] }) }),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u5185\u5BB9\u5B89\u5168\u533A", meta: marginsLinked ? "\u56DB\u8FB9\u8054\u52A8" : "\u72EC\u7ACB\u8BBE\u7F6E", children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          /* @__PURE__ */ jsx18(
            InspectorSegmentedInput,
            {
              label: "\u8FB9\u8DDD\u6A21\u5F0F",
              value: marginsLinked ? "linked" : "separate",
              wide: true,
              options: [
                { value: "linked", label: "\u56DB\u8FB9\u8054\u52A8" },
                { value: "separate", label: "\u72EC\u7ACB\u8BBE\u7F6E" }
              ],
              onValue: (value) => setMarginsLinked(value === "linked")
            }
          ),
          [
            ["pageMarginTop", "\u4E0A\u8FB9\u8DDD"],
            ["pageMarginRight", "\u53F3\u8FB9\u8DDD"],
            ["pageMarginBottom", "\u4E0B\u8FB9\u8DDD"],
            ["pageMarginLeft", "\u5DE6\u8FB9\u8DDD"]
          ].map(([key, label]) => /* @__PURE__ */ jsx18(
            InspectorMetricInput,
            {
              label,
              canvasValue: mmToPx5(page[key]),
              unit,
              minCanvasPx: 0,
              maxCanvasPx: mmToPx5(marginMax(key)),
              disabled: false,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onCanvasValue: (value) => updateMargin(key, value)
            },
            key
          ))
        ] }) }),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u7EB8\u5F20\u4E0E\u6392\u7248", meta: primaryFontName2(page.fontFamily), children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          /* @__PURE__ */ jsx18(
            InspectorColorControl,
            {
              label: "\u7EB8\u5F20\u989C\u8272",
              value: page.background,
              defaultValue: DEFAULT_PAGE_CONFIG.background,
              disabled: false,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (background) => update({ background })
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorColorControl,
            {
              label: "\u9ED8\u8BA4\u6587\u5B57",
              value: page.color,
              defaultValue: DEFAULT_PAGE_CONFIG.color,
              disabled: false,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (color2) => update({ color: color2 })
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSelectInput,
            {
              label: "\u9ED8\u8BA4\u5B57\u4F53",
              value: page.fontFamily,
              disabled: false,
              options: CJK_FONT_FAMILY_OPTIONS,
              onStart: start,
              onFinish: finish,
              onValue: (fontFamily) => update({ fontFamily })
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u9ED8\u8BA4\u5B57\u53F7",
              value: page.fontSize,
              unit: "pt",
              min: 1,
              disabled: false,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (fontSize) => update({ fontSize })
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u9ED8\u8BA4\u884C\u9AD8",
              value: page.lineHeight,
              step: 0.1,
              min: 0.1,
              disabled: false,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (lineHeight) => update({ lineHeight })
            }
          )
        ] }) })
      ]
    }
  );
}
function primaryFontName2(fontFamily) {
  return (fontFamily.split(",")[0]?.trim() || fontFamily).replace(/^['"]|['"]$/g, "");
}
function SingleInspector({ component }) {
  const store = useEditorStore();
  const measurementUnit = store.measurementUnit.value;
  const locked = Boolean(component.isLock);
  const [textDraft, setTextDraft] = useState12(null);
  const start = () => store.beginGesture();
  const finish = () => store.commitGesture();
  const cancel = () => store.cancelGesture();
  const updateStyle = (key, value) => store.updateComponentStyle(component.id, { [key]: value }, true);
  const updateDiscreteStyle = (key, value) => {
    start();
    updateStyle(key, value);
    finish();
  };
  const updateContent = (value) => store.updateComponent(component.id, { propValue: value }, true);
  const updateDiscreteContent = (value) => {
    start();
    updateContent(value);
    finish();
  };
  useEffect14(() => () => store.commitGesture(), [store]);
  const editableText = isEditableTextPropValue(component);
  const configurableContent = isConfigurableContentComponent(component);
  const showsContent = CONTENT_COMPONENTS.has(component.component);
  const showsTypography = TYPOGRAPHY_COMPONENTS.has(component.component);
  const showsAlignment = ALIGNMENT_COMPONENTS.has(component.component);
  const showsTextColor = TYPOGRAPHY_COMPONENTS.has(component.component);
  const showsBackground = BACKGROUND_COMPONENTS.has(component.component);
  const showsBorder = BORDER_COMPONENTS.has(component.component);
  const showsBorderStyle = BORDER_STYLE_COMPONENTS.has(component.component);
  const showsRadius = RADIUS_COMPONENTS.has(component.component);
  const showsAppearance = showsTextColor || showsBackground || showsBorder;
  const fontValue = text(component.style.fontFamily, DEFAULT_CJK_FONT_FAMILY);
  const fontSelection = resolveFontFamily(fontValue);
  const cjkFontOptions = fontSelection.recognized ? CJK_FONT_FAMILY_OPTIONS : [
    [fontSelection.cjk, primaryFontName2(fontSelection.cjk)],
    ...CJK_FONT_FAMILY_OPTIONS
  ];
  return /* @__PURE__ */ jsxs15(
    InspectorShell,
    {
      label: "\u5C5E\u6027\u9762\u677F",
      title: component.name || "\u672A\u547D\u540D\u7EC4\u4EF6",
      meta: component.component,
      footer: /* @__PURE__ */ jsx18(
        FooterSetting,
        {
          label: locked ? "\u7EC4\u4EF6\u5DF2\u9501\u5B9A" : "\u9501\u5B9A\u7EC4\u4EF6",
          detail: locked ? "\u4EC5\u5141\u8BB8\u89E3\u9501" : "\u4FDD\u62A4\u51E0\u4F55\u4E0E\u5185\u5BB9",
          checked: locked,
          onChange: (checked) => store.updateComponent(component.id, { isLock: checked })
        }
      ),
      children: [
        component.component === "RoyGroup" && /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.summary, role: "note", children: [
          /* @__PURE__ */ jsx18("strong", { children: "\u7EC4\u5408\u5BF9\u8C61" }),
          /* @__PURE__ */ jsxs15("span", { children: [
            "\u5305\u542B ",
            Array.isArray(component.propValue) ? component.propValue.length : 0,
            " ",
            "\u4E2A\u5B50\u5BF9\u8C61\uFF1B\u6B64\u5904\u53EA\u4FEE\u6539\u7EC4\u5408\u6846\u7684\u5B89\u5168\u5C5E\u6027\u3002"
          ] })
        ] }),
        showsContent && /* @__PURE__ */ jsx18(
          InspectorSection,
          {
            title: "\u5185\u5BB9",
            meta: component.component === "RoyText" ? "\u753B\u5E03\u5BCC\u6587\u672C\u7F16\u8F91" : editableText ? "\u53EF\u7F16\u8F91" : component.component === "RoySimpleTable" ? "\u5355\u5143\u683C\u7F16\u8F91" : configurableContent ? "\u5B9E\u65F6\u9884\u89C8" : "\u4E13\u7528\u7F16\u8F91\u5668",
            children: configurableContent ? /* @__PURE__ */ jsx18(
              ConfigurableContentFields,
              {
                component,
                disabled: locked,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onValue: updateContent,
                onDiscreteValue: updateDiscreteContent
              },
              component.id
            ) : component.component === "RoyText" ? /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.structuredNotice, role: "note", children: [
              /* @__PURE__ */ jsx18("strong", { children: "\u5728\u753B\u5E03\u4E2D\u7F16\u8F91\u5BCC\u6587\u672C" }),
              /* @__PURE__ */ jsx18("span", { children: "\u53CC\u51FB\u5185\u5BB9\u8FDB\u5165\u6392\u7248\u5DE5\u5177\u680F\uFF1B\u5C5E\u6027\u9762\u677F\u8D1F\u8D23\u7EC4\u4EF6\u7EA7\u9ED8\u8BA4\u6392\u7248\u4E0E\u5916\u89C2\u3002" })
            ] }) : editableText ? /* @__PURE__ */ jsx18(
              InspectorTextInput,
              {
                label: "\u6587\u672C\u503C",
                wide: true,
                value: textDraft ?? printable(component.propValue),
                disabled: locked,
                onStart: () => {
                  setTextDraft(printable(component.propValue));
                  start();
                },
                onValue: (draft) => {
                  setTextDraft(draft);
                  const value = parseTextPropValue(component.propValue, draft);
                  if (value !== null) store.updateComponent(component.id, { propValue: value }, true);
                },
                onFinish: () => {
                  setTextDraft(null);
                  finish();
                },
                onCancel: () => {
                  setTextDraft(null);
                  cancel();
                }
              }
            ) : component.component === "RoyComplexTable" ? /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.structuredNotice, role: "note", children: [
              /* @__PURE__ */ jsx18("strong", { children: "\u517C\u5BB9\u8868\u683C\u4E3A\u53EA\u8BFB\u5185\u5BB9" }),
              /* @__PURE__ */ jsx18("span", { children: "\u5F53\u524D\u7248\u672C\u4FDD\u7559\u6E32\u67D3\u4E0E\u51E0\u4F55\u8C03\u6574\uFF0C\u4E0D\u63D0\u4F9B\u5C1A\u672A\u95ED\u73AF\u7684\u6570\u636E\u533A\u7F16\u8F91\u5165\u53E3\u3002" })
            ] }) : /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.structuredNotice, role: "note", children: [
              /* @__PURE__ */ jsx18("strong", { children: "\u7ED3\u6784\u5316\u5185\u5BB9\u7531\u4E13\u7528\u7F16\u8F91\u5668\u7EF4\u62A4" }),
              /* @__PURE__ */ jsx18("span", { children: "\u6B64\u9762\u677F\u4ECD\u53EF\u8C03\u6574\u51E0\u4F55\u3001\u5C42\u7EA7\u4E0E\u89C6\u89C9\u6837\u5F0F\u3002" })
            ] })
          }
        ),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u51E0\u4F55", meta: "\u4F4D\u7F6E\u4E0E\u5C3A\u5BF8", children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          GEOMETRY_FIELDS.map(({ key, label }) => /* @__PURE__ */ jsx18(
            InspectorMetricInput,
            {
              label: component.component === "RoyLine" && key === "width" ? "\u7EBF\u6761\u957F\u5EA6" : component.component === "RoyLine" && key === "height" ? "\u7EBF\u6761\u539A\u5EA6" : label,
              canvasValue: numeric3(component.style[key]),
              unit: measurementUnit,
              minCanvasPx: key === "width" || key === "height" ? 1 : void 0,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onCanvasValue: (value) => updateStyle(key, value)
            },
            key
          )),
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u65CB\u8F6C",
              value: numeric3(component.style.rotate),
              unit: "\xB0",
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => updateStyle("rotate", value)
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u900F\u660E\u5EA6",
              value: numeric3(component.style.opacity) * 100,
              unit: "%",
              step: 5,
              scrubStep: 1,
              min: 0,
              max: 100,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => updateStyle("opacity", value / 100)
            }
          )
        ] }) }),
        showsTypography && /* @__PURE__ */ jsx18(InspectorSection, { title: "\u6392\u7248", meta: "\u6587\u5B57\u4E0E\u5BF9\u9F50", children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u5B57\u53F7",
              value: numeric3(component.style.fontSize),
              unit: "pt",
              min: 1,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => updateStyle("fontSize", value)
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSelectInput,
            {
              label: "\u4E2D\u6587\u5B57\u4F53",
              value: fontSelection.cjk,
              disabled: locked,
              options: cjkFontOptions,
              onStart: start,
              onFinish: finish,
              onValue: (value) => updateStyle("fontFamily", composeFontFamily(value, fontSelection.latin))
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSelectInput,
            {
              label: "\u897F\u6587\u5B57\u4F53",
              value: fontSelection.latin,
              disabled: locked,
              options: LATIN_FONT_FAMILY_OPTIONS,
              onStart: start,
              onFinish: finish,
              onValue: (value) => updateStyle("fontFamily", composeFontFamily(fontSelection.cjk, value))
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSegmentedInput,
            {
              label: "\u5B57\u91CD",
              value: text(component.style.fontWeight, "normal"),
              disabled: locked,
              options: [
                { value: "normal", label: "\u5E38\u89C4" },
                { value: "bold", label: "\u7C97\u4F53" }
              ],
              onValue: (value) => updateDiscreteStyle("fontWeight", value)
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSegmentedInput,
            {
              label: "\u5B57\u5F62",
              value: text(component.style.fontStyle, "normal"),
              disabled: locked,
              options: [
                { value: "normal", label: "\u5E38\u89C4" },
                { value: "italic", label: "\u659C\u4F53" }
              ],
              onValue: (value) => updateDiscreteStyle("fontStyle", value)
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSegmentedInput,
            {
              label: "\u4E0B\u5212\u7EBF",
              value: component.style.isUnderLine ? "show" : "hide",
              disabled: locked,
              options: [
                { value: "hide", label: "\u65E0" },
                { value: "show", label: "\u542F\u7528" }
              ],
              onValue: (value) => updateDiscreteStyle("isUnderLine", value === "show")
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorSegmentedInput,
            {
              label: "\u5220\u9664\u7EBF",
              value: component.style.isDelLine ? "show" : "hide",
              disabled: locked,
              options: [
                { value: "hide", label: "\u65E0" },
                { value: "show", label: "\u542F\u7528" }
              ],
              onValue: (value) => updateDiscreteStyle("isDelLine", value === "show")
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u884C\u9AD8",
              value: cssNumber(component.style.lineHeight) || 1,
              step: 0.1,
              min: 0.1,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => updateStyle("lineHeight", String(value))
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorMetricInput,
            {
              label: "\u5B57\u8DDD",
              canvasValue: cssNumber(component.style.letterSpacing),
              unit: measurementUnit,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onCanvasValue: (value) => updateStyle("letterSpacing", String(value))
            }
          ),
          /* @__PURE__ */ jsx18(
            InspectorMetricInput,
            {
              label: "\u5185\u8FB9\u8DDD",
              canvasValue: cssNumber(component.style.padding),
              unit: measurementUnit,
              minCanvasPx: 0,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onCanvasValue: (value) => updateStyle("padding", String(value))
            }
          ),
          showsAlignment && /* @__PURE__ */ jsxs15(Fragment7, { children: [
            /* @__PURE__ */ jsx18(
              InspectorSegmentedInput,
              {
                label: "\u6C34\u5E73\u5BF9\u9F50",
                value: text(component.style.justifyContent, "flex-start"),
                disabled: locked,
                wide: true,
                options: [
                  { value: "flex-start", label: "\u5DE6" },
                  { value: "center", label: "\u4E2D" },
                  { value: "flex-end", label: "\u53F3" }
                ],
                onValue: (value) => updateDiscreteStyle("justifyContent", value)
              }
            ),
            /* @__PURE__ */ jsx18(
              InspectorSegmentedInput,
              {
                label: "\u5782\u76F4\u5BF9\u9F50",
                value: text(component.style.alignItems, "flex-start"),
                disabled: locked,
                wide: true,
                options: [
                  { value: "flex-start", label: "\u4E0A" },
                  { value: "center", label: "\u4E2D" },
                  { value: "flex-end", label: "\u4E0B" }
                ],
                onValue: (value) => updateDiscreteStyle("alignItems", value)
              }
            )
          ] })
        ] }) }),
        showsAppearance && /* @__PURE__ */ jsx18(InspectorSection, { title: "\u5916\u89C2", meta: showsBorder ? "\u989C\u8272\u4E0E\u63CF\u8FB9" : "\u586B\u5145", children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          showsTextColor && /* @__PURE__ */ jsx18(
            InspectorColorControl,
            {
              label: "\u6587\u5B57",
              value: color(component.style.color, "#1d2735"),
              defaultValue: "#1d2735",
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => updateStyle("color", value)
            }
          ),
          showsBackground && /* @__PURE__ */ jsx18(
            InspectorColorControl,
            {
              label: component.component === "RoyLine" ? "\u7EBF\u6761" : "\u80CC\u666F",
              value: paint(component.style.background, "#f8fafc"),
              defaultValue: "#f8fafc",
              allowTransparent: true,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => updateStyle("background", value)
            }
          ),
          showsBorder && /* @__PURE__ */ jsx18("div", { className: PropertyInspector_default.advancedWrap, children: /* @__PURE__ */ jsx18(InspectorDisclosure, { title: "\u9AD8\u7EA7\u63CF\u8FB9\u4E0E\u5706\u89D2", meta: "\u6309\u9700\u5C55\u5F00", children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
            /* @__PURE__ */ jsx18(
              InspectorColorControl,
              {
                label: "\u8FB9\u6846\u8272",
                value: color(component.style.borderColor, "#7d8999"),
                defaultValue: "#7d8999",
                disabled: locked,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onValue: (value) => updateStyle("borderColor", value)
              }
            ),
            /* @__PURE__ */ jsx18(
              InspectorMetricInput,
              {
                label: "\u8FB9\u6846\u5BBD",
                canvasValue: numeric3(component.style.borderWidth),
                unit: measurementUnit,
                minCanvasPx: 0,
                disabled: locked,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onCanvasValue: (value) => updateStyle("borderWidth", value)
              }
            ),
            showsRadius && /* @__PURE__ */ jsx18(
              InspectorMetricInput,
              {
                label: "\u5706\u89D2",
                canvasValue: cssNumber(component.style.borderRadius),
                unit: measurementUnit,
                minCanvasPx: 0,
                disabled: locked,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onCanvasValue: (value) => updateStyle("borderRadius", `${value}px`)
              }
            ),
            showsBorderStyle && /* @__PURE__ */ jsx18(
              InspectorSegmentedInput,
              {
                label: "\u8FB9\u6846\u6837\u5F0F",
                value: text(component.style.borderType, "solid"),
                disabled: locked,
                wide: true,
                options: [
                  { value: "solid", label: "\u5B9E\u7EBF" },
                  { value: "dashed", label: "\u865A\u7EBF" },
                  { value: "dotted", label: "\u70B9\u7EBF" },
                  { value: "none", label: "\u65E0" }
                ],
                onValue: (value) => updateDiscreteStyle("borderType", value)
              }
            )
          ] }) }) })
        ] }) })
      ]
    }
  );
}
function isConfigurableContentComponent(component) {
  return CONFIGURABLE_CONTENT_COMPONENTS.has(component.component);
}
function ConfigurableContentFields(props) {
  switch (props.component.component) {
    case "RoySimpleTable":
      return /* @__PURE__ */ jsx18(TableContentFields, { ...props, component: props.component });
    case "RoyImage":
      return /* @__PURE__ */ jsx18(ImageContentFields, { ...props });
    case "RoyQRCode":
      return /* @__PURE__ */ jsx18(QRCodeContentFields, { ...props });
    case "RoyBarCode":
      return /* @__PURE__ */ jsx18(BarCodeContentFields, { ...props });
  }
}
function ImageContentFields({
  component,
  disabled,
  onStart,
  onFinish,
  onCancel,
  onValue,
  onDiscreteValue
}) {
  const value = normalizeImageProps(component.propValue);
  const [sourceDraft, setSourceDraft] = useState12(null);
  const [sourceStart, setSourceStart] = useState12("");
  const [fileError, setFileError] = useState12(null);
  const shownSource = sourceDraft ?? value.src;
  const sourceError = imageSourceError(shownSource);
  const patch = (next) => onValue({ ...value, ...next });
  const patchDiscrete = (next) => onDiscreteValue({ ...value, ...next });
  const loadFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFileError("\u8BF7\u9009\u62E9\u6D4F\u89C8\u5668\u652F\u6301\u7684\u56FE\u7247\u6587\u4EF6");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setFileError("\u56FE\u7247\u8BFB\u53D6\u5931\u8D25\uFF0C\u8BF7\u91CD\u65B0\u9009\u62E9");
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        setFileError("\u56FE\u7247\u8BFB\u53D6\u7ED3\u679C\u65E0\u6548");
        return;
      }
      setFileError(null);
      patchDiscrete({
        src: reader.result,
        alt: value.alt || file.name.replace(/\.[^.]+$/, "")
      });
    };
    reader.readAsDataURL(file);
  };
  return /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.contentEditor, children: [
    /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
      /* @__PURE__ */ jsx18(
        InspectorTextInput,
        {
          label: "\u56FE\u7247\u5730\u5740",
          wide: true,
          spellCheck: false,
          value: shownSource,
          placeholder: "https://\u2026 \u6216 data:image/\u2026",
          error: sourceError,
          disabled,
          onStart: () => {
            setSourceStart(value.src);
            setSourceDraft(value.src);
            onStart();
          },
          onValue: (next) => {
            setSourceDraft(next);
            if (!imageSourceError(next)) patch({ src: next });
          },
          onFinish: () => {
            if (sourceError) patch({ src: sourceStart });
            setSourceDraft(null);
            onFinish();
          },
          onCancel: () => {
            setSourceDraft(null);
            onCancel();
          }
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorTextInput,
        {
          label: "\u56FE\u7247\u66FF\u4EE3\u6587\u672C",
          wide: true,
          value: value.alt,
          placeholder: "\u4F8B\u5982\uFF1A\u516C\u53F8 Logo",
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (alt) => patch({ alt })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorSelectInput,
        {
          label: "\u9002\u914D\u65B9\u5F0F",
          value: value.fit,
          options: [
            ["contain", "\u5B8C\u6574\u663E\u793A"],
            ["cover", "\u586B\u6EE1\u88C1\u5207"],
            ["fill", "\u62C9\u4F38\u586B\u6EE1"]
          ],
          disabled,
          onStart,
          onFinish,
          onValue: (fit) => patch({ fit })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorSelectInput,
        {
          label: "\u5BF9\u8C61\u4F4D\u7F6E",
          value: value.position,
          options: [
            ["center", "\u5C45\u4E2D"],
            ["top", "\u9876\u90E8"],
            ["right", "\u53F3\u4FA7"],
            ["bottom", "\u5E95\u90E8"],
            ["left", "\u5DE6\u4FA7"]
          ],
          disabled,
          onStart,
          onFinish,
          onValue: (position) => patch({ position })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.assetActions, children: [
      /* @__PURE__ */ jsx18(
        InspectorFileAction,
        {
          label: "\u9009\u62E9\u672C\u5730\u56FE\u7247",
          icon: /* @__PURE__ */ jsx18(RiUpload2Line, { "aria-hidden": "true" }),
          accept: "image/*",
          disabled,
          onFile: loadFile
        }
      ),
      /* @__PURE__ */ jsxs15(
        "button",
        {
          type: "button",
          className: PropertyInspector_default.assetAction,
          disabled: disabled || value.src === "",
          onClick: () => patchDiscrete({ src: "" }),
          children: [
            /* @__PURE__ */ jsx18(RiDeleteBinLine5, { "aria-hidden": "true" }),
            /* @__PURE__ */ jsx18("span", { children: "\u6E05\u9664\u56FE\u7247" })
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx18(
      ValidationMessage,
      {
        error: sourceError ?? fileError,
        idle: value.src === "" ? "\u7ED8\u5236\u6846\u5DF2\u521B\u5EFA\uFF0C\u8BF7\u9009\u62E9\u56FE\u7247\u6216\u8F93\u5165\u7A33\u5B9A\u5730\u5740\u3002" : "\u56FE\u7247\u5185\u5BB9\u5DF2\u5199\u5165\u6A21\u677F\u3002"
      }
    )
  ] });
}
function QRCodeContentFields({
  component,
  disabled,
  onStart,
  onFinish,
  onCancel,
  onValue
}) {
  const value = normalizeQRCodeProps2(component.propValue);
  const patch = (next) => onValue({ ...value, ...next });
  const error = qrCodeContentError(value);
  return /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.contentEditor, children: [
    /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
      /* @__PURE__ */ jsx18(
        InspectorTextArea,
        {
          label: "\u4E8C\u7EF4\u7801\u5185\u5BB9",
          value: value.text,
          error,
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (text2) => patch({ text: text2 })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorSelectInput,
        {
          label: "\u7EA0\u9519\u7B49\u7EA7",
          value: value.correctLevel,
          options: [
            ["L", "L \xB7 \u7EA6 7%"],
            ["M", "M \xB7 \u7EA6 15%"],
            ["Q", "Q \xB7 \u7EA6 25%"],
            ["H", "H \xB7 \u7EA6 30%"]
          ],
          disabled,
          onStart,
          onFinish,
          onValue: (correctLevel) => patch({ correctLevel })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorNumberInput,
        {
          label: "\u9759\u533A",
          value: value.margin,
          min: 0,
          max: 32,
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (margin) => patch({ margin })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorColorControl,
        {
          label: "\u524D\u666F",
          value: value.colorDark,
          defaultValue: "#1d2735",
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (colorDark) => patch({ colorDark })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorColorControl,
        {
          label: "\u80CC\u666F",
          value: value.colorLight,
          defaultValue: "#ffffff",
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (colorLight) => patch({ colorLight })
        }
      )
    ] }),
    /* @__PURE__ */ jsx18(ValidationMessage, { error, idle: "\u5185\u5BB9\u4E0E\u6837\u5F0F\u4F1A\u5B9E\u65F6\u751F\u6210\u4E8C\u7EF4\u7801\u3002" })
  ] });
}
function BarCodeContentFields({
  component,
  disabled,
  onStart,
  onFinish,
  onCancel,
  onValue,
  onDiscreteValue
}) {
  const value = normalizeBarCodeProps2(component.propValue);
  const patch = (next) => onValue({ ...value, ...next });
  const error = barCodeContentError(value);
  return /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.contentEditor, children: [
    /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
      /* @__PURE__ */ jsx18(
        InspectorTextInput,
        {
          label: "\u6761\u5F62\u7801\u5185\u5BB9",
          wide: true,
          spellCheck: false,
          value: value.text,
          error,
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (text2) => patch({ text: text2 })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorSelectInput,
        {
          label: "\u7801\u5236",
          value: value.bcid,
          options: BAR_CODE_FORMATS.map(
            (format) => [format, barCodeFormatLabel(format)]
          ),
          disabled,
          onStart,
          onFinish,
          onValue: (bcid) => patch({ bcid })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorColorControl,
        {
          label: "\u524D\u666F",
          value: value.colorDark,
          defaultValue: "#1d2735",
          disabled,
          onStart,
          onFinish,
          onCancel,
          onValue: (colorDark) => patch({ colorDark })
        }
      ),
      /* @__PURE__ */ jsx18(
        InspectorSegmentedInput,
        {
          label: "\u53EF\u8BFB\u6587\u5B57",
          value: value.includeText ? "show" : "hide",
          wide: true,
          disabled,
          options: [
            { value: "show", label: "\u663E\u793A" },
            { value: "hide", label: "\u9690\u85CF" }
          ],
          onValue: (next) => onDiscreteValue({ ...value, includeText: next === "show" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx18(ValidationMessage, { error, idle: "\u5F53\u524D\u5185\u5BB9\u7B26\u5408\u6240\u9009\u7801\u5236\u3002" })
  ] });
}
function ValidationMessage({ error, idle }) {
  return /* @__PURE__ */ jsxs15(
    "div",
    {
      className: PropertyInspector_default.validationMessage,
      "data-error": Boolean(error) || void 0,
      role: "status",
      children: [
        /* @__PURE__ */ jsx18("strong", { children: error ? "\u9700\u8981\u4FEE\u6B63" : "\u914D\u7F6E\u6709\u6548" }),
        /* @__PURE__ */ jsx18("span", { children: error ?? idle })
      ]
    }
  );
}
function barCodeFormatLabel(format) {
  const labels = {
    code128: "Code 128",
    code39: "Code 39",
    ean13: "EAN-13",
    ean8: "EAN-8",
    upca: "UPC-A",
    itf14: "ITF-14"
  };
  return labels[format];
}
function BatchInspector({ components }) {
  const store = useEditorStore();
  const locked = components.some((component) => component.isLock);
  const start = () => store.beginGesture();
  const finish = () => store.commitGesture();
  const cancel = () => store.cancelGesture();
  useEffect14(() => () => store.commitGesture(), [store]);
  const shared = (key) => {
    const first = components[0].style[key];
    return {
      mixed: !components.every((component) => component.style[key] === first),
      value: first
    };
  };
  const opacity = shared("opacity");
  const fontSize = shared("fontSize");
  const sharedColor = shared("color");
  const allSupportTypography = components.every(
    (component) => TYPOGRAPHY_COMPONENTS.has(component.component)
  );
  return /* @__PURE__ */ jsxs15(
    InspectorShell,
    {
      label: "\u5C5E\u6027\u9762\u677F",
      title: `${components.length} \u4E2A\u5BF9\u8C61`,
      meta: "MULTI SELECT",
      footer: /* @__PURE__ */ jsx18(
        "button",
        {
          type: "button",
          className: PropertyInspector_default.footerAction,
          disabled: !locked,
          onClick: () => store.setLock(false),
          children: locked ? "\u89E3\u9501\u6240\u9009\u5BF9\u8C61" : "\u6240\u9009\u5BF9\u8C61\u5747\u53EF\u7F16\u8F91"
        }
      ),
      children: [
        /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.summary, role: "note", children: [
          /* @__PURE__ */ jsx18("strong", { children: locked ? "\u9009\u62E9\u4E2D\u5305\u542B\u9501\u5B9A\u5BF9\u8C61" : "\u6279\u91CF\u7F16\u8F91\u5171\u540C\u5C5E\u6027" }),
          /* @__PURE__ */ jsx18("span", { children: locked ? "\u5148\u89E3\u9501\u540E\u518D\u4FEE\u6539\u5171\u540C\u6837\u5F0F\u3002" : "\u6DF7\u5408\u503C\u4FDD\u6301\u4E3A\u7A7A\uFF0C\u5F55\u5165\u540E\u5E94\u7528\u5230\u5168\u90E8\u6240\u9009\u5BF9\u8C61\u3002" })
        ] }),
        /* @__PURE__ */ jsx18(InspectorSection, { title: "\u5171\u540C\u5C5E\u6027", meta: `${components.length} \u9879`, children: /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.fieldGrid, children: [
          /* @__PURE__ */ jsx18(
            InspectorNumberInput,
            {
              label: "\u900F\u660E\u5EA6",
              value: opacity.mixed ? null : numeric3(opacity.value) * 100,
              placeholder: opacity.mixed ? "\u6DF7\u5408" : "\u672A\u8BBE\u7F6E",
              unit: "%",
              step: 5,
              min: 0,
              max: 100,
              scrubStep: 1,
              disabled: locked,
              onStart: start,
              onFinish: finish,
              onCancel: cancel,
              onValue: (value) => store.updateSelectedStyles({ opacity: value / 100 }, true)
            }
          ),
          allSupportTypography && /* @__PURE__ */ jsxs15(Fragment7, { children: [
            /* @__PURE__ */ jsx18(
              InspectorNumberInput,
              {
                label: "\u5B57\u53F7",
                value: fontSize.mixed ? null : numeric3(fontSize.value),
                placeholder: fontSize.mixed ? "\u6DF7\u5408" : "\u672A\u8BBE\u7F6E",
                unit: "pt",
                min: 1,
                disabled: locked,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onValue: (value) => store.updateSelectedStyles({ fontSize: value }, true)
              }
            ),
            /* @__PURE__ */ jsx18(
              InspectorColorControl,
              {
                label: "\u6587\u5B57",
                value: sharedColor.mixed || !isHexColor(sharedColor.value) ? null : sharedColor.value,
                placeholder: sharedColor.mixed ? "\u6DF7\u5408\uFF1A\u8F93\u5165\u989C\u8272" : "\u672A\u8BBE\u7F6E",
                disabled: locked,
                onStart: start,
                onFinish: finish,
                onCancel: cancel,
                onValue: (value) => store.updateSelectedStyles({ color: value }, true)
              }
            )
          ] })
        ] }) })
      ]
    }
  );
}
function FooterSetting({
  icon,
  label,
  detail,
  checked,
  onChange
}) {
  return /* @__PURE__ */ jsxs15("div", { className: PropertyInspector_default.footerSetting, children: [
    icon && /* @__PURE__ */ jsx18("span", { className: PropertyInspector_default.footerIcon, children: icon }),
    /* @__PURE__ */ jsxs15("span", { className: PropertyInspector_default.footerCopy, children: [
      /* @__PURE__ */ jsx18("strong", { children: label }),
      /* @__PURE__ */ jsx18("small", { children: detail })
    ] }),
    /* @__PURE__ */ jsx18(
      "button",
      {
        type: "button",
        className: PropertyInspector_default.switchControl,
        role: "switch",
        "aria-label": label,
        "aria-checked": checked,
        onClick: () => onChange(!checked),
        children: /* @__PURE__ */ jsx18("span", {})
      }
    )
  ] });
}
function numeric3(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
function cssNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
function printable(value) {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}
function text(value, fallback = "") {
  return typeof value === "string" ? value : fallback;
}
function color(value, fallback) {
  return isHexColor(value) ? value : fallback;
}
function paint(value, fallback) {
  return value === "transparent" ? value : color(value, fallback);
}

// src/components/Sidebar/Sidebar.tsx
import { useState as useState14 } from "react";
import { useSignals as useSignals12 } from "@preact/signals-react/runtime";
import * as Tooltip4 from "@radix-ui/react-tooltip";
import {
  RiAddLine as RiAddLine2,
  RiArrowDownLine,
  RiArrowUpLine,
  RiCloseLine as RiCloseLine6,
  RiDatabase2Line as RiDatabase2Line2,
  RiDeleteBinLine as RiDeleteBinLine7,
  RiDraggable,
  RiFileList2Line,
  RiFileCopyLine as RiFileCopyLine3,
  RiGalleryLine,
  RiLock2Line,
  RiPagesLine as RiPagesLine3,
  RiStackLine as RiStackLine2
} from "@remixicon/react";

// src/components/DataPanel/DataPanel.tsx
import {
  useCallback as useCallback10,
  useEffect as useEffect15,
  useId as useId4,
  useMemo as useMemo7,
  useRef as useRef14,
  useState as useState13
} from "react";
import { useSignals as useSignals11 } from "@preact/signals-react/runtime";
import {
  DATA_SOURCE_LIMITS,
  flattenDataFields,
  formatDataPath,
  getComponentBindingTargets as getComponentBindingTargets2,
  getTableCellAt as getTableCellAt4,
  inferDataDefinition,
  normalizeSimpleTableProps as normalizeSimpleTableProps5,
  parseRuntimeRecordsJson
} from "@ptd/core";
import {
  RiArrowDownSLine as RiArrowDownSLine4,
  RiArrowLeftSLine,
  RiArrowRightSLine as RiArrowRightSLine2,
  RiCloseLine as RiCloseLine5,
  RiDeleteBinLine as RiDeleteBinLine6,
  RiFileUploadLine as RiFileUploadLine2,
  RiLink as RiLink2,
  RiSearchLine as RiSearchLine2,
  RiLinkUnlink
} from "@remixicon/react";

// src/components/DataPanel/DataPanel.module.css
var DataPanel_default = {
  tools: "DataPanel_tools",
  body: "DataPanel_body",
  iconButton: "DataPanel_iconButton",
  recordControl: "DataPanel_recordControl",
  searchControl: "DataPanel_searchControl",
  literalSegment: "DataPanel_literalSegment",
  fieldToken: "DataPanel_fieldToken",
  segmented: "DataPanel_segmented",
  primaryButton: "DataPanel_primaryButton",
  fieldMain: "DataPanel_fieldMain",
  inlineNote: "DataPanel_inlineNote",
  sourceSection: "DataPanel_sourceSection",
  bindingSection: "DataPanel_bindingSection",
  fieldsSection: "DataPanel_fieldsSection",
  sectionHeading: "DataPanel_sectionHeading",
  surfaceHeader: "DataPanel_surfaceHeader",
  actionButton: "DataPanel_actionButton",
  secondaryButton: "DataPanel_secondaryButton",
  textAction: "DataPanel_textAction",
  dropControl: "DataPanel_dropControl",
  textControl: "DataPanel_textControl",
  fieldTree: "DataPanel_fieldTree",
  fieldBranch: "DataPanel_fieldBranch",
  fieldRow: "DataPanel_fieldRow",
  disclosure: "DataPanel_disclosure",
  editField: "DataPanel_editField",
  disclosureSpacer: "DataPanel_disclosureSpacer",
  fieldName: "DataPanel_fieldName",
  fieldPath: "DataPanel_fieldPath",
  typeLabel: "DataPanel_typeLabel",
  emptyState: "DataPanel_emptyState",
  importSurface: "DataPanel_importSurface",
  pasteControl: "DataPanel_pasteControl",
  sourceName: "DataPanel_sourceName",
  summaryGrid: "DataPanel_summaryGrid",
  diagnostics: "DataPanel_diagnostics",
  impactSummary: "DataPanel_impactSummary",
  actionRow: "DataPanel_actionRow",
  fieldEditor: "DataPanel_fieldEditor",
  composer: "DataPanel_composer",
  presetControl: "DataPanel_presetControl",
  fieldIdentity: "DataPanel_fieldIdentity",
  composerSegments: "DataPanel_composerSegments",
  visuallyHidden: "DataPanel_visuallyHidden",
  designer: "DataPanel_designer"
};

// src/components/DataPanel/DataPanel.tsx
import { Fragment as Fragment8, jsx as jsx19, jsxs as jsxs16 } from "react/jsx-runtime";
var TYPE_LABELS = {
  string: "\u6587\u672C",
  number: "\u6570\u503C",
  boolean: "\u5E03\u5C14",
  date: "\u65E5\u671F",
  object: "\u5BF9\u8C61",
  array: "\u6570\u7EC4",
  unknown: "\u672A\u77E5"
};
function DataPanel({ onClose }) {
  useSignals11();
  const store = useEditorStore();
  return /* @__PURE__ */ jsx19(DataPanelSession, { onClose }, store.externalTemplateRevision.value);
}
function DataPanelSession({ onClose }) {
  useSignals11();
  const store = useEditorStore();
  const normalized = store.normalizedTemplateData.value;
  const fields = normalized.data.fields;
  const flatFields = useMemo7(() => flattenDataFields(fields), [fields]);
  const [query, setQuery] = useState13("");
  const [expanded, setExpanded] = useState13(
    () => new Set(fields.filter((field) => field.children?.length).map((field) => field.id))
  );
  const [importMode, setImportMode] = useState13("closed");
  const [pasteDraft, setPasteDraft] = useState13("");
  const [candidate, setCandidate] = useState13(null);
  const [fieldDraft, setFieldDraft] = useState13(null);
  const [composerState, setComposerState] = useState13(null);
  const fileInputRef = useRef14(null);
  const importTriggerRef = useRef14(null);
  const fieldTriggerRef = useRef14(null);
  const activeReaderRef = useRef14(null);
  const readGenerationRef = useRef14(0);
  const mountedRef = useRef14(true);
  const selected = store.selectedComponents.value;
  const component = selected.length === 1 ? selected[0] ?? null : null;
  const target = getConcreteTarget(component, store.tableCellSelection.value);
  const targetIdentity = target ? bindingTargetKey(target) : "none";
  const binding = component && target ? findBinding(component.bindings, target) : void 0;
  const selectedComponentId = component?.id;
  const selectedComponentValue = component?.propValue;
  const bindingState = describeBindingState(selected, component, target);
  const proofRecords = store.proofRecords.value;
  const canProof = proofRecords.length > 0;
  const composerKey = selectedComponentId && targetIdentity === "text" ? `${selectedComponentId}:${binding?.id ?? "new"}:${JSON.stringify(
    binding?.expression ?? selectedComponentValue
  )}` : null;
  const composerDraft = composerKey ? composerState?.key === composerKey ? composerState.value : toTextExpression(binding?.expression, selectedComponentValue) : null;
  useEffect15(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      readGenerationRef.current += 1;
      activeReaderRef.current?.abort();
    };
  }, []);
  const closeImport = useCallback10((restoreFocus = true) => {
    activeReaderRef.current?.abort();
    activeReaderRef.current = null;
    readGenerationRef.current += 1;
    setImportMode("closed");
    setCandidate(null);
    if (restoreFocus) requestAnimationFrame(() => importTriggerRef.current?.focus());
  }, []);
  const parseSource = useCallback10(
    (sourceText, sourceName) => {
      const result = parseRuntimeRecordsJson(sourceText);
      const inferred = result.ok ? inferDataDefinition(result.records) : void 0;
      const data = inferred ? reconcileDataDefinition(inferred, normalized.data, result.records) : void 0;
      setCandidate({ sourceName, sourceText, result, ...data ? { data } : {} });
      setImportMode("preview");
    },
    [normalized.data]
  );
  const readFile = useCallback10(
    (file) => {
      activeReaderRef.current?.abort();
      const generation = ++readGenerationRef.current;
      if (!file.name.toLowerCase().endsWith(".json")) {
        setCandidate(invalidFileCandidate(file.name, file.size));
        setImportMode("preview");
        return;
      }
      if (file.size > DATA_SOURCE_LIMITS.maxBytes) {
        setCandidate(oversizedFileCandidate(file.name, file.size));
        setImportMode("preview");
        return;
      }
      const reader = new FileReader();
      activeReaderRef.current = reader;
      reader.onload = () => {
        if (!mountedRef.current || generation !== readGenerationRef.current) return;
        activeReaderRef.current = null;
        parseSource(typeof reader.result === "string" ? reader.result : "", file.name);
      };
      reader.onerror = () => {
        if (!mountedRef.current || generation !== readGenerationRef.current) return;
        activeReaderRef.current = null;
        setCandidate(readErrorCandidate(file.name, file.size));
        setImportMode("preview");
      };
      reader.readAsText(file);
    },
    [parseSource]
  );
  const closeTopSurface = (event) => {
    if (event.key !== "Escape") return;
    if (fieldDraft) {
      event.preventDefault();
      event.stopPropagation();
      setFieldDraft(null);
      requestAnimationFrame(() => fieldTriggerRef.current?.focus());
      return;
    }
    if (importMode !== "closed") {
      event.preventDefault();
      event.stopPropagation();
      closeImport();
    }
  };
  const applyCandidate = () => {
    if (!candidate?.result.ok || !candidate.data) return;
    store.replaceTemplateData(candidate.data);
    closeImport();
  };
  const handleFieldAction = (field) => {
    if (!component || !target || component.isLock) return;
    if (target.kind === "text") {
      if (!composerKey) return;
      const current = composerDraft ?? { kind: "text", segments: [] };
      setComposerState({
        key: composerKey,
        value: {
          kind: "text",
          segments: [...current.segments, { kind: "field", fieldId: field.id }]
        }
      });
      return;
    }
    store.setComponentBinding(component.id, target, { kind: "field", fieldId: field.id });
  };
  const visibleFields = filterFieldTree(fields, query);
  const editedField = fieldDraft ? flatFields.find((field) => field.id === fieldDraft.id) : void 0;
  const impact = candidate?.data ? bindingImpact(
    store.template.value.pages.flatMap((page) => page.componentData),
    candidate.data
  ) : null;
  return /* @__PURE__ */ jsxs16(PanelRoot, { "data-ptd-region": "data-panel", onKeyDownCapture: closeTopSurface, children: [
    /* @__PURE__ */ jsx19(PanelHeader, { title: "\u6570\u636E", meta: `${flatFields.length} \u4E2A\u5B57\u6BB5`, children: /* @__PURE__ */ jsx19(
      "button",
      {
        type: "button",
        className: DataPanel_default.iconButton,
        "aria-label": "\u5173\u95ED\u6570\u636E\u9762\u677F",
        onClick: onClose,
        children: /* @__PURE__ */ jsx19(RiCloseLine5, { "aria-hidden": "true" })
      }
    ) }),
    /* @__PURE__ */ jsxs16(PanelTools, { className: DataPanel_default.tools, children: [
      /* @__PURE__ */ jsx19(
        SegmentedControl,
        {
          label: "\u753B\u5E03\u663E\u793A\u6A21\u5F0F",
          value: store.proofMode.value ? "proof" : "design",
          options: [
            { value: "design", label: "\u8BBE\u8BA1\u5185\u5BB9" },
            { value: "proof", label: "\u6570\u636E\u6821\u6837", disabled: !canProof }
          ],
          onChange: (value) => store.setProofMode(value === "proof")
        }
      ),
      /* @__PURE__ */ jsx19(
        RecordControl,
        {
          count: proofRecords.length,
          index: store.proofRecordIndex.value,
          disabled: !canProof,
          onPrevious: () => store.setProofRecordIndex(store.proofRecordIndex.value - 1),
          onNext: () => store.setProofRecordIndex(store.proofRecordIndex.value + 1)
        }
      ),
      !canProof && /* @__PURE__ */ jsx19("p", { className: DataPanel_default.inlineNote, children: "\u5BFC\u5165\u5E76\u5E94\u7528\u6837\u4F8B\u6570\u636E\u540E\u5373\u53EF\u8FDB\u5165\u6821\u6837\u3002" })
    ] }),
    /* @__PURE__ */ jsx19(PanelBody, { className: DataPanel_default.body, children: importMode !== "closed" ? /* @__PURE__ */ jsx19(
      ImportSurface,
      {
        mode: importMode,
        pasteDraft,
        candidate,
        impact,
        onPasteDraftChange: setPasteDraft,
        onParsePaste: () => parseSource(pasteDraft, "\u7C98\u8D34\u7684 JSON"),
        onBack: () => {
          setCandidate(null);
          setImportMode("paste");
        },
        onApply: applyCandidate,
        onClose: () => closeImport()
      }
    ) : /* @__PURE__ */ jsxs16(Fragment8, { children: [
      /* @__PURE__ */ jsxs16("section", { className: DataPanel_default.sourceSection, "aria-labelledby": "data-source-title", children: [
        /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.sectionHeading, children: [
          /* @__PURE__ */ jsxs16("div", { children: [
            /* @__PURE__ */ jsx19("h3", { id: "data-source-title", children: "\u6570\u636E\u6765\u6E90" }),
            /* @__PURE__ */ jsx19("p", { children: sourceDescription(
              normalized.source,
              normalized.data.sampleRecords?.length ?? 0
            ) })
          ] }),
          /* @__PURE__ */ jsxs16(
            "button",
            {
              ref: importTriggerRef,
              type: "button",
              className: DataPanel_default.actionButton,
              onClick: () => setImportMode("paste"),
              children: [
                /* @__PURE__ */ jsx19(RiFileUploadLine2, { "aria-hidden": "true" }),
                "\u5BFC\u5165 JSON"
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx19(
          FileDropControl,
          {
            fileInputRef,
            onFile: readFile,
            onChoose: () => fileInputRef.current?.click()
          }
        ),
        (normalized.data.sampleRecords?.length ?? 0) > 0 && /* @__PURE__ */ jsxs16(
          "button",
          {
            type: "button",
            className: DataPanel_default.textAction,
            onClick: () => store.removeSampleRecords(),
            children: [
              /* @__PURE__ */ jsx19(RiDeleteBinLine6, { "aria-hidden": "true" }),
              "\u79FB\u9664\u6837\u4F8B\uFF0C\u4FDD\u7559\u5B57\u6BB5\u4E0E\u7ED1\u5B9A"
            ]
          }
        ),
        normalized.diagnostics.length > 0 && /* @__PURE__ */ jsx19(DiagnosticList, { diagnostics: normalized.diagnostics, title: "\u517C\u5BB9\u6027\u4FE1\u606F" })
      ] }),
      /* @__PURE__ */ jsxs16("section", { className: DataPanel_default.bindingSection, "aria-labelledby": "binding-title", children: [
        /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.sectionHeading, children: [
          /* @__PURE__ */ jsxs16("div", { children: [
            /* @__PURE__ */ jsx19("h3", { id: "binding-title", children: "\u5F53\u524D\u7ED1\u5B9A" }),
            /* @__PURE__ */ jsx19("p", { children: bindingState })
          ] }),
          component && target && binding && !component.isLock && /* @__PURE__ */ jsx19(
            "button",
            {
              type: "button",
              className: DataPanel_default.iconButton,
              "aria-label": `\u89E3\u9664${targetLabel(target)}\u7ED1\u5B9A`,
              onClick: () => store.removeComponentBinding(component.id, target),
              children: /* @__PURE__ */ jsx19(RiLinkUnlink, { "aria-hidden": "true" })
            }
          )
        ] }),
        component && target?.kind === "text" && composerDraft && !component.isLock && /* @__PURE__ */ jsx19(
          TextComposer,
          {
            value: composerDraft,
            fields: flatFields,
            onChange: (value) => {
              if (composerKey) setComposerState({ key: composerKey, value });
            },
            onApply: () => store.setComponentBinding(component.id, target, composerDraft)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs16("section", { className: DataPanel_default.fieldsSection, "aria-labelledby": "field-tree-title", children: [
        /* @__PURE__ */ jsx19("div", { className: DataPanel_default.sectionHeading, children: /* @__PURE__ */ jsxs16("div", { children: [
          /* @__PURE__ */ jsx19("h3", { id: "field-tree-title", children: "\u5B57\u6BB5\u6A21\u578B" }),
          /* @__PURE__ */ jsx19("p", { children: "\u9009\u62E9\u5B57\u6BB5\u5373\u53EF\u7ED1\u5B9A\u5230\u5F53\u524D\u5BF9\u8C61\u7684\u517C\u5BB9\u5C5E\u6027\u3002" })
        ] }) }),
        /* @__PURE__ */ jsx19(SearchControl, { value: query, onChange: setQuery }),
        fieldDraft && editedField ? /* @__PURE__ */ jsx19(
          FieldEditor,
          {
            draft: fieldDraft,
            field: editedField,
            onChange: setFieldDraft,
            onCancel: () => {
              setFieldDraft(null);
              requestAnimationFrame(() => fieldTriggerRef.current?.focus());
            },
            onApply: () => {
              store.updateDataField(fieldDraft.id, {
                name: fieldDraft.name,
                ...fieldDraft.formatter ? { formatter: fieldDraft.formatter } : { removeFormatter: true }
              });
              setFieldDraft(null);
              requestAnimationFrame(() => fieldTriggerRef.current?.focus());
            }
          }
        ) : visibleFields.length > 0 ? /* @__PURE__ */ jsx19("div", { className: DataPanel_default.fieldTree, role: "tree", "aria-label": "\u6570\u636E\u5B57\u6BB5", children: visibleFields.map((field) => /* @__PURE__ */ jsx19(
          FieldNode,
          {
            field,
            depth: 0,
            query,
            expanded,
            isFieldCompatible: (candidateField) => isCompatible(candidateField, component, target),
            actionLabel: target?.kind === "text" ? "\u52A0\u5165\u7EC4\u5408\u6587\u672C" : "\u7ED1\u5B9A\u5B57\u6BB5",
            onToggle: (fieldId) => setExpanded((current) => {
              const next = new Set(current);
              if (next.has(fieldId)) next.delete(fieldId);
              else next.add(fieldId);
              return next;
            }),
            onBind: handleFieldAction,
            onEdit: (field2, trigger) => {
              fieldTriggerRef.current = trigger;
              setFieldDraft({
                id: field2.id,
                name: field2.name,
                ...field2.formatter ? { formatter: field2.formatter } : {}
              });
            }
          },
          field.id
        )) }) : /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.emptyState, children: [
          /* @__PURE__ */ jsx19("strong", { children: query ? "\u6CA1\u6709\u5339\u914D\u5B57\u6BB5" : "\u8FD8\u6CA1\u6709\u6570\u636E\u5B57\u6BB5" }),
          /* @__PURE__ */ jsx19("span", { children: query ? "\u8C03\u6574\u5B57\u6BB5\u540D\u79F0\u6216\u8DEF\u5F84\u5173\u952E\u8BCD\u3002" : "\u5BFC\u5165 JSON \u540E\u5148\u68C0\u67E5\u7ED3\u6784\uFF0C\u518D\u660E\u786E\u5E94\u7528\u3002" })
        ] })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx19(PanelFooter, { children: store.hostRenderContext.value ? "\u5F53\u524D\u6821\u6837\u4F18\u5148\u4F7F\u7528 Host \u4E34\u65F6\u6570\u636E\uFF0C\u4E0D\u4F1A\u5199\u5165\u6A21\u677F" : "\u5B57\u6BB5\u3001\u6837\u4F8B\u4E0E\u7ED1\u5B9A\u4F1A\u968F\u6A21\u677F\u7248\u672C\u4FDD\u5B58" })
  ] });
}
function FileDropControl({
  fileInputRef,
  onFile,
  onChoose
}) {
  const [dragging, setDragging] = useState13(false);
  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) onFile(file);
  };
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      className: DataPanel_default.dropControl,
      "data-dragging": dragging || void 0,
      onDragEnter: (event) => {
        event.preventDefault();
        setDragging(true);
      },
      onDragOver: (event) => event.preventDefault(),
      onDragLeave: () => setDragging(false),
      onDrop,
      children: [
        /* @__PURE__ */ jsx19(
          "input",
          {
            ref: fileInputRef,
            className: DataPanel_default.visuallyHidden,
            type: "file",
            accept: "application/json,.json",
            "aria-label": "\u9009\u62E9 JSON \u6570\u636E\u6587\u4EF6",
            onChange: (event) => {
              const file = event.currentTarget.files?.[0];
              if (file) onFile(file);
              event.currentTarget.value = "";
            }
          }
        ),
        /* @__PURE__ */ jsx19("span", { children: "\u5C06 .json \u6587\u4EF6\u62D6\u5230\u8FD9\u91CC" }),
        /* @__PURE__ */ jsx19("button", { type: "button", className: DataPanel_default.secondaryButton, onClick: onChoose, children: "\u9009\u62E9\u6587\u4EF6" })
      ]
    }
  );
}
function ImportSurface({
  mode,
  pasteDraft,
  candidate,
  impact,
  onPasteDraftChange,
  onParsePaste,
  onBack,
  onApply,
  onClose
}) {
  const titleId = useId4();
  if (mode === "paste") {
    return /* @__PURE__ */ jsxs16("section", { className: DataPanel_default.importSurface, "aria-labelledby": titleId, children: [
      /* @__PURE__ */ jsx19(SurfaceHeader, { title: "\u7C98\u8D34 JSON \u6570\u636E", titleId, onClose }),
      /* @__PURE__ */ jsx19(PasteControl, { value: pasteDraft, onChange: onPasteDraftChange }),
      /* @__PURE__ */ jsxs16("p", { className: DataPanel_default.inlineNote, children: [
        "\u63A5\u53D7\u4E00\u4E2A\u5BF9\u8C61\u6216\u5BF9\u8C61\u6570\u7EC4\uFF0C\u6700\u5927 ",
        formatBytes(DATA_SOURCE_LIMITS.maxBytes),
        "\u3001",
        DATA_SOURCE_LIMITS.maxRecords,
        " \u6761\u8BB0\u5F55\u3002"
      ] }),
      /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.actionRow, children: [
        /* @__PURE__ */ jsx19(
          "button",
          {
            type: "button",
            className: DataPanel_default.primaryButton,
            disabled: !pasteDraft.trim(),
            onClick: onParsePaste,
            children: "\u68C0\u67E5\u6570\u636E\u7ED3\u6784"
          }
        ),
        /* @__PURE__ */ jsx19("button", { type: "button", className: DataPanel_default.secondaryButton, onClick: onClose, children: "\u53D6\u6D88\u5BFC\u5165" })
      ] })
    ] });
  }
  const summary = candidate?.result.summary;
  return /* @__PURE__ */ jsxs16("section", { className: DataPanel_default.importSurface, "aria-labelledby": titleId, children: [
    /* @__PURE__ */ jsx19(SurfaceHeader, { title: "\u5BFC\u5165\u68C0\u67E5", titleId, onClose }),
    /* @__PURE__ */ jsx19("p", { className: DataPanel_default.sourceName, children: candidate?.sourceName }),
    summary && /* @__PURE__ */ jsxs16("dl", { className: DataPanel_default.summaryGrid, children: [
      /* @__PURE__ */ jsx19(SummaryMetric, { label: "\u8BB0\u5F55", value: String(summary.recordCount) }),
      /* @__PURE__ */ jsx19(SummaryMetric, { label: "\u5B57\u6BB5", value: String(summary.fieldCount) }),
      /* @__PURE__ */ jsx19(SummaryMetric, { label: "\u4F53\u79EF", value: formatBytes(summary.byteCount) }),
      /* @__PURE__ */ jsx19(SummaryMetric, { label: "\u6DF1\u5EA6", value: String(summary.maxDepth) })
    ] }),
    candidate && candidate.result.diagnostics.length > 0 && /* @__PURE__ */ jsx19(DiagnosticList, { diagnostics: candidate.result.diagnostics, title: "\u68C0\u67E5\u7ED3\u679C" }),
    candidate?.result.ok && impact && /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.impactSummary, children: [
      /* @__PURE__ */ jsx19("strong", { children: "\u5E94\u7528\u5F71\u54CD" }),
      /* @__PURE__ */ jsxs16("span", { children: [
        "\u4FDD\u7559 ",
        impact.valid,
        " \u4E2A\u73B0\u6709\u5B57\u6BB5\u5F15\u7528"
      ] }),
      /* @__PURE__ */ jsx19("span", { children: impact.invalid > 0 ? `${impact.invalid} \u4E2A\u7ED1\u5B9A\u5C06\u5931\u6548\u4F46\u4E0D\u4F1A\u88AB\u5220\u9664` : "\u73B0\u6709\u7ED1\u5B9A\u4E0D\u4F1A\u5931\u6548" })
    ] }),
    /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.actionRow, children: [
      /* @__PURE__ */ jsx19(
        "button",
        {
          type: "button",
          className: DataPanel_default.primaryButton,
          disabled: !candidate?.result.ok,
          onClick: onApply,
          children: "\u5E94\u7528\u5B57\u6BB5\u4E0E\u6837\u4F8B"
        }
      ),
      /* @__PURE__ */ jsx19("button", { type: "button", className: DataPanel_default.secondaryButton, onClick: onBack, children: "\u8FD4\u56DE\u4FEE\u6539" })
    ] })
  ] });
}
function SurfaceHeader({
  title,
  titleId,
  onClose
}) {
  return /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.surfaceHeader, children: [
    /* @__PURE__ */ jsx19("h3", { id: titleId, children: title }),
    /* @__PURE__ */ jsx19(
      "button",
      {
        type: "button",
        className: DataPanel_default.iconButton,
        "aria-label": `\u5173\u95ED${title}`,
        onClick: onClose,
        children: /* @__PURE__ */ jsx19(RiCloseLine5, { "aria-hidden": "true" })
      }
    )
  ] });
}
function PasteControl({ value, onChange }) {
  const id = useId4();
  return /* @__PURE__ */ jsxs16("label", { className: DataPanel_default.pasteControl, htmlFor: id, children: [
    /* @__PURE__ */ jsx19("span", { children: "JSON \u5185\u5BB9" }),
    /* @__PURE__ */ jsx19(
      "textarea",
      {
        id,
        value,
        spellCheck: false,
        placeholder: '\u4F8B\u5982\uFF1A[{\n  "orderNo": "CC-2026-0815"\n}]',
        onChange: (event) => onChange(event.currentTarget.value)
      }
    )
  ] });
}
function SearchControl({ value, onChange }) {
  return /* @__PURE__ */ jsxs16("label", { className: DataPanel_default.searchControl, children: [
    /* @__PURE__ */ jsx19(RiSearchLine2, { "aria-hidden": "true" }),
    /* @__PURE__ */ jsx19("span", { className: DataPanel_default.visuallyHidden, children: "\u641C\u7D22\u5B57\u6BB5\u540D\u79F0\u6216\u8DEF\u5F84" }),
    /* @__PURE__ */ jsx19(
      "input",
      {
        value,
        placeholder: "\u641C\u7D22\u540D\u79F0\u6216\u8DEF\u5F84",
        onChange: (event) => onChange(event.currentTarget.value)
      }
    ),
    value && /* @__PURE__ */ jsx19("button", { type: "button", "aria-label": "\u6E05\u9664\u5B57\u6BB5\u641C\u7D22", onClick: () => onChange(""), children: /* @__PURE__ */ jsx19(RiCloseLine5, { "aria-hidden": "true" }) })
  ] });
}
function SegmentedControl({
  label,
  value,
  options,
  onChange
}) {
  return /* @__PURE__ */ jsx19("div", { className: DataPanel_default.segmented, role: "group", "aria-label": label, children: options.map((option) => /* @__PURE__ */ jsx19(
    "button",
    {
      type: "button",
      "aria-pressed": value === option.value,
      disabled: option.disabled,
      onClick: () => onChange(option.value),
      children: option.label
    },
    option.value
  )) });
}
function RecordControl({
  count,
  index,
  disabled,
  onPrevious,
  onNext
}) {
  return /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.recordControl, role: "group", "aria-label": "\u6821\u6837\u8BB0\u5F55", children: [
    /* @__PURE__ */ jsx19(
      "button",
      {
        type: "button",
        "aria-label": "\u4E0A\u4E00\u6761\u6837\u4F8B\u8BB0\u5F55",
        disabled: disabled || index <= 0,
        onClick: onPrevious,
        children: /* @__PURE__ */ jsx19(RiArrowLeftSLine, { "aria-hidden": "true" })
      }
    ),
    /* @__PURE__ */ jsx19("output", { "aria-live": "polite", children: count > 0 ? `${index + 1} / ${count}` : "0 / 0" }),
    /* @__PURE__ */ jsx19(
      "button",
      {
        type: "button",
        "aria-label": "\u4E0B\u4E00\u6761\u6837\u4F8B\u8BB0\u5F55",
        disabled: disabled || index >= count - 1,
        onClick: onNext,
        children: /* @__PURE__ */ jsx19(RiArrowRightSLine2, { "aria-hidden": "true" })
      }
    )
  ] });
}
function FieldNode({
  field,
  depth,
  query,
  expanded,
  isFieldCompatible,
  actionLabel,
  onToggle,
  onBind,
  onEdit
}) {
  const children = field.children ?? [];
  const open = query.trim() !== "" || expanded.has(field.id);
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      role: "treeitem",
      "aria-expanded": children.length ? open : void 0,
      className: DataPanel_default.fieldBranch,
      "data-depth": depth,
      children: [
        /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.fieldRow, children: [
          children.length > 0 ? /* @__PURE__ */ jsx19(
            "button",
            {
              type: "button",
              className: DataPanel_default.disclosure,
              "aria-label": `${open ? "\u6536\u8D77" : "\u5C55\u5F00"}${field.name}`,
              onClick: () => onToggle(field.id),
              children: /* @__PURE__ */ jsx19(RiArrowDownSLine4, { "aria-hidden": "true" })
            }
          ) : /* @__PURE__ */ jsx19("span", { className: DataPanel_default.disclosureSpacer }),
          /* @__PURE__ */ jsxs16(
            "button",
            {
              type: "button",
              className: DataPanel_default.fieldMain,
              disabled: !isFieldCompatible(field),
              "aria-label": `${actionLabel}\uFF1A${field.name}\uFF0C\u8DEF\u5F84 ${formatDataPath(field.path)}`,
              onClick: () => onBind(field),
              children: [
                /* @__PURE__ */ jsx19("span", { className: DataPanel_default.fieldName, children: field.name }),
                /* @__PURE__ */ jsx19("span", { className: DataPanel_default.fieldPath, children: formatDataPath(field.path) })
              ]
            }
          ),
          /* @__PURE__ */ jsx19("span", { className: DataPanel_default.typeLabel, children: TYPE_LABELS[field.valueType] }),
          /* @__PURE__ */ jsx19(
            "button",
            {
              type: "button",
              className: DataPanel_default.editField,
              "aria-label": `\u7F16\u8F91\u5B57\u6BB5 ${field.name}`,
              onClick: (event) => onEdit(field, event.currentTarget),
              children: "\u7F16\u8F91"
            }
          )
        ] }),
        children.length > 0 && open && /* @__PURE__ */ jsx19("div", { role: "group", className: DataPanel_default.fieldChildren, children: children.map((child) => /* @__PURE__ */ jsx19(
          FieldNode,
          {
            field: child,
            depth: depth + 1,
            query,
            expanded,
            isFieldCompatible,
            actionLabel,
            onToggle,
            onBind,
            onEdit
          },
          child.id
        )) })
      ]
    }
  );
}
function FieldEditor({
  draft,
  field,
  onChange,
  onCancel,
  onApply
}) {
  const nameId = useId4();
  const presets = formatterPresets(field);
  return /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.fieldEditor, "aria-label": `\u7F16\u8F91\u5B57\u6BB5 ${field.name}`, children: [
    /* @__PURE__ */ jsxs16("label", { className: DataPanel_default.textControl, htmlFor: nameId, children: [
      /* @__PURE__ */ jsx19("span", { children: "\u663E\u793A\u540D\u79F0" }),
      /* @__PURE__ */ jsx19(
        "input",
        {
          id: nameId,
          autoFocus: true,
          value: draft.name,
          onChange: (event) => onChange({ ...draft, name: event.currentTarget.value })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.presetControl, children: [
      /* @__PURE__ */ jsx19("span", { children: "\u9ED8\u8BA4\u683C\u5F0F" }),
      /* @__PURE__ */ jsx19("div", { role: "group", "aria-label": "\u5B57\u6BB5\u9ED8\u8BA4\u683C\u5F0F", children: presets.map((preset) => /* @__PURE__ */ jsx19(
        "button",
        {
          type: "button",
          "aria-pressed": sameFormatter(draft.formatter, preset.formatter),
          onClick: () => onChange({ ...draft, formatter: preset.formatter }),
          children: preset.label
        },
        preset.label
      )) })
    ] }),
    /* @__PURE__ */ jsxs16("p", { className: DataPanel_default.fieldIdentity, children: [
      "\u5B57\u6BB5 ID \u4E0E\u8DEF\u5F84\u4FDD\u6301\u4E0D\u53D8\uFF1A",
      formatDataPath(field.path)
    ] }),
    /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.actionRow, children: [
      /* @__PURE__ */ jsx19(
        "button",
        {
          type: "button",
          className: DataPanel_default.primaryButton,
          disabled: !draft.name.trim(),
          onClick: onApply,
          children: "\u4FDD\u5B58\u5B57\u6BB5"
        }
      ),
      /* @__PURE__ */ jsx19("button", { type: "button", className: DataPanel_default.secondaryButton, onClick: onCancel, children: "\u53D6\u6D88\u7F16\u8F91" })
    ] })
  ] });
}
function TextComposer({
  value,
  fields,
  onChange,
  onApply
}) {
  const addLiteral = () => onChange({ ...value, segments: [...value.segments, { kind: "literal", value: "" }] });
  return /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.composer, "aria-label": "\u7EC4\u5408\u6587\u672C\u7F16\u8F91\u5668", children: [
    /* @__PURE__ */ jsx19("div", { className: DataPanel_default.composerSegments, children: value.segments.map(
      (segment, index) => segment.kind === "literal" ? /* @__PURE__ */ jsxs16("label", { className: DataPanel_default.literalSegment, children: [
        /* @__PURE__ */ jsxs16("span", { children: [
          "\u56FA\u5B9A\u6587\u5B57 ",
          index + 1
        ] }),
        /* @__PURE__ */ jsx19(
          "input",
          {
            value: segment.value,
            onChange: (event) => onChange(
              replaceSegment(value, index, {
                kind: "literal",
                value: event.currentTarget.value
              })
            )
          }
        ),
        /* @__PURE__ */ jsx19(
          "button",
          {
            type: "button",
            "aria-label": `\u79FB\u9664\u56FA\u5B9A\u6587\u5B57 ${index + 1}`,
            onClick: () => onChange(removeSegment(value, index)),
            children: /* @__PURE__ */ jsx19(RiCloseLine5, { "aria-hidden": "true" })
          }
        )
      ] }, `literal-${index}`) : /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.fieldToken, children: [
        /* @__PURE__ */ jsx19(RiLink2, { "aria-hidden": "true" }),
        /* @__PURE__ */ jsx19("span", { children: fields.find((field) => field.id === segment.fieldId)?.name ?? `\u5931\u6548\u5B57\u6BB5 ${segment.fieldId}` }),
        /* @__PURE__ */ jsx19(
          "button",
          {
            type: "button",
            "aria-label": `\u79FB\u9664\u5B57\u6BB5\u7247\u6BB5 ${index + 1}`,
            onClick: () => onChange(removeSegment(value, index)),
            children: /* @__PURE__ */ jsx19(RiCloseLine5, { "aria-hidden": "true" })
          }
        )
      ] }, `field-${index}`)
    ) }),
    /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.actionRow, children: [
      /* @__PURE__ */ jsx19("button", { type: "button", className: DataPanel_default.secondaryButton, onClick: addLiteral, children: "\u6DFB\u52A0\u56FA\u5B9A\u6587\u5B57" }),
      /* @__PURE__ */ jsx19(
        "button",
        {
          type: "button",
          className: DataPanel_default.primaryButton,
          disabled: value.segments.length === 0,
          onClick: onApply,
          children: "\u5E94\u7528\u7EC4\u5408\u6587\u672C"
        }
      )
    ] })
  ] });
}
function DiagnosticList({
  diagnostics,
  title
}) {
  return /* @__PURE__ */ jsxs16("div", { className: DataPanel_default.diagnostics, "aria-live": "polite", children: [
    /* @__PURE__ */ jsx19("strong", { children: title }),
    /* @__PURE__ */ jsx19("ul", { children: diagnostics.map((diagnostic, index) => /* @__PURE__ */ jsxs16("li", { "data-severity": diagnostic.severity, children: [
      /* @__PURE__ */ jsx19("span", { children: diagnostic.severity === "error" ? "\u9519\u8BEF" : diagnostic.severity === "warning" ? "\u63D0\u9192" : "\u4FE1\u606F" }),
      diagnostic.message
    ] }, `${diagnostic.code}-${index}`)) })
  ] });
}
function SummaryMetric({ label, value }) {
  return /* @__PURE__ */ jsxs16("div", { children: [
    /* @__PURE__ */ jsx19("dt", { children: label }),
    /* @__PURE__ */ jsx19("dd", { children: value })
  ] });
}
function filterFieldTree(fields, query) {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return fields;
  return fields.flatMap((field) => {
    const children = filterFieldTree(field.children ?? [], query);
    const matches = `${field.name} ${formatDataPath(field.path)} ${field.valueType}`.toLocaleLowerCase().includes(normalized);
    return matches || children.length > 0 ? [{ ...field, ...children.length > 0 ? { children } : {} }] : [];
  });
}
function reconcileDataDefinition(inferred, existing, records) {
  const oldByPath = new Map(
    flattenDataFields(existing.fields).map((field) => [JSON.stringify(field.path), field])
  );
  const reconcile = (field) => {
    const old = oldByPath.get(JSON.stringify(field.path));
    const children = field.children?.map(reconcile);
    return {
      ...field,
      ...old ? { id: old.id, name: old.name, ...old.formatter ? { formatter: old.formatter } : {} } : {},
      ...children ? { children } : {}
    };
  };
  return { version: 1, fields: inferred.fields.map(reconcile), sampleRecords: records };
}
function getConcreteTarget(component, tableSelection) {
  if (!component) return null;
  const definitions = getComponentBindingTargets2(component.component);
  const definition = definitions[0];
  if (!definition) return null;
  if (definition.kind !== "table-cell-text") return { kind: definition.kind };
  if (!tableSelection || tableSelection.componentId !== component.id) return null;
  const cell = getTableCellAt4(
    normalizeSimpleTableProps5(component.propValue),
    tableSelection.focusRow,
    tableSelection.focusColumn
  );
  return cell ? { kind: "table-cell-text", cellId: cell.id } : null;
}
function findBinding(bindings, target) {
  return bindings?.find(
    (binding) => binding.target.kind === target.kind && (target.kind !== "table-cell-text" || binding.target.kind === "table-cell-text" && binding.target.cellId === target.cellId)
  );
}
function bindingTargetKey(target) {
  return target.kind === "table-cell-text" ? `${target.kind}:${target.cellId}` : target.kind;
}
function describeBindingState(selected, component, target) {
  if (selected.length === 0) return "\u9009\u62E9\u4E00\u4E2A\u652F\u6301\u6570\u636E\u7ED1\u5B9A\u7684\u7EC4\u4EF6\u3002";
  if (selected.length > 1) return "\u591A\u9009\u72B6\u6001\u4E0D\u80FD\u5EFA\u7ACB\u5B57\u6BB5\u7ED1\u5B9A\uFF0C\u8BF7\u53EA\u9009\u62E9\u4E00\u4E2A\u7EC4\u4EF6\u3002";
  if (component?.isLock) return "\u5F53\u524D\u7EC4\u4EF6\u5DF2\u9501\u5B9A\uFF0C\u89E3\u9501\u540E\u624D\u80FD\u4FEE\u6539\u7ED1\u5B9A\u3002";
  if (component?.component === "RoySimpleTable" && !target)
    return "\u9009\u62E9\u81EA\u7531\u8868\u683C\u4E2D\u7684\u4E00\u4E2A\u5355\u5143\u683C\u540E\u5373\u53EF\u7ED1\u5B9A\u3002";
  if (!target) return "\u5F53\u524D\u7EC4\u4EF6\u6CA1\u6709\u53EF\u7528\u7684\u6570\u636E\u7ED1\u5B9A\u5C5E\u6027\u3002";
  return `${component?.name || component?.component} \xB7 ${targetLabel(target)}`;
}
function targetLabel(target) {
  if (target.kind === "text") return "\u6587\u672C\u5185\u5BB9";
  if (target.kind === "rich-text") return "\u5BCC\u6587\u672C\u5185\u5BB9";
  if (target.kind === "image-source") return "\u56FE\u7247\u5730\u5740";
  if (target.kind === "code-content") return "\u7F16\u7801\u5185\u5BB9";
  return `\u5355\u5143\u683C ${target.cellId}`;
}
function isCompatible(field, component, target) {
  if (!component || !target || component.isLock || field.valueType === "object" || field.valueType === "array")
    return false;
  const definition = getComponentBindingTargets2(component.component).find(
    (item) => item.kind === target.kind
  );
  return Boolean(definition?.acceptedTypes.includes(field.valueType));
}
function toTextExpression(expression, staticValue) {
  if (expression?.kind === "text") return { kind: "text", segments: [...expression.segments] };
  if (expression?.kind === "field") return { kind: "text", segments: [expression] };
  const literal = typeof staticValue === "string" ? staticValue : "";
  return { kind: "text", segments: literal ? [{ kind: "literal", value: literal }] : [] };
}
function replaceSegment(value, index, segment) {
  return {
    ...value,
    segments: value.segments.map(
      (current, currentIndex) => currentIndex === index ? segment : current
    )
  };
}
function removeSegment(value, index) {
  return { ...value, segments: value.segments.filter((_, currentIndex) => currentIndex !== index) };
}
function bindingImpact(components, data) {
  const ids = new Set(flattenDataFields(data.fields).map((field) => field.id));
  let valid = 0;
  let invalid = 0;
  const inspectExpression = (expression) => {
    const segments = expression.kind === "field" ? [expression] : expression.segments.filter((segment) => segment.kind === "field");
    segments.forEach((segment) => {
      if (segment.kind !== "field") return;
      if (ids.has(segment.fieldId)) valid += 1;
      else invalid += 1;
    });
  };
  const visit = (component) => {
    component.bindings?.forEach((binding) => inspectExpression(binding.expression));
    if (component.component === "RoyGroup" && Array.isArray(component.propValue)) {
      ;
      component.propValue.forEach(visit);
    }
  };
  components.forEach(visit);
  return { valid, invalid };
}
function formatterPresets(field) {
  if (field.valueType === "number")
    return [
      { label: "\u539F\u503C" },
      {
        label: "\u5343\u5206\u4F4D",
        formatter: { kind: "number", useGrouping: true, maximumFractionDigits: 2 }
      },
      {
        label: "\u4EBA\u6C11\u5E01",
        formatter: { kind: "currency", currency: "CNY", maximumFractionDigits: 2 }
      },
      { label: "\u5927\u5199\u91D1\u989D", formatter: { kind: "chinese-currency" } }
    ];
  if (field.valueType === "date")
    return [
      { label: "\u539F\u503C" },
      { label: "\u5E74\u6708\u65E5", formatter: { kind: "date", pattern: "YYYY-MM-DD" } },
      { label: "\u4E2D\u6587\u65E5\u671F", formatter: { kind: "date", pattern: "YYYY\u5E74M\u6708D\u65E5" } }
    ];
  if (field.valueType === "object" || field.valueType === "array")
    return [{ label: "\u539F\u503C" }, { label: "JSON", formatter: { kind: "json" } }];
  return [{ label: "\u539F\u503C" }];
}
function sameFormatter(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}
function sourceDescription(source, records) {
  if (source === "legacy") return `\u65E7\u6A21\u677F\u6570\u636E\u5DF2\u517C\u5BB9\u8BFB\u53D6 \xB7 ${records} \u6761\u6837\u4F8B`;
  if (source === "canonical")
    return records > 0 ? `\u6A21\u677F\u6837\u4F8B \xB7 ${records} \u6761\u8BB0\u5F55` : "\u5B57\u6BB5\u6A21\u578B\u5DF2\u5B9A\u4E49\uFF0C\u672A\u4FDD\u5B58\u6837\u4F8B";
  return "\u5C1A\u672A\u5B9A\u4E49\u5B57\u6BB5\u6A21\u578B";
}
function formatBytes(bytes) {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
}
function invalidFileCandidate(name, byteCount) {
  return {
    sourceName: name,
    sourceText: "",
    result: {
      ok: false,
      records: [],
      diagnostics: [
        { code: "invalid-root", severity: "error", message: "\u53EA\u80FD\u5BFC\u5165\u6269\u5C55\u540D\u4E3A .json \u7684\u6570\u636E\u6587\u4EF6\u3002" }
      ],
      summary: { root: "invalid", recordCount: 0, fieldCount: 0, byteCount, maxDepth: 0 }
    }
  };
}
function readErrorCandidate(name, byteCount) {
  return {
    sourceName: name,
    sourceText: "",
    result: {
      ok: false,
      records: [],
      diagnostics: [
        {
          code: "unsupported-value",
          severity: "error",
          message: "\u6587\u4EF6\u8BFB\u53D6\u5931\u8D25\uFF0C\u8BF7\u91CD\u65B0\u9009\u62E9\u672C\u5730 JSON \u6587\u4EF6\u3002"
        }
      ],
      summary: { root: "invalid", recordCount: 0, fieldCount: 0, byteCount, maxDepth: 0 }
    }
  };
}
function oversizedFileCandidate(name, byteCount) {
  return {
    sourceName: name,
    sourceText: "",
    result: {
      ok: false,
      records: [],
      diagnostics: [
        {
          code: "max-bytes",
          severity: "error",
          message: `JSON \u6587\u4EF6\u4F53\u79EF ${byteCount} \u5B57\u8282\u8D85\u8FC7\u4E0A\u9650 ${DATA_SOURCE_LIMITS.maxBytes} \u5B57\u8282\u3002`
        }
      ],
      summary: { root: "invalid", recordCount: 0, fieldCount: 0, byteCount, maxDepth: 0 }
    }
  };
}

// src/components/Sidebar/Sidebar.module.css
var Sidebar_default = {
  sidebar: "Sidebar_sidebar",
  toolDock: "Sidebar_toolDock",
  dockZone: "Sidebar_dockZone",
  dockButton: "Sidebar_dockButton",
  closePanel: "Sidebar_closePanel",
  resizeHandle: "Sidebar_resizeHandle",
  layerRow: "Sidebar_layerRow",
  pageRow: "Sidebar_pageRow",
  pageAction: "Sidebar_pageAction",
  emptyState: "Sidebar_emptyState",
  panelSlot: "Sidebar_panelSlot",
  pageList: "Sidebar_pageList",
  layerList: "Sidebar_layerList",
  pageItem: "Sidebar_pageItem",
  pageDragIcon: "Sidebar_pageDragIcon",
  pageNumber: "Sidebar_pageNumber",
  pageActions: "Sidebar_pageActions",
  layerName: "Sidebar_layerName",
  layerIndex: "Sidebar_layerIndex",
  lockIcon: "Sidebar_lockIcon",
  tooltip: "Sidebar_tooltip",
  tooltipArrow: "Sidebar_tooltipArrow"
};

// src/components/Sidebar/Sidebar.tsx
import { jsx as jsx20, jsxs as jsxs17 } from "react/jsx-runtime";
var RESOURCE_PANELS = [
  { value: "assets", label: "\u7D20\u6750", icon: RiGalleryLine },
  { value: "pages", label: "\u9875\u9762", icon: RiPagesLine3 },
  { value: "layers", label: "\u56FE\u5C42", icon: RiStackLine2 },
  { value: "data", label: "\u6570\u636E", icon: RiDatabase2Line2 }
];
var PTD_PAGE_MIME = "application/x-ptd-page";
function Sidebar({ mode, activePanel, open, onTogglePanel, onResizeStart }) {
  useSignals12();
  const store = useEditorStore();
  return /* @__PURE__ */ jsx20(
    "aside",
    {
      className: Sidebar_default.sidebar,
      "data-mode": mode,
      "data-open": open,
      "data-ptd-region": "left-sidebar",
      children: /* @__PURE__ */ jsxs17(Tooltip4.Provider, { delayDuration: 400, skipDelayDuration: 120, children: [
        /* @__PURE__ */ jsx20("nav", { className: Sidebar_default.toolDock, "aria-label": "\u5DE5\u4F5C\u533A\u8D44\u6E90\u9762\u677F", children: /* @__PURE__ */ jsx20("div", { className: Sidebar_default.dockZone, role: "group", "aria-label": "\u8D44\u6E90\u9762\u677F", children: RESOURCE_PANELS.map(({ value, label, icon: Icon }) => /* @__PURE__ */ jsx20(
          DockButton2,
          {
            label: `${open && activePanel === value ? "\u5173\u95ED" : "\u6253\u5F00"}${label}\u9762\u677F`,
            pressed: open && activePanel === value,
            onClick: () => onTogglePanel(value),
            children: /* @__PURE__ */ jsx20(Icon, {})
          },
          value
        )) }) }),
        /* @__PURE__ */ jsxs17("div", { className: Sidebar_default.panelSlot, hidden: !open, "data-ptd-region": "resource-panel", children: [
          activePanel === "assets" && /* @__PURE__ */ jsx20(
            AssetsPanel,
            {
              onClose: () => onTogglePanel("assets"),
              onDrawImage: () => {
                const image = findAvailableCatalogItem("RoyImage");
                if (image) store.setActiveTool(image.type);
                if (mode === "compact") onTogglePanel("assets");
              }
            }
          ),
          activePanel === "pages" && /* @__PURE__ */ jsx20(PagesPanel, { onClose: () => onTogglePanel("pages") }),
          activePanel === "layers" && /* @__PURE__ */ jsx20(LayersPanel, { onClose: () => onTogglePanel("layers") }),
          activePanel === "data" && /* @__PURE__ */ jsx20(DataPanel, { onClose: () => onTogglePanel("data") }),
          /* @__PURE__ */ jsx20(
            "button",
            {
              type: "button",
              className: Sidebar_default.resizeHandle,
              "aria-label": "\u8C03\u6574\u8D44\u6E90\u9762\u677F\u5BBD\u5EA6",
              onPointerDown: onResizeStart
            }
          )
        ] })
      ] })
    }
  );
}
function DockButton2({
  label,
  pressed,
  children,
  onClick
}) {
  return /* @__PURE__ */ jsxs17(Tooltip4.Root, { children: [
    /* @__PURE__ */ jsx20(Tooltip4.Trigger, { asChild: true, children: /* @__PURE__ */ jsx20(
      "button",
      {
        type: "button",
        className: Sidebar_default.dockButton,
        "aria-label": label,
        "aria-pressed": pressed,
        "data-state-kind": "panel",
        onClick,
        children
      }
    ) }),
    /* @__PURE__ */ jsx20(Tooltip4.Portal, { children: /* @__PURE__ */ jsxs17(
      Tooltip4.Content,
      {
        className: `${Sidebar_default.tooltip} ${ptdThemeClass}`,
        side: "right",
        sideOffset: 8,
        children: [
          /* @__PURE__ */ jsx20("span", { children: label }),
          /* @__PURE__ */ jsx20(Tooltip4.Arrow, { className: Sidebar_default.tooltipArrow })
        ]
      }
    ) })
  ] });
}
function PanelCloseButton({ label, onClick }) {
  return /* @__PURE__ */ jsx20("button", { type: "button", className: Sidebar_default.closePanel, "aria-label": label, onClick, children: /* @__PURE__ */ jsx20(RiCloseLine6, { "aria-hidden": "true" }) });
}
function PageActionButton({
  label,
  disabled = false,
  danger = false,
  onClick,
  children
}) {
  return /* @__PURE__ */ jsxs17(Tooltip4.Root, { children: [
    /* @__PURE__ */ jsx20(Tooltip4.Trigger, { asChild: true, children: /* @__PURE__ */ jsx20(
      "button",
      {
        type: "button",
        className: Sidebar_default.pageAction,
        "aria-label": label,
        "data-danger": danger || void 0,
        disabled,
        onClick,
        children
      }
    ) }),
    /* @__PURE__ */ jsx20(Tooltip4.Portal, { children: /* @__PURE__ */ jsxs17(Tooltip4.Content, { className: `${Sidebar_default.tooltip} ${ptdThemeClass}`, side: "top", sideOffset: 8, children: [
      /* @__PURE__ */ jsx20("span", { children: label }),
      /* @__PURE__ */ jsx20(Tooltip4.Arrow, { className: Sidebar_default.tooltipArrow })
    ] }) })
  ] });
}
function AssetsPanel({ onClose, onDrawImage }) {
  return /* @__PURE__ */ jsxs17(PanelRoot, { "data-ptd-region": "asset-panel", children: [
    /* @__PURE__ */ jsx20(PanelHeader, { title: "\u7D20\u6750", meta: "0 \u9879", children: /* @__PURE__ */ jsx20(PanelCloseButton, { label: "\u5173\u95ED\u7D20\u6750\u9762\u677F", onClick: onClose }) }),
    /* @__PURE__ */ jsx20(PanelBody, { children: /* @__PURE__ */ jsx20(
      PanelEmpty,
      {
        title: "\u8FD8\u6CA1\u6709\u53EF\u590D\u7528\u7D20\u6750",
        detail: "\u5148\u7ED8\u5236\u56FE\u7247\u6846\u5E76\u5728\u5C5E\u6027\u9762\u677F\u8F7D\u5165\u56FE\u7247\u3002\u7D20\u6750\u6301\u4E45\u5316\u5C06\u5728\u8D44\u4EA7\u9636\u6BB5\u63A5\u5165\u3002",
        actionLabel: "\u7ED8\u5236\u56FE\u7247\u6846",
        onAction: onDrawImage
      }
    ) }),
    /* @__PURE__ */ jsx20(PanelFooter, { children: "\u672A\u6765\u7528\u4E8E\u56FE\u7247\u3001Logo\u3001\u5370\u7AE0\u4E0E SVG \u7D20\u6750" })
  ] });
}
function PagesPanel({ onClose }) {
  useSignals12();
  const store = useEditorStore();
  const pages = store.template.value.pages;
  const currentIndex = store.currentPageIndex.value;
  const [draggedPageId, setDraggedPageId] = useState14(null);
  const [dropTargetIndex, setDropTargetIndex] = useState14(null);
  const startPageDrag = (event, pageId) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(PTD_PAGE_MIME, pageId);
    setDraggedPageId(pageId);
  };
  const dropPage = (event, targetIndex) => {
    event.preventDefault();
    const pageId = event.dataTransfer.getData(PTD_PAGE_MIME) || draggedPageId;
    const sourceIndex = pages.findIndex((page) => page.id === pageId);
    if (sourceIndex >= 0) store.movePage(sourceIndex, targetIndex);
    setDraggedPageId(null);
    setDropTargetIndex(null);
  };
  return /* @__PURE__ */ jsxs17(PanelRoot, { "data-ptd-region": "pages-panel", children: [
    /* @__PURE__ */ jsx20(PanelHeader, { title: "\u9875\u9762", meta: `${pages.length} \u9875`, children: /* @__PURE__ */ jsx20(PanelCloseButton, { label: "\u5173\u95ED\u9875\u9762\u9762\u677F", onClick: onClose }) }),
    /* @__PURE__ */ jsx20(PanelBody, { children: /* @__PURE__ */ jsx20("ol", { className: Sidebar_default.pageList, children: pages.map((page, index) => /* @__PURE__ */ jsx20(
      "li",
      {
        className: Sidebar_default.pageItem,
        draggable: true,
        "data-dragging": draggedPageId === page.id || void 0,
        "data-drop-target": dropTargetIndex === index || void 0,
        onDragStart: (event) => startPageDrag(event, page.id),
        onDragEnd: () => {
          setDraggedPageId(null);
          setDropTargetIndex(null);
        },
        onDragOver: (event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setDropTargetIndex(index);
        },
        onDragLeave: () => {
          if (dropTargetIndex === index) setDropTargetIndex(null);
        },
        onDrop: (event) => dropPage(event, index),
        children: /* @__PURE__ */ jsxs17(
          "button",
          {
            type: "button",
            className: Sidebar_default.pageRow,
            "data-selected": store.currentPageIndex.value === index || void 0,
            onClick: () => store.setCurrentPage(index),
            children: [
              /* @__PURE__ */ jsx20(RiDraggable, { className: Sidebar_default.pageDragIcon, "aria-hidden": "true" }),
              /* @__PURE__ */ jsx20("span", { className: Sidebar_default.pageNumber, children: String(index + 1).padStart(2, "0") }),
              /* @__PURE__ */ jsxs17("span", { children: [
                /* @__PURE__ */ jsx20("strong", { children: `\u9875\u9762 ${index + 1}` }),
                /* @__PURE__ */ jsxs17("small", { children: [
                  page.componentData.length,
                  " \u4E2A\u5BF9\u8C61"
                ] })
              ] })
            ]
          }
        )
      },
      page.id
    )) }) }),
    /* @__PURE__ */ jsxs17(PanelFooter, { className: Sidebar_default.pageActions, "aria-label": "\u9875\u9762\u7ED3\u6784\u64CD\u4F5C", children: [
      /* @__PURE__ */ jsx20(PageActionButton, { label: "\u6DFB\u52A0\u7A7A\u767D\u9875", onClick: () => store.addPage(), children: /* @__PURE__ */ jsx20(RiAddLine2, { "aria-hidden": "true" }) }),
      /* @__PURE__ */ jsx20(PageActionButton, { label: "\u590D\u5236\u5F53\u524D\u9875", onClick: () => store.duplicatePage(), children: /* @__PURE__ */ jsx20(RiFileCopyLine3, { "aria-hidden": "true" }) }),
      /* @__PURE__ */ jsx20(
        PageActionButton,
        {
          label: "\u5F53\u524D\u9875\u4E0A\u79FB",
          disabled: currentIndex <= 0,
          onClick: () => store.movePage(currentIndex, currentIndex - 1),
          children: /* @__PURE__ */ jsx20(RiArrowUpLine, { "aria-hidden": "true" })
        }
      ),
      /* @__PURE__ */ jsx20(
        PageActionButton,
        {
          label: "\u5F53\u524D\u9875\u4E0B\u79FB",
          disabled: currentIndex >= pages.length - 1,
          onClick: () => store.movePage(currentIndex, currentIndex + 1),
          children: /* @__PURE__ */ jsx20(RiArrowDownLine, { "aria-hidden": "true" })
        }
      ),
      /* @__PURE__ */ jsx20(
        PageActionButton,
        {
          label: "\u5220\u9664\u5F53\u524D\u9875",
          danger: true,
          disabled: pages.length <= 1,
          onClick: () => store.deletePage(),
          children: /* @__PURE__ */ jsx20(RiDeleteBinLine7, { "aria-hidden": "true" })
        }
      )
    ] })
  ] });
}
function LayersPanel({ onClose }) {
  useSignals12();
  const store = useEditorStore();
  const components = store.components.value;
  const selected = new Set(store.selectedIds.value);
  return /* @__PURE__ */ jsxs17(PanelRoot, { "data-ptd-region": "structure-panel", children: [
    /* @__PURE__ */ jsx20(PanelHeader, { title: "\u56FE\u5C42", meta: `${components.length} \u5C42`, children: /* @__PURE__ */ jsx20(PanelCloseButton, { label: "\u5173\u95ED\u56FE\u5C42\u9762\u677F", onClick: onClose }) }),
    /* @__PURE__ */ jsx20(PanelBody, { children: components.length > 0 ? /* @__PURE__ */ jsx20("ol", { className: Sidebar_default.layerList, children: [...components].reverse().map((component, reverseIndex) => /* @__PURE__ */ jsx20("li", { children: /* @__PURE__ */ jsxs17(
      "button",
      {
        type: "button",
        className: Sidebar_default.layerRow,
        "data-selected": selected.has(component.id) || void 0,
        onClick: (event) => store.selectComponent(component.id, event.shiftKey),
        children: [
          /* @__PURE__ */ jsx20("span", { className: Sidebar_default.layerIndex, children: components.length - reverseIndex }),
          /* @__PURE__ */ jsx20(RiFileList2Line, { "aria-hidden": "true" }),
          /* @__PURE__ */ jsx20("span", { className: Sidebar_default.layerName, children: component.name || component.component }),
          component.isLock && /* @__PURE__ */ jsx20(RiLock2Line, { className: Sidebar_default.lockIcon, "aria-label": "\u5DF2\u9501\u5B9A" })
        ]
      }
    ) }, component.id)) }) : /* @__PURE__ */ jsx20(
      PanelEmpty,
      {
        title: "\u753B\u5E03\u4E2D\u8FD8\u6CA1\u6709\u5BF9\u8C61",
        detail: "\u4F7F\u7528\u5E95\u90E8\u521B\u5EFA\u5DE5\u5177\uFF0C\u6216\u4ECE\u66F4\u591A\u7EC4\u4EF6\u9009\u62E9\u5668\u9009\u62E9\u5DE5\u5177\u540E\u5728\u7EB8\u5F20\u4E0A\u7ED8\u5236\u3002"
      }
    ) }),
    /* @__PURE__ */ jsx20(PanelFooter, { children: "\u5217\u8868\u9876\u90E8\u5BF9\u5E94\u7EB8\u5F20\u6700\u4E0A\u5C42" })
  ] });
}
function PanelEmpty({
  title,
  detail,
  actionLabel,
  onAction
}) {
  return /* @__PURE__ */ jsxs17("div", { className: Sidebar_default.emptyState, children: [
    /* @__PURE__ */ jsx20("strong", { children: title }),
    /* @__PURE__ */ jsx20("span", { children: detail }),
    actionLabel && onAction && /* @__PURE__ */ jsx20("button", { type: "button", onClick: onAction, children: actionLabel })
  ] });
}

// src/components/StatusBar/StatusBar.tsx
import { useSignals as useSignals13 } from "@preact/signals-react/runtime";
import { formatMeasurement as formatMeasurement5, getPageDimensions as getPageDimensions5 } from "@ptd/core";
import {
  RiEyeLine as RiEyeLine2,
  RiEyeOffLine,
  RiLockLine as RiLockLine4,
  RiPagesLine as RiPagesLine4,
  RiZoomInLine,
  RiZoomOutLine
} from "@remixicon/react";

// src/components/StatusBar/StatusBar.module.css
var StatusBar_default = {
  status: "StatusBar_status",
  statusGroup: "StatusBar_statusGroup",
  documentGroup: "StatusBar_documentGroup",
  pageStatus: "StatusBar_pageStatus",
  guideStatus: "StatusBar_guideStatus",
  unitSwitch: "StatusBar_unitSwitch",
  zoom: "StatusBar_zoom",
  documentTitle: "StatusBar_documentTitle",
  documentVersion: "StatusBar_documentVersion",
  documentState: "StatusBar_documentState",
  divider: "StatusBar_divider",
  pageMetric: "StatusBar_pageMetric",
  lockIcon: "StatusBar_lockIcon",
  grow: "StatusBar_grow",
  visuallyHidden: "StatusBar_visuallyHidden"
};

// src/components/StatusBar/StatusBar.tsx
import { jsx as jsx21, jsxs as jsxs18 } from "react/jsx-runtime";
var ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
var DOCUMENT_STATUS_LABELS = {
  clean: "\u5DF2\u4FDD\u5B58",
  dirty: "\u672A\u4FDD\u5B58",
  saving: "\u6B63\u5728\u4FDD\u5B58",
  loading: "\u6B63\u5728\u8F7D\u5165",
  error: "\u64CD\u4F5C\u5931\u8D25",
  conflict: "\u7248\u672C\u51B2\u7A81"
};
function StatusBar({ document: document2 }) {
  useSignals13();
  const store = useEditorStore();
  const page = getPageDimensions5(store.pageConfig.value);
  const selectedCount = store.selectedIds.value.length;
  const guideCount = store.guides.value.length;
  const pageNumber = store.currentPageIndex.value + 1;
  const pageCount = store.template.value.pages.length;
  const scale = store.scale.value;
  const measurementUnit = store.measurementUnit.value;
  return /* @__PURE__ */ jsxs18("footer", { className: StatusBar_default.status, "aria-label": "\u8BBE\u8BA1\u5668\u72B6\u6001", "data-ptd-region": "status-bar", children: [
    document2 && /* @__PURE__ */ jsxs18("div", { className: StatusBar_default.documentGroup, "aria-label": "\u5BBF\u4E3B\u6587\u6863\u72B6\u6001", children: [
      document2.title && /* @__PURE__ */ jsx21("strong", { className: StatusBar_default.documentTitle, children: document2.title }),
      document2.version !== void 0 && /* @__PURE__ */ jsxs18("span", { className: StatusBar_default.documentVersion, children: [
        "v",
        document2.version
      ] }),
      /* @__PURE__ */ jsxs18(
        "span",
        {
          className: StatusBar_default.documentState,
          "data-status": document2.status,
          role: "status",
          title: document2.message,
          children: [
            /* @__PURE__ */ jsx21("span", { "aria-hidden": "true" }),
            DOCUMENT_STATUS_LABELS[document2.status]
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs18("div", { className: StatusBar_default.statusGroup, "aria-label": "\u6587\u6863\u72B6\u6001", children: [
      /* @__PURE__ */ jsxs18("span", { className: StatusBar_default.pageStatus, children: [
        /* @__PURE__ */ jsx21(RiPagesLine4, { "aria-hidden": "true" }),
        pageNumber,
        " / ",
        pageCount
      ] }),
      /* @__PURE__ */ jsx21("span", { className: StatusBar_default.divider, "aria-hidden": "true" }),
      /* @__PURE__ */ jsx21("span", { children: selectedCount ? `\u5DF2\u9009\u62E9 ${selectedCount} \u4E2A\u5BF9\u8C61` : "\u672A\u9009\u62E9\u5BF9\u8C61" }),
      /* @__PURE__ */ jsx21("span", { className: StatusBar_default.divider, "aria-hidden": "true" }),
      /* @__PURE__ */ jsxs18("span", { className: StatusBar_default.pageMetric, children: [
        formatMeasurement5(page.width, measurementUnit),
        " \xD7",
        " ",
        formatMeasurement5(page.height, measurementUnit),
        " ",
        measurementUnit
      ] })
    ] }),
    /* @__PURE__ */ jsx21("div", { className: StatusBar_default.statusGroup, "aria-label": "\u53C2\u8003\u7EBF\u72B6\u6001", children: /* @__PURE__ */ jsxs18(
      "button",
      {
        type: "button",
        className: StatusBar_default.guideStatus,
        "aria-label": store.guidesVisible.value ? "\u9690\u85CF\u53C2\u8003\u7EBF" : "\u663E\u793A\u53C2\u8003\u7EBF",
        "aria-pressed": store.guidesVisible.value,
        onClick: () => store.toggleGuidesVisible(),
        children: [
          store.guidesVisible.value ? /* @__PURE__ */ jsx21(RiEyeLine2, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx21(RiEyeOffLine, { "aria-hidden": "true" }),
          "\u53C2\u8003\u7EBF ",
          guideCount,
          store.guidesLocked.value && /* @__PURE__ */ jsx21(RiLockLine4, { className: StatusBar_default.lockIcon, "aria-label": "\u5DF2\u9501\u5B9A" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx21("span", { className: StatusBar_default.grow }),
    /* @__PURE__ */ jsx21("div", { className: StatusBar_default.unitSwitch, role: "group", "aria-label": "\u6587\u6863\u663E\u793A\u5355\u4F4D", children: ["mm", "px"].map((unit) => /* @__PURE__ */ jsx21(
      "button",
      {
        type: "button",
        "aria-label": unit === "mm" ? "\u4F7F\u7528\u6BEB\u7C73\u663E\u793A\u5C3A\u5BF8" : "\u4F7F\u7528 PTD \u753B\u5E03\u50CF\u7D20\u663E\u793A\u5C3A\u5BF8",
        "aria-pressed": measurementUnit === unit,
        onClick: () => store.setMeasurementUnit(unit),
        children: unit
      },
      unit
    )) }),
    /* @__PURE__ */ jsxs18("div", { className: StatusBar_default.zoom, "aria-label": "\u753B\u5E03\u7F29\u653E", children: [
      /* @__PURE__ */ jsx21(
        "button",
        {
          type: "button",
          "aria-label": "\u7F29\u5C0F\u753B\u5E03",
          disabled: scale <= 0.25,
          onClick: () => store.setZoom(scale - 0.25),
          children: /* @__PURE__ */ jsx21(RiZoomOutLine, { "aria-hidden": "true" })
        }
      ),
      /* @__PURE__ */ jsxs18("label", { children: [
        /* @__PURE__ */ jsx21("span", { className: StatusBar_default.visuallyHidden, children: "\u7F29\u653E\u6BD4\u4F8B" }),
        /* @__PURE__ */ jsx21("select", { value: scale, onChange: (event) => store.setZoom(Number(event.target.value)), children: ZOOM_LEVELS.map((level) => /* @__PURE__ */ jsxs18("option", { value: level, children: [
          Math.round(level * 100),
          "%"
        ] }, level)) })
      ] }),
      /* @__PURE__ */ jsx21(
        "button",
        {
          type: "button",
          "aria-label": "\u653E\u5927\u753B\u5E03",
          disabled: scale >= 2,
          onClick: () => store.setZoom(scale + 0.25),
          children: /* @__PURE__ */ jsx21(RiZoomInLine, { "aria-hidden": "true" })
        }
      )
    ] })
  ] });
}

// src/components/Designer/Designer.module.css
var Designer_default = {
  designer: "Designer_designer",
  workspace: "Designer_workspace",
  canvasArea: "Designer_canvasArea",
  screens: "Designer_screens",
  inspectorRegion: "Designer_inspectorRegion",
  resizeHandle: "Designer_resizeHandle",
  resizeInspector: "Designer_resizeInspector",
  overlayScrim: "Designer_overlayScrim"
};

// src/components/Designer/Designer.tsx
import { jsx as jsx22, jsxs as jsxs19 } from "react/jsx-runtime";
function Designer({ value, onChange, host, renderContext }) {
  const [store] = useState15(() => createEditorStore(value, { onChange, renderContext }));
  const rootRef = useRef15(null);
  const layout = useWorkspaceLayout(rootRef);
  const getTemplate = useCallback11(() => store.template.value, [store]);
  const hostCommands = useDesignerHostCommands(host, getTemplate);
  const keyboardActions = useMemo8(
    () => ({
      hostCommands,
      openResource: layout.openResource,
      toggleInspector: layout.toggleInspector
    }),
    [hostCommands, layout.openResource, layout.toggleInspector]
  );
  useEditorKeyboard(store, rootRef, keyboardActions);
  useEffect16(() => {
    store.setOnChange(onChange);
    store.syncExternal(value);
    store.setHostRenderContext(renderContext);
  }, [onChange, renderContext, store, value]);
  const workspaceStyle = {
    "--ptd-resource-panel-width": `${layout.resourceWidth}px`,
    "--ptd-inspector-width": `${layout.inspectorWidth}px`
  };
  const overlayOpen = layout.mode === "compact" && (layout.resourcesOpen || layout.inspectorOpen);
  return /* @__PURE__ */ jsx22(EditorStoreProvider, { store, children: /* @__PURE__ */ jsxs19(
    "div",
    {
      ref: rootRef,
      className: `${Designer_default.designer} ${ptdThemeClass}`,
      "data-ptd-region": "designer",
      tabIndex: -1,
      onPointerDownCapture: (event) => {
        if (!isEditorInteractiveTarget(event.target)) {
          rootRef.current?.focus({ preventScroll: true });
        }
      },
      children: [
        /* @__PURE__ */ jsx22(
          AppBar,
          {
            hostCommands,
            workspace: { openResource: layout.openResource, openInspector: layout.openInspector }
          }
        ),
        /* @__PURE__ */ jsxs19(
          "div",
          {
            className: Designer_default.workspace,
            "data-mode": layout.mode,
            "data-resources-open": layout.resourcesOpen,
            "data-inspector-open": layout.inspectorOpen,
            style: workspaceStyle,
            children: [
              /* @__PURE__ */ jsx22(
                Sidebar,
                {
                  mode: layout.mode,
                  activePanel: layout.activeResource,
                  open: layout.resourcesOpen,
                  onTogglePanel: layout.toggleResource,
                  onResizeStart: (event) => layout.beginResize("resources", event)
                }
              ),
              /* @__PURE__ */ jsxs19("div", { className: Designer_default.canvasArea, children: [
                /* @__PURE__ */ jsx22("div", { className: Designer_default.screens, "data-ptd-region": "canvas-viewport", children: /* @__PURE__ */ jsx22(Canvas, { onOpenInspector: layout.openInspector }) }),
                /* @__PURE__ */ jsx22(
                  FloatingToolDock,
                  {
                    mode: layout.mode,
                    inspectorOpen: layout.inspectorOpen,
                    onToggleInspector: layout.toggleInspector
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs19("div", { className: Designer_default.inspectorRegion, hidden: !layout.inspectorOpen, children: [
                /* @__PURE__ */ jsx22(
                  "button",
                  {
                    type: "button",
                    className: `${Designer_default.resizeHandle} ${Designer_default.resizeInspector}`,
                    "aria-label": "\u8C03\u6574\u5C5E\u6027\u9762\u677F\u5BBD\u5EA6",
                    onPointerDown: (event) => layout.beginResize("inspector", event)
                  }
                ),
                /* @__PURE__ */ jsx22(PropertyInspector, {})
              ] }),
              overlayOpen && /* @__PURE__ */ jsx22(
                "button",
                {
                  type: "button",
                  className: Designer_default.overlayScrim,
                  "aria-label": "\u5173\u95ED\u5DE5\u4F5C\u533A\u9762\u677F",
                  onClick: layout.closeOverlay
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx22(StatusBar, { document: hostCommands.document })
      ]
    }
  ) });
}

// src/components/TemplatePreview/TemplatePreview.tsx
import { useEffect as useEffect17, useLayoutEffect as useLayoutEffect3, useRef as useRef16, useState as useState16 } from "react";
import {
  getPageDimensions as getPageDimensions6
} from "@ptd/core";

// src/components/TemplatePreview/TemplatePreview.module.css
var TemplatePreview_default = {
  preview: "TemplatePreview_preview",
  paper: "TemplatePreview_paper",
  component: "TemplatePreview_component"
};

// src/components/TemplatePreview/TemplatePreview.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
function componentVariables(component) {
  return {
    "--preview-component-left": `${component.style.left}px`,
    "--preview-component-top": `${component.style.top}px`,
    "--preview-component-width": `${component.style.width}px`,
    "--preview-component-height": `${component.style.height}px`,
    "--preview-component-rotate": `${component.style.rotate ?? 0}deg`
  };
}
function TemplatePreview({
  template,
  pageIndex = 0,
  label = `${template.pageConfig.title} \u6A21\u677F\u9884\u89C8`,
  className,
  renderContext
}) {
  const rootRef = useRef16(null);
  const [store] = useState16(() => createEditorStore(template));
  const initialDimensions = getPageDimensions6(template.pageConfig);
  const [viewportSize, setViewportSize] = useState16(() => ({
    width: 320,
    height: 320 * initialDimensions.height / initialDimensions.width
  }));
  useEffect17(() => {
    store.syncExternal(template);
  }, [store, template]);
  useLayoutEffect3(() => {
    const root = rootRef.current;
    if (!root) return;
    const updateSize = () => {
      const bounds = root.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        setViewportSize({ width: bounds.width, height: bounds.height });
      }
    };
    updateSize();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateSize);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  const safePageIndex = Math.min(Math.max(0, pageIndex), template.pages.length - 1);
  const page = template.pages[safePageIndex] ?? template.pages[0];
  const { width, height } = getPageDimensions6(template.pageConfig);
  const scale = Math.min(viewportSize.width / width, viewportSize.height / height);
  const renderedWidth = width * scale;
  const renderedHeight = height * scale;
  const variables = {
    "--preview-page-width": `${width}px`,
    "--preview-page-height": `${height}px`,
    "--preview-page-scale": String(scale),
    "--preview-page-left": `${Math.max(0, (viewportSize.width - renderedWidth) / 2)}px`,
    "--preview-page-top": `${Math.max(0, (viewportSize.height - renderedHeight) / 2)}px`,
    "--preview-page-ratio": `${width} / ${height}`,
    "--preview-page-background": template.pageConfig.background,
    "--preview-page-color": template.pageConfig.color,
    "--preview-page-font-size": `${template.pageConfig.fontSize}px`,
    "--preview-page-font-family": template.pageConfig.fontFamily,
    "--preview-page-line-height": String(template.pageConfig.lineHeight)
  };
  return /* @__PURE__ */ jsx23(EditorStoreProvider, { store, children: /* @__PURE__ */ jsx23(
    "div",
    {
      ref: rootRef,
      className: className ? `${TemplatePreview_default.preview} ${className}` : TemplatePreview_default.preview,
      style: variables,
      role: "img",
      "aria-label": label,
      "data-ptd-region": "template-preview",
      children: /* @__PURE__ */ jsx23("div", { className: TemplatePreview_default.paper, "aria-hidden": "true", children: page?.componentData.map((component) => /* @__PURE__ */ jsx23(
        "div",
        {
          className: TemplatePreview_default.component,
          style: componentVariables(component),
          children: /* @__PURE__ */ jsx23(ComponentRenderer, { schema: component, renderContext })
        },
        component.id
      )) })
    }
  ) });
}
export {
  DESIGNER_HOST_COMMAND_IDS,
  Designer,
  TemplatePreview
};
//# sourceMappingURL=index.js.map